import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Form, InputNumber, Modal, Select, Spin, Switch, Tooltip, message } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, BulbOutlined, InfoCircleOutlined, LoadingOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { getI18n } from '../../../../../utils/Utils';
import { useNavigate } from 'react-router-dom';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IAppComponentProps } from '../../../../../components';
import EimExecutiveSectionCard from '../../components/EimExecutiveSectionCard';
import {
    useAllActivePartnersLazyQuery,
    useCountAllActivePartnersLazyQuery,
    useGetDashCardCountLazyQuery,
} from '../../dashboardQueries.generated';
import ReactEcharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { getActivePartnerGroupBarChartOptions, getAllActiveInvoiceGraphOptions, getAverageTransitDaysTrendChartOptions, getVolumeTrendWithMedianAndStdDevChartOptions } from '../../utils/InvoiceStatusUtils';
import { buildCompanyCountryLookup, ExecutiveThresholdConfig, formatCountryCompanyLabel as formatCountryCompanyLabelFromUtils, getCountryDisplayName, getCountryFlagUrl, getExecutiveSections, PROVIDER_BREAKDOWN_ACTION, resolveCountryCode } from '../../eimDashboardUtils';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import { EimDashboardCardsResponse, EimDashboardRequest } from '../../../../../__generated__/types';
import { getDateFormatOrDefault } from '../../../../users/UserFormatPreferenceServices';
import dashboardStyles from '../../eimDashboard.module.scss';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import { getRangePickerLocalization } from '../../../../info-portal/components/grid/CalendarUtils';
import {
    QUERY_EIM_EXECUTIVE_DASHBOARD,
    QUERY_EIM_EXECUTIVE_DASHBOARD_CHANNEL_METRICS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_COUNTRY_METRICS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_GROUP_METRICS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_SYSTEM_METRICS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_REASONS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_PROCESSES,
    QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_COMPANY_CODES,
    QUERY_EIM_ACTIVE_PARTNER_INVOICE_HOURLY_TREND,
    QUERY_EIM_ACTIVE_PARTNER_INVOICE_SUMMARY,
    QUERY_EIM_ACTIVE_PARTNER_COUNTRY_DETAIL,
} from '../../dashboardQueries';
import * as echarts from 'echarts';
import worldGeoJson from '../../../../../assets/maps/world.json';
import worldEditableShapeDataset from '../../docs/worldEditableShapeDataset.json';

const OPERATIONAL_DATA_ORIGIN_KEY = 'eimOperationalDataOrigin';
const EXECUTIVE_DASHBOARD_V1_ROUTE = '/eim-document-executive-dashboard-monitoring';

const BMW_WORLD_SHAPE_NAME_SET = new Set(
    (worldEditableShapeDataset.shapeNames ?? []).map((shapeName) => shapeName.toLowerCase())
);

type BmwTerritoryMode = 'allTerritories' | 'mainlandOnly';

type BmwCountryTerritoryRule = {
    mapName: string;
    territoryMode: BmwTerritoryMode;
};

// Deterministic BMW territory policy table by ISO2 code.
const BMW_COUNTRY_TERRITORY_RULES: Record<string, BmwCountryTerritoryRule> = {
    FR: { mapName: 'France', territoryMode: 'mainlandOnly' },
    US: { mapName: 'USA', territoryMode: 'mainlandOnly' },
    GB: { mapName: 'United Kingdom', territoryMode: 'mainlandOnly' },
    NL: { mapName: 'Netherlands', territoryMode: 'mainlandOnly' },
    DK: { mapName: 'Denmark', territoryMode: 'mainlandOnly' },
    NO: { mapName: 'Norway', territoryMode: 'mainlandOnly' },
    PT: { mapName: 'Portugal', territoryMode: 'mainlandOnly' },
    ES: { mapName: 'Spain', territoryMode: 'mainlandOnly' },
    GF: { mapName: 'Guyane (French Guiana)', territoryMode: 'allTerritories' },
};

const BMW_ISO2_TO_MAP_NAME_OVERRIDES: Record<string, string> = {
    ...Object.keys(BMW_COUNTRY_TERRITORY_RULES).reduce<Record<string, string>>((accumulator, isoCode) => {
        accumulator[isoCode] = BMW_COUNTRY_TERRITORY_RULES[isoCode].mapName;
        return accumulator;
    }, {}),
};

const BMW_MAP_NAME_TERRITORY_RULES = Object.values(BMW_COUNTRY_TERRITORY_RULES).reduce<Record<string, BmwCountryTerritoryRule>>((accumulator, rule) => {
    accumulator[rule.mapName] = rule;
    return accumulator;
}, {});

const BMW_OVERSEAS_POLYGON_DISTANCE_THRESHOLD = 30;

const getBmwMapCountryName = (countryCode: string, localizedCountryName?: string | null) => {
    const overriddenName = BMW_ISO2_TO_MAP_NAME_OVERRIDES[countryCode];
    const candidateName = overriddenName ?? localizedCountryName ?? null;

    if (!candidateName) {
        return null;
    }

    const normalizedName = candidateName.trim().toLowerCase();
    return BMW_WORLD_SHAPE_NAME_SET.has(normalizedName) ? candidateName : null;
};

const getPolygonOuterRing = (polygon: any): number[][] => {
    if (!Array.isArray(polygon) || !Array.isArray(polygon[0])) {
        return [];
    }

    return polygon[0].filter((point: any) => (
        Array.isArray(point)
        && typeof point[0] === 'number'
        && typeof point[1] === 'number'
    ));
};

const getRingArea = (ring: number[][]) => {
    if (ring.length < 3) {
        return 0;
    }

    let area = 0;
    for (let index = 0; index < ring.length; index += 1) {
        const [x1, y1] = ring[index];
        const [x2, y2] = ring[(index + 1) % ring.length];
        area += (x1 * y2) - (x2 * y1);
    }

    return Math.abs(area) / 2;
};

const getPolygonArea = (polygon: any) => getRingArea(getPolygonOuterRing(polygon));

const getPolygonCentroid = (polygon: any): [number, number] | null => {
    const ring = getPolygonOuterRing(polygon);
    if (!ring.length) {
        return null;
    }

    let sumX = 0;
    let sumY = 0;
    for (const [x, y] of ring) {
        sumX += x;
        sumY += y;
    }

    return [sumX / ring.length, sumY / ring.length];
};

const getCentroidDistance = (left: [number, number] | null, right: [number, number] | null) => {
    if (!left || !right) {
        return Number.POSITIVE_INFINITY;
    }

    const dx = left[0] - right[0];
    const dy = left[1] - right[1];
    return Math.hypot(dx, dy);
};

const getBmwMainlandPolygons = (polygons: any[]) => {
    if (polygons.length <= 1) {
        return polygons;
    }

    let primaryIndex = 0;
    let largestArea = 0;

    polygons.forEach((polygon, index) => {
        const area = getPolygonArea(polygon);
        if (area > largestArea) {
            largestArea = area;
            primaryIndex = index;
        }
    });

    const primaryCentroid = getPolygonCentroid(polygons[primaryIndex]);
    const filteredPolygons = polygons.filter((polygon, index) => {
        if (index === primaryIndex) {
            return true;
        }

        const polygonCentroid = getPolygonCentroid(polygon);
        return getCentroidDistance(primaryCentroid, polygonCentroid) <= BMW_OVERSEAS_POLYGON_DISTANCE_THRESHOLD;
    });

    return filteredPolygons.length ? filteredPolygons : [polygons[primaryIndex]];
};

const getBmwTerritoryWorldGeoJson = (sourceGeoJson: any) => {
    const clonedGeoJson = structuredClone(sourceGeoJson);
    const features = Array.isArray(clonedGeoJson?.features) ? clonedGeoJson.features : [];

    for (const feature of features) {
        if (feature?.geometry?.type !== 'MultiPolygon') {
            continue;
        }

        const featureName = feature?.properties?.name;
        const territoryRule = featureName ? BMW_MAP_NAME_TERRITORY_RULES[featureName] : null;
        if (!territoryRule || territoryRule.territoryMode !== 'mainlandOnly') {
            continue;
        }

        const polygons = Array.isArray(feature.geometry.coordinates) ? feature.geometry.coordinates : [];
        feature.geometry.coordinates = getBmwMainlandPolygons(polygons);
    }

    return clonedGeoJson;
};

type ThemeMode = 'dark' | 'light';
type PeriodOption = 'today' | 'currentMonth' | 'lastWeek' | 'lastMonth' | 'last6Months' | 'lastYear' | 'custom';
type PeriodRangeValue = [Dayjs, Dayjs];

type ExecutiveDashboardRequest = {
    periodStart: string;
    periodEnd: string;
    processType?: string;
    partnerGroup?: string;
};

type CountryMetric = {
    country?: string | null;
    companyCode?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type WorldMapDataPoint = {
    name: string;
    value: number;
    countryCode: string;
    totalProcessed: number;
    inTransitCount: number;
    failedCount: number;
    overdueCount: number;
    stpRate: number;
};

type WorldMapHealthCategory = 'healthy' | 'warning' | 'critical' | 'noData';

type WorldMapSeriesDataPoint = WorldMapDataPoint & {
    healthCategory: WorldMapHealthCategory;
    failureRate: number;
    inTransitRate: number;
    itemStyle: {
        areaColor: string;
    };
};

type CountryActionSelection = {
    country: string;
    companyCode?: string | null;
};

type CountryDetailsSelection = {
    country: string;
    companyCode?: string | null;
};

const WORLD_MAP_HEALTH_COLORS: Record<WorldMapHealthCategory, string> = {
    healthy: '#6ccf8d',
    warning: '#f5b436',
    critical: '#ea5b5b',
    noData: '#d8dde6',
};

type LiveTrafficBarLevel = 1 | 2 | 3 | 4 | 5 | 6;

type LiveTrafficBar = {
    key: string;
    level: LiveTrafficBarLevel;
    tone: 'positive' | 'warning' | 'critical';
};

const getLiveTrafficToneFromHealthCategory = (healthCategory: WorldMapHealthCategory): LiveTrafficBar['tone'] => {
    if (healthCategory === 'critical') {
        return 'critical';
    }

    if (healthCategory === 'warning') {
        return 'warning';
    }

    return 'positive';
};

const getLiveTrafficBarToneClassName = (tone: LiveTrafficBar['tone']): string => {
    if (tone === 'critical') {
        return dashboardStyles.worldMapLiveTrafficBarCritical;
    }

    if (tone === 'warning') {
        return dashboardStyles.worldMapLiveTrafficBarWarning;
    }

    return dashboardStyles.worldMapLiveTrafficBarPositive;
};

type CountryMetricsQueryData = {
    eimExecutiveDashboardCountryMetrics?: CountryMetric[] | null;
};

type CountryMetricsQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type SystemMetric = {
    systemId?: number | null;
    systemLabel?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type SystemMetricsQueryData = {
    eimExecutiveDashboardSystemMetrics?: SystemMetric[] | null;
};

type SystemMetricsQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type ChannelMetric = {
    channel?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type ChannelMetricsQueryData = {
    eimExecutiveDashboardChannelMetrics?: ChannelMetric[] | null;
};

type ChannelMetricsQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type GroupMetric = {
    groupName?: string | null;
    totalInvoices?: number | null;
    successfulCount?: number | null;
    inTransitCount?: number | null;
    failedCount?: number | null;
};

type GroupMetricsQueryData = {
    eimExecutiveDashboardGroupMetrics?: GroupMetric[] | null;
};

type GroupMetricsQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type FailureReason = {
    reason?: string | null;
    invoiceCount?: number | null;
    percentage?: number | null;
    failedCount?: number | null;
    failureRate?: number | null;
    overdueCount?: number | null;
    volume?: number | null;
};

type TopFailureReasonsQueryData = {
    eimExecutiveDashboardTopFailureReasons?: FailureReason[] | null;
};

type TopFailureReasonsQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type FailureProcess = {
    process?: string | null;
    failedCount?: number | null;
    totalCount?: number | null;
    failureRate?: number | null;
    overdueCount?: number | null;
};

type TopFailureProcessesQueryData = {
    eimExecutiveDashboardTopFailureProcesses?: FailureProcess[] | null;
};

type TopFailureProcessesQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type FailureCompanyCode = {
    companyCode?: string | null;
    country?: string | null;
    failureRate?: number | null;
    overdueCount?: number | null;
    volume?: number | null;
    riskRanking?: number | null;
};

type TopFailureCompanyCodesQueryData = {
    eimExecutiveDashboardTopFailureCompanyCodes?: FailureCompanyCode[] | null;
};

type TopFailureCompanyCodesQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type ExecutiveDashboardQueryData = {
    eimExecutiveDashboard?: Record<string, unknown> | null;
};

type ExecutiveDashboardQueryVariables = {
    request: ExecutiveDashboardRequest;
};

type MetricTone = 'neutral' | 'positive' | 'warning' | 'critical';
type V2SparklineMode = 'bars' | 'line';
type V2BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'critical';
type V2DeltaDirection = 'up' | 'down' | 'neutral';
type V2SignalTile = {
    label: string;
    value: string;
    tone: MetricTone;
    hint?: React.ReactNode;
    trendLevels: number[];
    sparklineMode: V2SparklineMode;
    badgeLabel: string;
    badgeTone: V2BadgeTone;
    deltaText?: string;
    deltaDirection?: V2DeltaDirection;
};
type TopGroupRankMode = 'failureRate' | 'overdueRate';
type TransitTrendTimeRange = 'today' | 'currentMonth' | '30d' | '6m' | '1y';
type InboundOutboundTrendPoint = { label: string; inbound: number; outbound: number };
type InboundOutboundTrendSeries = { daily: InboundOutboundTrendPoint[]; monthly: InboundOutboundTrendPoint[]; hourly?: InboundOutboundTrendPoint[] };
type TransitHourlyTrendPoint = { month: string; avgTransitDays: number };
type TodayHourlyTrendSeries = { volumeHourly: InboundOutboundTrendPoint[]; transitHourly: TransitHourlyTrendPoint[] };
type CountryHourlyTrendPoint = { label: string; successful: number; failed: number };
type CountryPartnerVolumePoint = { partner: string; country: string; volume: number; successRate: number };
type PartnerInvoiceHourlyTrendItem = { hourLabel?: string | null; successfulCount?: number | null; failedCount?: number | null };
type PartnerInvoiceHourlyTrendQueryData = { allActivePartnerInvoiceHourlyTrend?: PartnerInvoiceHourlyTrendItem[] | null };
type PartnerInvoiceHourlyTrendQueryVariables = { request: EimDashboardRequest };
type PartnerInvoiceSummaryItem = { partnerno?: string | null; partnerName?: string | null; company?: string | null; total?: number | null };
type PartnerInvoiceSummaryQueryData = { allActivePartnerInvoiceSummary?: PartnerInvoiceSummaryItem[] | null };
type PartnerInvoiceSummaryQueryVariables = { request: EimDashboardRequest };
type CountryDetailErrorMessageItem = { message?: string | null; count?: number | null; percentage?: number | null };
type ActivePartnerCountryDetailQueryData = {
    allActivePartnerCountryDetail?: {
        topErrorMessages?: CountryDetailErrorMessageItem[] | null;
    } | null;
};
type ActivePartnerCountryDetailQueryVariables = { request: EimDashboardRequest };
type CountryDetailsPartnerSortKey = 'volume' | 'successRate';
type CountryDetailsPartnerSortDirection = 'asc' | 'desc';

const readFirstDefinedField = (row: any, keys: string[]): string => {
    for (const key of keys) {
        const raw = row?.[key];
        if (raw !== undefined && raw !== null && String(raw).trim().length > 0) {
            return String(raw).trim();
        }
    }

    return '';
};

const normalizeFieldKey = (key: string): string => key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const readFieldByNormalizedKey = (row: any, candidateKeys: string[]): string => {
    if (!row || typeof row !== 'object') {
        return '';
    }

    const normalizedCandidates = new Set(candidateKeys.map((key) => normalizeFieldKey(key)));
    const entries = Object.entries(row as Record<string, unknown>);

    for (const [key, raw] of entries) {
        if (!normalizedCandidates.has(normalizeFieldKey(key))) {
            continue;
        }

        if (raw === undefined || raw === null) {
            continue;
        }

        const value = String(raw).trim();
        if (value.length > 0) {
            return value;
        }
    }

    return '';
};

const normalizePartnerNumber = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    // Some upstream payloads serialize numeric identifiers as decimal strings (e.g. "12345.0").
    const normalizedDecimal = /^\d+\.0+$/.test(trimmed) ? trimmed.replace(/\.0+$/, '') : trimmed;
    return normalizedDecimal;
};

const parseNumericLike = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value !== 'string') {
        return 0;
    }

    const normalized = value.replaceAll(',', '').trim();
    if (!normalized) {
        return 0;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeHourlyBucketLabel = (value: unknown): string | null => {
    let raw = '';
    if (typeof value === 'string') {
        raw = value.trim();
    } else if (typeof value === 'number' && Number.isFinite(value)) {
        raw = String(value);
    } else if (value instanceof Date) {
        raw = value.toISOString();
    }

    if (!raw) {
        return null;
    }

    const simpleHourPattern = /^(\d{1,2})(?::\d{2})?$/;
    const simpleHourMatch = simpleHourPattern.exec(raw);
    if (simpleHourMatch) {
        const hourValue = Number.parseInt(simpleHourMatch[1], 10);
        if (!Number.isNaN(hourValue) && hourValue >= 0 && hourValue <= 23) {
            return `${String(hourValue).padStart(2, '0')}:00`;
        }
    }

    const embeddedHourPattern = /(?:T|\s)(\d{1,2}):\d{2}/;
    const embeddedHourMatch = embeddedHourPattern.exec(raw);
    if (embeddedHourMatch) {
        const hourValue = Number.parseInt(embeddedHourMatch[1], 10);
        if (!Number.isNaN(hourValue) && hourValue >= 0 && hourValue <= 23) {
            return `${String(hourValue).padStart(2, '0')}:00`;
        }
    }

    const parsed = dayjs(raw);
    if (parsed.isValid()) {
        return parsed.format('HH:00');
    }

    return null;
};

const isValidTodayHourlyTrendSeries = (value: unknown): value is TodayHourlyTrendSeries => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as TodayHourlyTrendSeries;
    return Array.isArray(candidate.volumeHourly)
        && candidate.volumeHourly.length > 0
        && Array.isArray(candidate.transitHourly);
};
type MetricTileStatus = 'total' | 'successful' | 'inTransit' | 'failed';
type ExecutiveDashboardViewCache = {
    dashboardCounts?: EimDashboardCardsResponse;
    executiveMetricsV2?: any;
    volumeTrendMetricsV2?: any;
    inboundOutboundTrendSeries?: InboundOutboundTrendSeries;
    todayTransitHourlyTrendData?: TransitHourlyTrendPoint[];
    countryMetrics?: CountryMetric[];
    systemMetrics?: SystemMetric[];
    channelMetrics?: ChannelMetric[];
    groupMetrics?: GroupMetric[];
    topFailureReasons?: FailureReason[];
    topFailureProcesses?: FailureProcess[];
    topFailureCompanyCodes?: FailureCompanyCode[];
    rawGroupCountData?: any[];
    rawAllActivePartnerData?: any[];
    activePartnerTotal?: number;
    activePartnerGroupCount?: number;
};
type ExecutiveDashboardViewCacheEntry = {
    updatedAt: number;
    snapshot: ExecutiveDashboardViewCache;
};

const EXECUTIVE_INBOUND_OUTBOUND_TREND_CACHE_PREFIX = 'eimExecutiveInboundOutboundTrendCache';
const inboundOutboundTrendSeriesRequestCache: Record<string, Promise<InboundOutboundTrendSeries>> = {};
const EXECUTIVE_TODAY_HOURLY_TREND_CACHE_PREFIX = 'eimExecutiveTodayHourlyTrendCache:v3';
const todayHourlyTrendSeriesRequestCache: Record<string, Promise<TodayHourlyTrendSeries>> = {};
const EXECUTIVE_DASHBOARD_VIEW_CACHE_PREFIX = 'eimExecutiveViewCache:v1';
const EXECUTIVE_DASHBOARD_VIEW_CACHE_TTL_MS = 10 * 60 * 1000;

const isQuotaExceededStorageError = (error: unknown): boolean => {
    return error instanceof DOMException && error.name === 'QuotaExceededError';
};

const getExecutiveViewCacheKeys = (): string[] => {
    const prefix = `${EXECUTIVE_DASHBOARD_VIEW_CACHE_PREFIX}:`;
    const keys: string[] = [];

    for (let index = 0; index < globalThis.sessionStorage.length; index += 1) {
        const key = globalThis.sessionStorage.key(index);
        if (key && key.startsWith(prefix)) {
            keys.push(key);
        }
    }

    return keys;
};

const pruneOldExecutiveViewCacheEntries = (currentCacheKey: string): void => {
    const cacheKeysByAge = getExecutiveViewCacheKeys()
        .filter((key) => key !== currentCacheKey)
        .map((key) => {
            const rawValue = globalThis.sessionStorage.getItem(key);
            if (!rawValue) {
                return { key, updatedAt: 0 };
            }

            try {
                const parsed = JSON.parse(rawValue) as ExecutiveDashboardViewCacheEntry;
                return { key, updatedAt: Number(parsed?.updatedAt ?? 0) };
            } catch {
                return { key, updatedAt: 0 };
            }
        })
        .sort((left, right) => left.updatedAt - right.updatedAt);

    cacheKeysByAge.forEach((entry) => {
        globalThis.sessionStorage.removeItem(entry.key);
    });
};

const buildReducedExecutiveViewSnapshot = (snapshot: ExecutiveDashboardViewCache): ExecutiveDashboardViewCache => ({
    ...snapshot,
    rawGroupCountData: [],
    rawAllActivePartnerData: [],
    countryMetrics: [],
    systemMetrics: [],
    channelMetrics: [],
    groupMetrics: [],
    topFailureReasons: [],
    topFailureProcesses: [],
    topFailureCompanyCodes: [],
});

const trySetSessionStorageValue = (cacheKey: string, value: string): 'ok' | 'quota' | 'other' => {
    try {
        globalThis.sessionStorage.setItem(cacheKey, value);
        return 'ok';
    } catch (error) {
        if (isQuotaExceededStorageError(error)) {
            return 'quota';
        }

        return 'other';
    }
};

const persistExecutiveViewCacheSafely = (
    cacheKey: string,
    cacheEntry: ExecutiveDashboardViewCacheEntry,
): void => {
    const serializedCacheEntry = JSON.stringify(cacheEntry);

    const initialWriteResult = trySetSessionStorageValue(cacheKey, serializedCacheEntry);
    if (initialWriteResult !== 'quota') {
        return;
    }

    pruneOldExecutiveViewCacheEntries(cacheKey);

    const retryWriteResult = trySetSessionStorageValue(cacheKey, serializedCacheEntry);
    if (retryWriteResult !== 'quota') {
        return;
    }

    const reducedEntry: ExecutiveDashboardViewCacheEntry = {
        ...cacheEntry,
        snapshot: buildReducedExecutiveViewSnapshot(cacheEntry.snapshot),
    };
    trySetSessionStorageValue(cacheKey, JSON.stringify(reducedEntry));
};

const EXECUTIVE_THRESHOLD_CONFIG_SESSION_KEY = 'eimExecutiveThresholdConfig';
const EXECUTIVE_PERIOD_OPTION_SESSION_KEY = 'eimExecutivePeriodOption';
const EXECUTIVE_PERIOD_RANGE_SESSION_KEY = 'eimExecutivePeriodRange';
const EXECUTIVE_TRANSIT_TREND_TIME_RANGE_SESSION_KEY = 'eimExecutiveTransitTrendTimeRange';
const EXECUTIVE_FONT_SIZE_LEVEL_SESSION_KEY = 'eimExecutiveFontSizeLevel';
const EXECUTIVE_SCROLL_Y_SESSION_KEY = 'eimExecutiveScrollY';
const DEFAULT_FONT_SIZE_LEVEL = 4; // 0=smallest, 4=default, 9=largest

const getSavedFontSizeLevel = (): number => {
    const raw = globalThis.sessionStorage.getItem(EXECUTIVE_FONT_SIZE_LEVEL_SESSION_KEY);
    if (!raw) {
        return DEFAULT_FONT_SIZE_LEVEL;
    }
    const level = parseInt(raw, 10);
    return !isNaN(level) && level >= 0 && level <= 9 ? level : DEFAULT_FONT_SIZE_LEVEL;
};

const DEFAULT_EXECUTIVE_THRESHOLDS: ExecutiveThresholdConfig = {
    onTimePositiveThreshold: 95,
    onTimeWarningThreshold: 85,
    failureCriticalThreshold: 5,
    overdueWarningThreshold: 50,
    overdueCriticalThreshold: 200,
    overdueHoursThreshold: 24,
    coveragePositiveThreshold: 95,
    coverageWarningThreshold: 85,
    emailTransitSlaHours: 2,
    portalTransitSlaHours: 2,
    ediTransitSlaHours: 8,
    enableValueZoom: true,
};

// Feature flag for Phase 2 executive dashboard query rollout
// Set to 'true' via REACT_APP_ENABLE_EXECUTIVE_DASHBOARD_V2 env var to enable the new query
// Default: true (enabled). Set env var to 'false' only to force legacy fallback.
const ENABLE_EXECUTIVE_DASHBOARD_V2 = process.env.REACT_APP_ENABLE_EXECUTIVE_DASHBOARD_V2 !== 'false';

const { RangePicker } = DatePicker;

const DEFAULT_PERIOD_OPTION: PeriodOption = 'today';

const PERIOD_OPTIONS: PeriodOption[] = ['today', 'currentMonth', 'lastWeek', 'lastMonth', 'last6Months', 'lastYear', 'custom'];

const PERIOD_OPTION_LABELS: Record<PeriodOption, { key: string; defaultLabel: string }> = {
    today: { key: 'eim-period-today', defaultLabel: 'Today' },
    currentMonth: { key: 'eim-period-currentMonth', defaultLabel: 'Current month' },
    lastWeek: { key: 'eim-period-lastWeek', defaultLabel: 'Last week' },
    lastMonth: { key: 'eim-period-lastMonth', defaultLabel: 'Last month' },
    last6Months: { key: 'eim-period-last6Months', defaultLabel: 'Last 6 months' },
    lastYear: { key: 'eim-period-lastYear', defaultLabel: 'Last year' },
    custom: { key: 'eim-period-custom', defaultLabel: 'Custom date' },
};

const filterTransitTrendDataByRange = (
    trendDataDaily: Array<{ month: string; avgTransitDays: number }>,
    trendDataYearly: Array<{ month: string; avgTransitDays: number }>,
    timeRange: TransitTrendTimeRange,
    trendDataHourly: Array<{ month: string; avgTransitDays: number }> = []
): Array<{ month: string; avgTransitDays: number }> => {
    if (timeRange === 'today') {
        if (trendDataHourly.length > 0) {
            return trendDataHourly;
        }
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        return trendDataDaily.slice(-1);
    } else if (timeRange === 'currentMonth') {
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        const currentMonthPrefix = dayjs().format('YYYY-MM');
        return trendDataDaily.filter((point) => String(point?.month ?? '').startsWith(currentMonthPrefix));
    } else if (timeRange === '30d') {
        // 30 days: show last 30 days of daily data (independent of dashboard period)
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        return trendDataDaily.slice(-30);
    } else if (timeRange === '6m') {
        // 6 months: show last 6 months of monthly data (independent of dashboard period)
        if (!trendDataYearly || trendDataYearly.length === 0) {
            return [];
        }
        return trendDataYearly.slice(-6);
    } else if (timeRange === '1y') {
        // 1 year: show last 12 months of monthly data (independent of dashboard period)
        if (!trendDataYearly || trendDataYearly.length === 0) {
            return [];
        }
        return trendDataYearly;
    }

    return [];
};

const filterTrendValuesByRange = (
    trendDataHourly: Array<{ label: string; value: number }>,
    trendDataDaily: Array<{ label: string; value: number }>,
    trendDataYearly: Array<{ label: string; value: number }>,
    timeRange: TransitTrendTimeRange
): Array<{ label: string; value: number }> => {
    if (timeRange === 'today') {
        if (trendDataHourly.length > 0) {
            return trendDataHourly;
        }
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        return trendDataDaily.slice(-1);
    }

    if (timeRange === 'currentMonth') {
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        const currentMonthPrefix = dayjs().format('YYYY-MM');
        return trendDataDaily.filter((point) => String(point?.label ?? '').startsWith(currentMonthPrefix));
    }

    if (timeRange === '30d') {
        if (!trendDataDaily || trendDataDaily.length === 0) {
            return [];
        }
        return trendDataDaily.slice(-30);
    }

    if (timeRange === '6m') {
        if (!trendDataYearly || trendDataYearly.length === 0) {
            return [];
        }
        return trendDataYearly.slice(-6);
    }

    if (timeRange === '1y') {
        if (!trendDataYearly || trendDataYearly.length === 0) {
            return [];
        }
        return trendDataYearly;
    }

    return [];
};

const mapTrendPoints = (raw: any[] | undefined, mode: 'inbound' | 'outbound'): Array<{ label: string; value: number }> => (
    Array.isArray(raw)
        ? raw
            .map((point) => {
                const label = point?.month ?? point?.date ?? point?.label;
                const valueCandidate = mode === 'inbound'
                    ? (point?.inbound ?? point?.count ?? point?.total ?? point?.volume)
                    : (point?.outbound ?? point?.count ?? point?.total ?? point?.volume);
                const value = Number(valueCandidate ?? 0);
                return {
                    label: String(label ?? ''),
                    value: Number.isFinite(value) ? value : 0,
                };
            })
            .filter((point) => point.label.length > 0)
        : []
);

const getVolumeTrendByRange = (
    volumeMetrics: any,
    trendSeries: InboundOutboundTrendSeries | undefined,
    timeRange: TransitTrendTimeRange,
    mode: 'inbound' | 'outbound'
): Array<{ label: string; value: number }> => {
    const seriesHourly = (trendSeries?.hourly ?? []).map((point) => ({
        label: point.label,
        value: mode === 'inbound' ? point.inbound : point.outbound,
    }));
    const seriesDaily = (trendSeries?.daily ?? []).map((point) => ({
        label: point.label,
        value: mode === 'inbound' ? point.inbound : point.outbound,
    }));
    const seriesMonthly = (trendSeries?.monthly ?? []).map((point) => ({
        label: point.label,
        value: mode === 'inbound' ? point.inbound : point.outbound,
    }));

    const seriesDriven = filterTrendValuesByRange(seriesHourly, seriesDaily, seriesMonthly, timeRange);
    if (seriesDriven.length > 0) {
        return seriesDriven;
    }

    const inboundTotal = Number(volumeMetrics?.inboundCount ?? 0);
    const outboundTotal = Number(volumeMetrics?.outboundCount ?? 0);
    const directionalTotal = mode === 'inbound' ? inboundTotal : outboundTotal;
    const splitTotal = inboundTotal + outboundTotal;
    const ratio = splitTotal > 0 ? directionalTotal / splitTotal : 0;

    const monthlyFallback = Array.isArray(volumeMetrics?.volumeTrendByMonth)
        ? volumeMetrics.volumeTrendByMonth
            .map((point: any) => {
                const label = String(point?.month ?? '');
                const total = Number(point?.total ?? 0);
                const value = Number.isFinite(total) ? Math.round(total * ratio) : 0;
                return { label, value };
            })
            .filter((point: { label: string; value: number }) => point.label.length > 0)
        : [];

    const daily = mapTrendPoints(mode === 'inbound' ? volumeMetrics?.inboundTrendByDay : volumeMetrics?.outboundTrendByDay, mode);
    const yearly = mapTrendPoints(mode === 'inbound' ? volumeMetrics?.inboundTrendByYear : volumeMetrics?.outboundTrendByYear, mode);

    return filterTrendValuesByRange(
        [],
        daily.length ? daily : monthlyFallback,
        yearly.length ? yearly : monthlyFallback,
        timeRange
    );
};

const EMPTY_EXECUTIVE_METRICS_V2 = {
    volume: {
        totalProcessed: 0,
        inboundCount: 0,
        outboundCount: 0,
        previousPeriodTotal: 0,
        volumeDelta: 0,
        failedCount: 0,
        inTransitCount: 0,
        busnTransitCount: 0,
        failureRate: 0,
        failureRateTarget: 0,
        failureRateThreshold: 0,
        volumeTrendByMonth: [],
    },
    flowHealth: {
        stpRate: 0,
        previousStpRate: 0,
        stpDelta: 0,
        stpTarget: 0,
        stpThreshold: 0,
        onTimeRate: 0,
        previousOnTimeRate: 0,
        onTimeDelta: 0,
        onTimeTarget: 0,
        onTimeThreshold: 0,
        avgTransitDays: 0,
        previousAvgTransitDays: 0,
        transitDelta: '0.0d',
        stpRateTrendByMonth: [],
        stpRateTrendByDay: [],
        stpRateTrendByYear: [],
    },
    risk: {
        overdueCount: 0,
        previousOverdueCount: 0,
        overdueDelta: 0,
        overdueTarget: 0,
        overdueThreshold: 0,
        overdueAvgDays: 0,
        failedCount: 0,
        failureRate: 0,
        previousFailureRate: 0,
        failureRateDelta: 0,
        failureRateTarget: 0,
        failureRateThreshold: 0,
        agingBuckets: [],
        overdueTrendByMonth: [],
    },
    coverage: {
        activePartners: 0,
        totalPartners: 0,
        coverageRate: 0,
        previousCoverageRate: 0,
        coverageDelta: 0,
        coverageTarget: 0,
        coverageThreshold: 0,
        monthlyNewPartners: 0,
        topImpactedGroups: [],
        coverageTrendByMonth: [],
    },
    metadata: {
        healthScoreLabel: '-',
    },
};

const getPeriodRangeForOption = (option: PeriodOption): PeriodRangeValue => {
    const today = dayjs();

    switch (option) {
        case 'today':
            return [today.startOf('day'), today.endOf('day')];
        case 'currentMonth':
            return [today.startOf('month'), today.endOf('day')];
        case 'lastWeek':
            return [today.subtract(6, 'day').startOf('day'), today.endOf('day')];
        case 'lastMonth':
            return [today.subtract(1, 'month').startOf('day'), today.endOf('day')];
        case 'last6Months':
            return [today.subtract(6, 'month').startOf('day'), today.endOf('day')];
        case 'lastYear':
            return [today.subtract(1, 'year').startOf('day'), today.endOf('day')];
        case 'custom':
        default:
            return [today.subtract(6, 'month').startOf('day'), today.endOf('day')];
    }
};

const toRequestDate = (value: Dayjs): string => value.format('YYYY-MM-DD');

const getRequestRange = (range: PeriodRangeValue) => {
    const [start, end] = range;

    return {
        fromDate: start.startOf('day'),
        toDate: end.endOf('day'),
    };
};

const getSavedPeriodOption = (): PeriodOption | undefined => {
    const raw = globalThis.sessionStorage.getItem(EXECUTIVE_PERIOD_OPTION_SESSION_KEY);
    if (!raw) {
        return undefined;
    }

    if (PERIOD_OPTIONS.includes(raw as PeriodOption)) {
        return raw as PeriodOption;
    }

    return undefined;
};

const getSavedPeriodRange = (): PeriodRangeValue | undefined => {
    const raw = globalThis.sessionStorage.getItem(EXECUTIVE_PERIOD_RANGE_SESSION_KEY);
    if (!raw) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(raw) as { from?: string; to?: string };
        const from = parsed?.from ? dayjs(parsed.from) : undefined;
        const to = parsed?.to ? dayjs(parsed.to) : undefined;

        if (!from?.isValid() || !to?.isValid()) {
            return undefined;
        }

        const todayEnd = dayjs().endOf('day');
        const safeFrom = from.startOf('day');
        const safeTo = to.endOf('day').isAfter(todayEnd) ? todayEnd : to.endOf('day');

        if (safeFrom.isAfter(safeTo)) {
            return undefined;
        }

        return [safeFrom, safeTo];
    } catch {
        return undefined;
    }
};

const getSavedTransitTrendTimeRange = (): TransitTrendTimeRange => {
    const raw = globalThis.sessionStorage.getItem(EXECUTIVE_TRANSIT_TREND_TIME_RANGE_SESSION_KEY);
    if (raw === 'today' || raw === 'currentMonth' || raw === '30d' || raw === '6m' || raw === '1y') {
        return raw;
    }
    return '30d';
};

const getRatio = (value?: number | null, total?: number | null) => {
    if (!total) {
        return 0;
    }

    return ((value ?? 0) / total) * 100;
};

const toThresholdConfig = (value: unknown): ExecutiveThresholdConfig => {
    if (!value || typeof value !== 'object') {
        return DEFAULT_EXECUTIVE_THRESHOLDS;
    }

    const source = value as Partial<ExecutiveThresholdConfig>;
    const toNumber = (input: unknown, fallback: number) => {
        const parsed = typeof input === 'number' ? input : Number(input);
        return Number.isFinite(parsed) ? parsed : fallback;
    };
    const toBoolean = (input: unknown, fallback: boolean) => {
        if (typeof input === 'boolean') {
            return input;
        }
        if (typeof input === 'string') {
            return input.toLowerCase() === 'true';
        }
        return fallback;
    };

    return {
        onTimePositiveThreshold: toNumber(source.onTimePositiveThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.onTimePositiveThreshold),
        onTimeWarningThreshold: toNumber(source.onTimeWarningThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.onTimeWarningThreshold),
        failureCriticalThreshold: toNumber(source.failureCriticalThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.failureCriticalThreshold),
        overdueWarningThreshold: toNumber(source.overdueWarningThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.overdueWarningThreshold),
        overdueCriticalThreshold: toNumber(source.overdueCriticalThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.overdueCriticalThreshold),
        overdueHoursThreshold: toNumber(source.overdueHoursThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.overdueHoursThreshold),
        coveragePositiveThreshold: toNumber(source.coveragePositiveThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.coveragePositiveThreshold),
        coverageWarningThreshold: toNumber(source.coverageWarningThreshold, DEFAULT_EXECUTIVE_THRESHOLDS.coverageWarningThreshold),
        emailTransitSlaHours: toNumber(source.emailTransitSlaHours, DEFAULT_EXECUTIVE_THRESHOLDS.emailTransitSlaHours),
        portalTransitSlaHours: toNumber(source.portalTransitSlaHours, DEFAULT_EXECUTIVE_THRESHOLDS.portalTransitSlaHours),
        ediTransitSlaHours: toNumber(source.ediTransitSlaHours, DEFAULT_EXECUTIVE_THRESHOLDS.ediTransitSlaHours),
        enableValueZoom: toBoolean(source.enableValueZoom, DEFAULT_EXECUTIVE_THRESHOLDS.enableValueZoom),
    };
};

const loadThresholdConfigFromSession = (): ExecutiveThresholdConfig => {
    const raw = globalThis.sessionStorage.getItem(EXECUTIVE_THRESHOLD_CONFIG_SESSION_KEY);
    if (!raw) {
        return DEFAULT_EXECUTIVE_THRESHOLDS;
    }

    try {
        return toThresholdConfig(JSON.parse(raw));
    } catch {
        return DEFAULT_EXECUTIVE_THRESHOLDS;
    }
};

const getCountryProcessingTone = (
    thresholds: ExecutiveThresholdConfig,
    onTimeRate?: number | null,
): MetricTone => {
    if ((onTimeRate ?? 0) >= thresholds.onTimePositiveThreshold) {
        return 'positive';
    }
    if ((onTimeRate ?? 0) >= thresholds.onTimeWarningThreshold) {
        return 'warning';
    }
    return 'critical';
};

const getCountryExceptionsTone = (
    thresholds: ExecutiveThresholdConfig,
    failureRate?: number | null,
): MetricTone => {
    return (failureRate ?? 0) > thresholds.failureCriticalThreshold ? 'critical' : 'warning';
};

const getCountryCoverageTone = (
    thresholds: ExecutiveThresholdConfig,
    coverageRate?: number | null,
): MetricTone => {
    if ((coverageRate ?? 0) >= thresholds.coveragePositiveThreshold) {
        return 'positive';
    }
    if ((coverageRate ?? 0) >= thresholds.coverageWarningThreshold) {
        return 'warning';
    }
    return 'critical';
};

const getCountryOverdueTone = (
    thresholds: ExecutiveThresholdConfig,
    overdueCount?: number | null,
): MetricTone => {
    const count = overdueCount ?? 0;
    if (count === 0) {
        return 'neutral';
    }
    if (count < thresholds.overdueWarningThreshold) {
        return 'positive';
    }
    if (count < thresholds.overdueCriticalThreshold) {
        return 'warning';
    }
    return 'critical';
};

const getChannelTransitThresholdHours = (
    thresholds: ExecutiveThresholdConfig,
    channel?: string | null,
) => {
    const normalizedChannel = (channel ?? '').trim().toUpperCase();

    if (normalizedChannel === 'EDI' || normalizedChannel === 'QBQ') {
        return thresholds.ediTransitSlaHours;
    }
    if (normalizedChannel === 'PORTAL') {
        return thresholds.portalTransitSlaHours;
    }
    return thresholds.emailTransitSlaHours;
};

const getChannelTransitTone = (
    thresholds: ExecutiveThresholdConfig,
    channel?: string | null,
    avgTransitDays?: number | null,
): MetricTone => {
    const transitHours = (avgTransitDays ?? 0) * 24;
    if (transitHours <= 0) {
        return 'neutral';
    }

    const thresholdHours = getChannelTransitThresholdHours(thresholds, channel);
    if (transitHours <= thresholdHours) {
        return 'positive';
    }
    if (transitHours <= thresholdHours * 2) {
        return 'warning';
    }
    return 'critical';
};

const getStatusFiltersForMetricTile = (status: MetricTileStatus): string[] | undefined => {
    if (status === 'successful') {
        return ['PASS'];
    }
    if (status === 'inTransit') {
        return ['INTRANSIT', 'BUSN', 'NONE'];
    }
    if (status === 'failed') {
        return ['FAIL', 'OVERDUE'];
    }
    return undefined;
};

const formatOverdueThresholdText = (template: string, overdueHoursThreshold: number): string => template
    .replace('>24h', `>${overdueHoursThreshold}h`)
    .replace('24h', `${overdueHoursThreshold}h`)
    .replace('24 hours', `${overdueHoursThreshold} hours`)
    .replace('24 Stunden', `${overdueHoursThreshold} Stunden`);

const getGroupSuccessTone = (successfulCount: number, totalInvoices: number): 'positive' | 'neutral' | 'warning' => {
    if (successfulCount > totalInvoices * 0.95) {
        return 'positive';
    }
    if (successfulCount > totalInvoices * 0.85) {
        return 'neutral';
    }
    return 'warning';
};

const getGroupFailedTone = (failedCount: number, totalInvoices: number): 'positive' | 'neutral' | 'warning' | 'critical' => {
    if (failedCount === 0) {
        return 'positive';
    }
    if (failedCount < totalInvoices * 0.05) {
        return 'neutral';
    }
    if (failedCount < totalInvoices * 0.1) {
        return 'warning';
    }
    return 'critical';
};

const getGroupInTransitTone = (inTransitCount: number, totalInvoices: number): 'positive' | 'neutral' | 'warning' => {
    if (inTransitCount === 0) {
        return 'positive';
    }
    if (inTransitCount < totalInvoices * 0.2) {
        return 'neutral';
    }
    return 'warning';
};

const toTrendLevels = (values: number[], maxBars = 16): number[] => {
    const sanitizedValues = values.filter((value) => Number.isFinite(value));
    if (sanitizedValues.length === 0) {
        return new Array(Math.min(maxBars, 10)).fill(2);
    }

    const slicedValues = sanitizedValues.slice(-maxBars);
    const minValue = Math.min(...slicedValues);
    const maxValue = Math.max(...slicedValues);
    const span = Math.max(1, maxValue - minValue);

    return slicedValues.map((value) => {
        const ratio = (value - minValue) / span;

        if (ratio >= 0.85) return 6;
        if (ratio >= 0.7) return 5;
        if (ratio >= 0.55) return 4;
        if (ratio >= 0.4) return 3;
        if (ratio >= 0.25) return 2;
        return 1;
    });
};

const getDeltaIcon = (direction: V2DeltaDirection | undefined): string => {
    if (direction === 'up') {
        return '↗';
    }
    if (direction === 'down') {
        return '↘';
    }
    return '→';
};

const toSparklinePoints = (levels: number[]): string => {
    if (!levels.length) {
        return '0,18 100,18';
    }

    const maxIndex = Math.max(1, levels.length - 1);
    return levels
        .map((level, index) => {
            const x = (index / maxIndex) * 100;
            const y = 22 - Math.max(2, Math.min(20, level * 3));
            return `${x},${y}`;
        })
        .join(' ');
};

const getEstimatedOverdueAgeDays = (agingBuckets?: Array<{ ageRange?: string | null; count?: number | null }> | null) => {
    const buckets = agingBuckets ?? [];
    if (!buckets.length) {
        return 0;
    }

    let weightedSum = 0;
    let totalCount = 0;

    buckets.forEach((bucket) => {
        const count = bucket.count ?? 0;
        if (count <= 0) {
            return;
        }

        let representativeAge = 0;
        if (bucket.ageRange === '0-7d') {
            representativeAge = 3.5;
        } else if (bucket.ageRange === '8-30d') {
            representativeAge = 19;
        } else if (bucket.ageRange === '30+d') {
            representativeAge = 45;
        }

        weightedSum += representativeAge * count;
        totalCount += count;
    });

    if (!totalCount) {
        return 0;
    }

    return weightedSum / totalCount;
};

const getGroupOverdueRate = (group: { overdueCount?: number | null; volume?: number | null }) => {
    const volume = group.volume ?? 0;
    if (volume <= 0) {
        return 0;
    }

    return ((group.overdueCount ?? 0) / volume) * 100;
};

const EIMExecutiveDashboard: FunctionComponent<IAppComponentProps> = (props) => {
    const [thresholdForm] = Form.useForm<ExecutiveThresholdConfig>();
    const [loading, setLoading] = useState(false);
    const [theme, setTheme] = useState<ThemeMode>(() => {
        const storedTheme = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        return storedTheme === 'light' ? 'light' : 'dark';
    });
    const intl = useIntl();
    const navigate = useNavigate();

    const [selectedPeriodOption, setSelectedPeriodOption] = useState<PeriodOption>(() => getSavedPeriodOption() ?? DEFAULT_PERIOD_OPTION);
    const [selectedRangeValue, setSelectedRangeValue] = useState<PeriodRangeValue>(() => {
        const savedOption = getSavedPeriodOption() ?? DEFAULT_PERIOD_OPTION;
        const savedRange = getSavedPeriodRange();

        if (savedOption === 'custom' && savedRange) {
            return savedRange;
        }

        return getPeriodRangeForOption(savedOption);
    });

    const [activePartnerDataGraphOption, setActivePartnerDataGraphOption] = useState(undefined);
    const [graphData, setGraphData] = useState(undefined);
    const [rawGroupCountData, setRawGroupCountData] = useState<any[]>(undefined);
    const [rawAllActivePartnerData, setRawAllActivePartnerData] = useState<any[]>(undefined);
    const [rawPartnerInvoiceSummaryData, setRawPartnerInvoiceSummaryData] = useState<PartnerInvoiceSummaryItem[]>([]);
    const [activePartnerTotal, setActivePartnerTotal] = useState(0);
    const [activePartnerGroupCount, setActivePartnerGroupCount] = useState(0);
    const [dashboardCounts, setDashboardCounts] = useState<EimDashboardCardsResponse>(undefined);
    const [executiveMetricsV2, setExecutiveMetricsV2] = useState<any>(undefined);
    const [volumeTrendMetricsV2, setVolumeTrendMetricsV2] = useState<any>(undefined);
    const [inboundOutboundTrendSeries, setInboundOutboundTrendSeries] = useState<InboundOutboundTrendSeries | undefined>(undefined);
    const [todayTransitHourlyTrendData, setTodayTransitHourlyTrendData] = useState<TransitHourlyTrendPoint[]>([]);
    const [countryMetrics, setCountryMetrics] = useState<CountryMetric[]>([]);
    const [systemMetrics, setSystemMetrics] = useState<SystemMetric[]>([]);
    const [channelMetrics, setChannelMetrics] = useState<ChannelMetric[]>([]);
    const [groupMetrics, setGroupMetrics] = useState<GroupMetric[]>([]);
    const [topFailureReasons, setTopFailureReasons] = useState<FailureReason[]>([]);
    const [topFailureProcesses, setTopFailureProcesses] = useState<FailureProcess[]>([]);
    const [topFailureCompanyCodes, setTopFailureCompanyCodes] = useState<FailureCompanyCode[]>([]);
    const [topGroupRankMode, setTopGroupRankMode] = useState<TopGroupRankMode>('failureRate');
    const [topFailureReasonRankMode, setTopFailureReasonRankMode] = useState<TopGroupRankMode>('failureRate');
    const [topFailureProcessRankMode, setTopFailureProcessRankMode] = useState<TopGroupRankMode>('failureRate');
    const [topFailureCompanyCodeRankMode, setTopFailureCompanyCodeRankMode] = useState<TopGroupRankMode>('failureRate');
    const [transitTrendTimeRange, setTransitTrendTimeRange] = useState<TransitTrendTimeRange>(() => getSavedTransitTrendTimeRange());
    const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
    const [channelBreakdownModalOpen, setChannelBreakdownModalOpen] = useState(false);
    const [channelBreakdownZoomLevel, setChannelBreakdownZoomLevel] = useState(0);
    const [thresholdConfig, setThresholdConfig] = useState<ExecutiveThresholdConfig>(() => loadThresholdConfigFromSession());
    const [fontSizeLevel, setFontSizeLevel] = useState<number>(() => getSavedFontSizeLevel());
    const [worldMapReady, setWorldMapReady] = useState(false);
    const [worldMapLoadFailed, setWorldMapLoadFailed] = useState(false);
    const [worldMapModalOpen, setWorldMapModalOpen] = useState(false);
    const [countryActionSelection, setCountryActionSelection] = useState<CountryActionSelection | null>(null);
    const [countryDetailsSelection, setCountryDetailsSelection] = useState<CountryDetailsSelection | null>(null);
    const [allPartnersModalOpen, setAllPartnersModalOpen] = useState(false);
    const [allPartnersSortKey, setAllPartnersSortKey] = useState<CountryDetailsPartnerSortKey>('volume');
    const [allPartnersSortDirection, setAllPartnersSortDirection] = useState<CountryDetailsPartnerSortDirection>('desc');
    const [countryDetailsHourlyTrend, setCountryDetailsHourlyTrend] = useState<CountryHourlyTrendPoint[]>([]);
    const [countryDetailsTopErrorMessages, setCountryDetailsTopErrorMessages] = useState<CountryDetailErrorMessageItem[]>([]);
    const [countryDetailsTrendChartReady, setCountryDetailsTrendChartReady] = useState(false);
    const worldMapModalChartRef = useRef<ReactEcharts | null>(null);
    const pendingScrollRestoreRef = useRef<number | null>(null);
    const countryDetailsHourlyTrendCacheRef = useRef<Record<string, CountryHourlyTrendPoint[]>>({});
    const partnerSummaryInvalidShapeWarningRef = useRef<string>('');

    const [getAllActivePartnerGraphData] = useCountAllActivePartnersLazyQuery();
    const [getAllActivePartners] = useAllActivePartnersLazyQuery();
    const [getDashCardCount] = useGetDashCardCountLazyQuery();
    const [getEimExecutiveDashboard] = useLazyQuery<ExecutiveDashboardQueryData, ExecutiveDashboardQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD);
    const [getCountryMetrics] = useLazyQuery<CountryMetricsQueryData, CountryMetricsQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_COUNTRY_METRICS);
    const [getSystemMetrics] = useLazyQuery<SystemMetricsQueryData, SystemMetricsQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_SYSTEM_METRICS);
    const [getChannelMetrics] = useLazyQuery<ChannelMetricsQueryData, ChannelMetricsQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_CHANNEL_METRICS);
    const [getGroupMetrics] = useLazyQuery<GroupMetricsQueryData, GroupMetricsQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_GROUP_METRICS);
    const [getTopFailureReasons] = useLazyQuery<TopFailureReasonsQueryData, TopFailureReasonsQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_REASONS);
    const [getTopFailureProcesses] = useLazyQuery<TopFailureProcessesQueryData, TopFailureProcessesQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_PROCESSES);
    const [getTopFailureCompanyCodes] = useLazyQuery<TopFailureCompanyCodesQueryData, TopFailureCompanyCodesQueryVariables>(QUERY_EIM_EXECUTIVE_DASHBOARD_TOP_FAILURE_COMPANY_CODES);
    const [getActivePartnerInvoiceHourlyTrend] = useLazyQuery<PartnerInvoiceHourlyTrendQueryData, PartnerInvoiceHourlyTrendQueryVariables>(QUERY_EIM_ACTIVE_PARTNER_INVOICE_HOURLY_TREND);
    const [getActivePartnerInvoiceSummary] = useLazyQuery<PartnerInvoiceSummaryQueryData, PartnerInvoiceSummaryQueryVariables>(QUERY_EIM_ACTIVE_PARTNER_INVOICE_SUMMARY);
    const [getActivePartnerCountryDetail] = useLazyQuery<ActivePartnerCountryDetailQueryData, ActivePartnerCountryDetailQueryVariables>(QUERY_EIM_ACTIVE_PARTNER_COUNTRY_DETAIL);

    const companyCountryLookup = useMemo(() => buildCompanyCountryLookup(countryMetrics), [countryMetrics]);

    const getInboundOutboundTrendCacheKey = useCallback(() => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        return `${EXECUTIVE_INBOUND_OUTBOUND_TREND_CACHE_PREFIX}:${locale}:${thresholdConfig.overdueHoursThreshold}:${dayjs().format('YYYY-MM-DD')}`;
    }, [thresholdConfig.overdueHoursThreshold]);

    const getTodayHourlyTrendCacheKey = useCallback(() => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        return `${EXECUTIVE_TODAY_HOURLY_TREND_CACHE_PREFIX}:${locale}:${dayjs().format('YYYY-MM-DD')}`;
    }, []);

    const currentRequestRange = useMemo(
        () => getRequestRange(selectedRangeValue),
        [selectedRangeValue]
    );

    const persistScrollPosition = useCallback(() => {
        const scrollY = globalThis.scrollY || globalThis.pageYOffset || 0;
        globalThis.sessionStorage.setItem(EXECUTIVE_SCROLL_Y_SESSION_KEY, scrollY.toString());
    }, []);

    useEffect(() => {
        const raw = globalThis.sessionStorage.getItem(EXECUTIVE_SCROLL_Y_SESSION_KEY);
        if (!raw) {
            return;
        }

        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed >= 0) {
            pendingScrollRestoreRef.current = parsed;
        }
        globalThis.sessionStorage.removeItem(EXECUTIVE_SCROLL_Y_SESSION_KEY);
    }, []);

    useEffect(() => {
        if (loading || pendingScrollRestoreRef.current === null) {
            return;
        }

        const targetScrollY = pendingScrollRestoreRef.current;
        pendingScrollRestoreRef.current = null;

        globalThis.requestAnimationFrame(() => {
            globalThis.scrollTo({ top: targetScrollY, behavior: 'auto' });
            globalThis.requestAnimationFrame(() => {
                globalThis.scrollTo({ top: targetScrollY, behavior: 'auto' });
            });
        });
    }, [loading]);

    useEffect(() => {
        try {
            echarts.registerMap('world', getBmwTerritoryWorldGeoJson(worldGeoJson));
            setWorldMapReady(true);
            setWorldMapLoadFailed(false);
        } catch (error) {
            console.error(error);
            setWorldMapReady(false);
            setWorldMapLoadFailed(true);
        }
    }, []);

    const getExecutiveDashboardViewCacheKey = useCallback((request?: EimDashboardRequest) => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        const fromDate = request?.fromDate ?? currentRequestRange.fromDate;
        const toDate = request?.toDate ?? currentRequestRange.toDate;
        return `${EXECUTIVE_DASHBOARD_VIEW_CACHE_PREFIX}:${locale}:${fromDate.format('YYYY-MM-DD')}:${toDate.format('YYYY-MM-DD')}:${thresholdConfig.overdueHoursThreshold}`;
    }, [currentRequestRange.fromDate, currentRequestRange.toDate, thresholdConfig.overdueHoursThreshold]);

    const hydrateDashboardFromCache = useCallback((snapshot: ExecutiveDashboardViewCache) => {
        const groupCountData = snapshot.rawGroupCountData ?? [];
        const allActivePartnerData = snapshot.rawAllActivePartnerData ?? [];

        setDashboardCounts(snapshot.dashboardCounts);
        setExecutiveMetricsV2(snapshot.executiveMetricsV2);
        setVolumeTrendMetricsV2(snapshot.volumeTrendMetricsV2);
        setInboundOutboundTrendSeries(snapshot.inboundOutboundTrendSeries);
        setTodayTransitHourlyTrendData(snapshot.todayTransitHourlyTrendData ?? []);
        setCountryMetrics(snapshot.countryMetrics ?? []);
        setSystemMetrics(snapshot.systemMetrics ?? []);
        setChannelMetrics(snapshot.channelMetrics ?? []);
        setGroupMetrics(snapshot.groupMetrics ?? []);
        setTopFailureReasons(snapshot.topFailureReasons ?? []);
        setTopFailureProcesses(snapshot.topFailureProcesses ?? []);
        setTopFailureCompanyCodes(snapshot.topFailureCompanyCodes ?? []);
        setRawGroupCountData(groupCountData);
        setRawAllActivePartnerData(allActivePartnerData);
        setActivePartnerDataGraphOption(getActivePartnerGroupBarChartOptions(groupCountData, theme, intl));
        setGraphData(getAllActiveInvoiceGraphOptions(allActivePartnerData, theme, intl));
        setActivePartnerTotal(snapshot.activePartnerTotal ?? 0);
        setActivePartnerGroupCount(snapshot.activePartnerGroupCount ?? 0);
    }, [intl, theme]);

    const persistDateFilters = useCallback(() => {
        globalThis.sessionStorage.setItem(OPERATIONAL_DATA_ORIGIN_KEY, EXECUTIVE_DASHBOARD_V1_ROUTE);
        globalThis.sessionStorage.setItem('fromDate', currentRequestRange.fromDate.format('YYYY-MM-DD'));
        globalThis.sessionStorage.setItem('toDate', currentRequestRange.toDate.format('YYYY-MM-DD'));
        globalThis.sessionStorage.removeItem('type');
        globalThis.sessionStorage.removeItem('company');
        globalThis.sessionStorage.removeItem('process');
        globalThis.sessionStorage.removeItem('companyGroup');
        globalThis.sessionStorage.removeItem('statusFilters');
        globalThis.sessionStorage.removeItem('partnerCountry');
        globalThis.sessionStorage.removeItem('systemIds');
    }, [currentRequestRange]);

    const redirectToMain = useCallback(() => {
        persistScrollPosition();
        persistDateFilters();
        navigate('../eim-document-overview-monitoring');
    }, [navigate, persistDateFilters, persistScrollPosition]);

    const openMetricDetails = useCallback((filterType?: string) => {
        if (!filterType) {
            return;
        }
        if (filterType === PROVIDER_BREAKDOWN_ACTION) {
            setChannelBreakdownModalOpen(true);
            return;
        }
        persistScrollPosition();
        persistDateFilters();
        globalThis.sessionStorage.setItem('type', filterType);
        navigate('../eim-document-overview-monitoring');
    }, [navigate, persistDateFilters, persistScrollPosition]);

    const openOperationalViewForMetricTile = useCallback((options: {
        status: MetricTileStatus;
        country?: string | null;
        companyCode?: string | null;
        systemLabel?: string | null;
        groupName?: string | null;
    }) => {
        persistScrollPosition();
        persistDateFilters();

        const statusFilters = getStatusFiltersForMetricTile(options.status);
        if (statusFilters && statusFilters.length > 0) {
            globalThis.sessionStorage.setItem('statusFilters', JSON.stringify(statusFilters));
        }

        if (options.country) {
            globalThis.sessionStorage.setItem('partnerCountry', options.country);
        }
        if (options.companyCode) {
            globalThis.sessionStorage.setItem('company', JSON.stringify([options.companyCode]));
        }
        if (options.systemLabel) {
            globalThis.sessionStorage.setItem('systemIds', JSON.stringify([options.systemLabel]));
        }
        if (options.groupName) {
            globalThis.sessionStorage.setItem('companyGroup', JSON.stringify([options.groupName]));
        }

        navigate('../eim-document-overview-monitoring');
    }, [navigate, persistDateFilters, persistScrollPosition]);

    const openCountryDetailsView = useCallback((options: {
        country: string;
        companyCode?: string | null;
    }) => {
        setCountryDetailsSelection({
            country: options.country,
            companyCode: options.companyCode,
        });
    }, []);

    const openCountryActionModal = useCallback((options: {
        country?: string | null;
        companyCode?: string | null;
    }) => {
        const country = options.country?.trim();
        if (!country) {
            return;
        }

        setCountryActionSelection({
            country,
            companyCode: options.companyCode,
        });
    }, []);

    const closeCountryActionModal = useCallback(() => {
        setCountryActionSelection(null);
    }, []);

    const closeCountryDetailsModal = useCallback(() => {
        setCountryDetailsSelection(null);
        setAllPartnersModalOpen(false);
    }, []);

    const openAllPartnersModal = useCallback(() => {
        setAllPartnersModalOpen(true);
    }, []);

    const closeAllPartnersModal = useCallback(() => {
        setAllPartnersModalOpen(false);
    }, []);

    const handleAllPartnersSort = useCallback((key: CountryDetailsPartnerSortKey) => {
        if (allPartnersSortKey === key) {
            setAllPartnersSortDirection((previousDirection) => (previousDirection === 'desc' ? 'asc' : 'desc'));
            return;
        }

        setAllPartnersSortKey(key);
        setAllPartnersSortDirection('desc');
    }, [allPartnersSortKey]);

    const handleOpenCountryOperationalDetails = useCallback(() => {
        if (!countryActionSelection?.country) {
            return;
        }

        openOperationalViewForMetricTile({
            status: 'total',
            country: countryActionSelection.country,
            companyCode: countryActionSelection.companyCode,
        });
        closeCountryActionModal();
    }, [closeCountryActionModal, countryActionSelection, openOperationalViewForMetricTile]);

    const handleOpenCountryDetails = useCallback(() => {
        if (!countryActionSelection?.country) {
            return;
        }

        openCountryDetailsView({
            country: countryActionSelection.country,
            companyCode: countryActionSelection.companyCode,
        });
        closeCountryActionModal();
    }, [closeCountryActionModal, countryActionSelection, openCountryDetailsView]);

    const openOperationalViewForRiskEntry = useCallback((options: {
        groupName?: string | null;
        process?: string | null;
        country?: string | null;
        companyCode?: string | null;
        statusFilters?: string[];
    }) => {
        persistScrollPosition();
        persistDateFilters();

        const statusFilters = options.statusFilters ?? ['FAIL', 'OVERDUE'];
        if (statusFilters.length > 0) {
            globalThis.sessionStorage.setItem('statusFilters', JSON.stringify(statusFilters));
        }

        if (options.groupName) {
            globalThis.sessionStorage.setItem('companyGroup', JSON.stringify([options.groupName]));
        }
        if (options.process) {
            globalThis.sessionStorage.setItem('process', JSON.stringify([options.process]));
        }
        if (options.country) {
            globalThis.sessionStorage.setItem('partnerCountry', options.country);
        }
        if (options.companyCode) {
            globalThis.sessionStorage.setItem('company', JSON.stringify([options.companyCode]));
        }

        navigate('../eim-document-overview-monitoring');
    }, [navigate, persistDateFilters, persistScrollPosition]);

    const handleMetricTileKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>, onActivate: () => void) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onActivate();
        }
    }, []);

    const loadPartnerCoverage = useCallback(async () => {
        const response = await getAllActivePartnerGraphData();
        const groupData = response?.data?.countAllActivePartnerData?.groupCountData || [];

        setActivePartnerTotal(response?.data?.countAllActivePartnerData?.total ?? 0);
        setActivePartnerGroupCount(groupData.length);
        setRawGroupCountData(groupData);
        setActivePartnerDataGraphOption(getActivePartnerGroupBarChartOptions(groupData, theme, intl));
    }, [getAllActivePartnerGraphData]);

    const loadMonthlyActivePartners = useCallback(async (request: EimDashboardRequest) => {
        const response = await getAllActivePartners({ variables: { request } });
        const raw = response?.data?.allActivePartnerData || [];
        setRawAllActivePartnerData(raw);
        setGraphData(getAllActiveInvoiceGraphOptions(raw, theme, intl));
    }, [getAllActivePartners]);

    const loadPartnerInvoiceSummary = useCallback(async (request: EimDashboardRequest) => {
        const response = await getActivePartnerInvoiceSummary({ variables: { request }, fetchPolicy: 'network-only' });
        const raw = response?.data?.allActivePartnerInvoiceSummary ?? [];
        console.info('[EIM Executive][PartnerLookup] Loaded partner invoice summary dataset', {
            rowCount: raw.length,
            sampleRowKeys: Object.keys(raw[0] ?? {}),
            request,
        });
        setRawPartnerInvoiceSummaryData(raw);
    }, [getActivePartnerInvoiceSummary]);

    useEffect(() => {
        if (!countryDetailsSelection) {
            return;
        }

        const toDate = dayjs();
        const fromDate = toDate.subtract(24, 'hour');

        const request: EimDashboardRequest = {
            fromDate,
            toDate,
        };

        loadPartnerInvoiceSummary(request).catch((error) => {
            console.error('[EIM Executive][PartnerLookup] Failed to load partner invoice summary dataset for popup', {
                error,
                request,
            });
        });
    }, [
        countryDetailsSelection,
        loadPartnerInvoiceSummary,
    ]);

    const loadDashboardCards = useCallback(async (request: EimDashboardRequest) => {
        const response = await getDashCardCount({
            variables: {
                request: {
                    fromDate: request?.fromDate ? request.fromDate : dayjs(),
                    toDate: request?.toDate ? request.toDate : dayjs(),
                },
            },
        });
        setDashboardCounts(response?.data?.getDashCardCount);
    }, [getDashCardCount]);

    const loadInboundOutboundTrendSeries = useCallback(async () => {
        const cacheKey = getInboundOutboundTrendCacheKey();
        const cached = globalThis.sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached) as InboundOutboundTrendSeries;
                setInboundOutboundTrendSeries((previousSeries) => ({
                    ...parsed,
                    hourly: parsed.hourly ?? previousSeries?.hourly,
                }));
                return;
            } catch {
                globalThis.sessionStorage.removeItem(cacheKey);
            }
        }

        const inFlightTrendSeriesRequest = inboundOutboundTrendSeriesRequestCache[cacheKey];
        if (inFlightTrendSeriesRequest !== undefined) {
            const cachedSeries = await inFlightTrendSeriesRequest;
            setInboundOutboundTrendSeries((previousSeries) => ({
                ...cachedSeries,
                hourly: cachedSeries.hourly ?? previousSeries?.hourly,
            }));
            return;
        }

        const now = dayjs();

        const fetchCountByRange = async (fromDate: Dayjs, toDate: Dayjs) => {
            const response = await getDashCardCount({
                variables: {
                    request: {
                        fromDate,
                        toDate,
                    },
                },
            });
            return response?.data?.getDashCardCount;
        };

        const dailyRanges = Array.from({ length: 31 }, (_, index) => {
            const date = now.subtract(30 - index, 'day');
            return {
                label: date.format('YYYY-MM-DD'),
                fromDate: date.startOf('day'),
                toDate: date.endOf('day'),
            };
        });

        const monthlyRanges = Array.from({ length: 12 }, (_, index) => {
            const month = now.subtract(11 - index, 'month');
            return {
                label: month.format('YYYY-MM'),
                fromDate: month.startOf('month'),
                toDate: month.endOf('month'),
            };
        });

        inboundOutboundTrendSeriesRequestCache[cacheKey] = Promise.all([
            Promise.all(
                dailyRanges.map(async (range) => {
                    const counts = await fetchCountByRange(range.fromDate, range.toDate);
                    return {
                        label: range.label,
                        inbound: Number(counts?.inbound ?? 0),
                        outbound: Number(counts?.outbound ?? 0),
                    };
                })
            ),
            Promise.all(
                monthlyRanges.map(async (range) => {
                    const counts = await fetchCountByRange(range.fromDate, range.toDate);
                    return {
                        label: range.label,
                        inbound: Number(counts?.inbound ?? 0),
                        outbound: Number(counts?.outbound ?? 0),
                    };
                })
            ),
        ]).then(([daily, monthly]) => ({ daily, monthly }));

        try {
            const series = await inboundOutboundTrendSeriesRequestCache[cacheKey];
            globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(series));
            setInboundOutboundTrendSeries((previousSeries) => ({
                ...series,
                hourly: series.hourly ?? previousSeries?.hourly,
            }));
        } finally {
            delete inboundOutboundTrendSeriesRequestCache[cacheKey];
        }
    }, [getDashCardCount, getInboundOutboundTrendCacheKey]);

    const loadTodayHourlyTrendSeries = useCallback(async () => {
        const cacheKey = getTodayHourlyTrendCacheKey();
        const cached = globalThis.sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached) as TodayHourlyTrendSeries;
                if (!isValidTodayHourlyTrendSeries(parsed)) {
                    globalThis.sessionStorage.removeItem(cacheKey);
                } else {
                setInboundOutboundTrendSeries((previousSeries) => {
                    if (previousSeries) {
                        return { ...previousSeries, hourly: parsed.volumeHourly };
                    }
                    return { daily: [], monthly: [], hourly: parsed.volumeHourly };
                });
                setTodayTransitHourlyTrendData(parsed.transitHourly);
                return;
                }
            } catch {
                globalThis.sessionStorage.removeItem(cacheKey);
            }
        }

        const inFlightHourlyTrendRequest = todayHourlyTrendSeriesRequestCache[cacheKey];
        if (inFlightHourlyTrendRequest !== undefined) {
            const cachedSeries = await inFlightHourlyTrendRequest;
            setInboundOutboundTrendSeries((previousSeries) => {
                if (previousSeries) {
                    return { ...previousSeries, hourly: cachedSeries.volumeHourly };
                }
                return { daily: [], monthly: [], hourly: cachedSeries.volumeHourly };
            });
            setTodayTransitHourlyTrendData(cachedSeries.transitHourly);
            return;
        }

        const now = dayjs();
        const startOfDay = now.startOf('day');
        const hours = Array.from({ length: now.hour() + 1 }, (_, index) => index);

        todayHourlyTrendSeriesRequestCache[cacheKey] = Promise.all(
            hours.map(async (hourOffset) => {
                const hourStart = startOfDay.add(hourOffset, 'hour');
                const hourEnd = hourStart.isSame(now, 'hour') ? now : hourStart.endOf('hour');

                const [countResponse, transitResponse] = await Promise.all([
                    getDashCardCount({
                        variables: {
                            request: {
                                fromDate: hourStart,
                                toDate: hourEnd,
                            },
                        },
                    }),
                    getEimExecutiveDashboard({
                        variables: {
                            request: {
                                periodStart: hourStart.toDate().toISOString(),
                                periodEnd: hourEnd.toDate().toISOString(),
                            },
                        },
                        errorPolicy: 'all',
                        fetchPolicy: 'network-only',
                    }),
                ]);

                const counts = countResponse?.data?.getDashCardCount;
                const flowHealth = (transitResponse?.data?.eimExecutiveDashboard as any)?.flowHealth;
                const avgTransitDays = Number(flowHealth?.avgTransitDays ?? 0);

                return {
                    label: hourStart.format('HH:00'),
                    inbound: Number(counts?.inbound ?? 0),
                    outbound: Number(counts?.outbound ?? 0),
                    avgTransitDays: Number.isFinite(avgTransitDays) ? avgTransitDays : 0,
                };
            })
        ).then((hourlyPoints) => ({
            volumeHourly: hourlyPoints.map((point) => ({
                label: point.label,
                inbound: point.inbound,
                outbound: point.outbound,
            })),
            transitHourly: hourlyPoints
                .filter((point) => (point.inbound + point.outbound) > 0)
                .map((point) => ({
                    month: point.label,
                    avgTransitDays: point.avgTransitDays,
                })),
        }));

        try {
            const series = await todayHourlyTrendSeriesRequestCache[cacheKey];
            globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(series));
            setInboundOutboundTrendSeries((previousSeries) => {
                if (previousSeries) {
                    return { ...previousSeries, hourly: series.volumeHourly };
                }
                return { daily: [], monthly: [], hourly: series.volumeHourly };
            });
            setTodayTransitHourlyTrendData(series.transitHourly);
        } finally {
            delete todayHourlyTrendSeriesRequestCache[cacheKey];
        }
    }, [getDashCardCount, getEimExecutiveDashboard, getTodayHourlyTrendCacheKey]);

    const toExecutiveDashboardRequest = useCallback((request: EimDashboardRequest): ExecutiveDashboardRequest => ({
        periodStart: request?.fromDate ? toRequestDate(request.fromDate) : toRequestDate(dayjs()),
        periodEnd: request?.toDate ? toRequestDate(request.toDate) : toRequestDate(dayjs()),
        processType: Array.isArray(request?.process) && request.process.length > 0 ? request.process[0] : undefined,
        partnerGroup: typeof request?.companyGroup === 'string' ? request.companyGroup : undefined,
    }), []);

    const loadExecutiveDashboardV2 = useCallback(async (request: EimDashboardRequest) => {
        const v2Request = toExecutiveDashboardRequest(request);

        try {
            const response = await getEimExecutiveDashboard({
                variables: { request: v2Request },
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
            });

            if (response.error) {
                console.error('Executive dashboard GraphQL error:', response.error);
            }

            const data = response?.data?.eimExecutiveDashboard as any;
            setExecutiveMetricsV2(data ?? EMPTY_EXECUTIVE_METRICS_V2);

            // Phase 2: Log the new metrics to console for inspection
            console.log('Phase 2 - New Executive Dashboard Query Response:', data);
            if (data?.volume) {
                console.log('  Volume Metrics:', {
                    total: data.volume.totalProcessed,
                    failed: data.volume.failedCount,
                    failureRate: `${data.volume.failureRate}%`,
                    volumeDelta: `${data.volume.volumeDelta}%`,
                    trends: `${data.volume.volumeTrendByMonth?.length} months of data`,
                });
            }
            if (data?.flowHealth) {
                console.log('  Flow Health Metrics:', {
                    stpRate: `${data.flowHealth.stpRate}%`,
                    onTimeRate: `${data.flowHealth.onTimeRate}%`,
                    avgTransitDays: data.flowHealth.avgTransitDays,
                    stpDelta: `${data.flowHealth.stpDelta}%`,
                });
            }
            if (data?.risk) {
                console.log('  Risk Metrics:', {
                    agingBuckets: `${data.risk.agingBuckets?.length} buckets`,
                    overdueCount: data.risk.overdueTrendByMonth?.reduce((sum, t) => sum + (t.overdueCount || 0), 0),
                });
            }
            if (data?.coverage) {
                console.log('  Coverage Metrics:', {
                    activePartners: data.coverage.activePartners,
                    coverageRate: `${data.coverage.coverageRate}%`,
                });
            }

            if (data?.coverage) {
                // Keep coverage headline aligned with the executive query.
                setActivePartnerTotal(data.coverage.activePartners ?? 0);
            }
        } catch (error) {
            console.error('Phase 2 - Executive dashboard query failed. Rendering fallback metrics.', error);
            setExecutiveMetricsV2(EMPTY_EXECUTIVE_METRICS_V2);
        }
    }, [getEimExecutiveDashboard, toExecutiveDashboardRequest]);

    const loadVolumeTrendBaseline = useCallback(async (request: EimDashboardRequest) => {
        const now = dayjs();
        const baseRequest = toExecutiveDashboardRequest(request);

        try {
            const response = await getEimExecutiveDashboard({
                variables: {
                    request: {
                        ...baseRequest,
                        periodStart: toRequestDate(now.subtract(1, 'year').startOf('day')),
                        periodEnd: toRequestDate(now.endOf('day')),
                    },
                },
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
            });

            const data = response?.data?.eimExecutiveDashboard as any;
            setVolumeTrendMetricsV2(data?.volume ?? undefined);
        } catch (error) {
            console.error('Volume trend baseline request failed. Falling back to selected-period volume data.', error);
            setVolumeTrendMetricsV2(undefined);
        }
    }, [getEimExecutiveDashboard, toExecutiveDashboardRequest]);

    const loadCountryMetrics = useCallback(async (request: EimDashboardRequest) => {
        const response = await getCountryMetrics({ variables: { request: toExecutiveDashboardRequest(request) } });
        setCountryMetrics(response?.data?.eimExecutiveDashboardCountryMetrics ?? []);
    }, [getCountryMetrics, toExecutiveDashboardRequest]);

    const loadSystemMetrics = useCallback(async (request: EimDashboardRequest) => {
        const response = await getSystemMetrics({ variables: { request: toExecutiveDashboardRequest(request) } });
        setSystemMetrics(response?.data?.eimExecutiveDashboardSystemMetrics ?? []);
    }, [getSystemMetrics, toExecutiveDashboardRequest]);

    const loadChannelMetrics = useCallback(async (request: EimDashboardRequest) => {
        const response = await getChannelMetrics({ variables: { request: toExecutiveDashboardRequest(request) } });
        setChannelMetrics(response?.data?.eimExecutiveDashboardChannelMetrics ?? []);
    }, [getChannelMetrics, toExecutiveDashboardRequest]);

    const loadGroupMetrics = useCallback(async (request: EimDashboardRequest) => {
        const response = await getGroupMetrics({ variables: { request: toExecutiveDashboardRequest(request) } });
        setGroupMetrics(response?.data?.eimExecutiveDashboardGroupMetrics ?? []);
    }, [getGroupMetrics, toExecutiveDashboardRequest]);

    const loadTopFailureReasons = useCallback(async (request: EimDashboardRequest) => {
        const response = await getTopFailureReasons({ variables: { request: toExecutiveDashboardRequest(request) } });
        setTopFailureReasons(response?.data?.eimExecutiveDashboardTopFailureReasons ?? []);
    }, [getTopFailureReasons, toExecutiveDashboardRequest]);

    const loadTopFailureProcesses = useCallback(async (request: EimDashboardRequest) => {
        const response = await getTopFailureProcesses({ variables: { request: toExecutiveDashboardRequest(request) } });
        setTopFailureProcesses(response?.data?.eimExecutiveDashboardTopFailureProcesses ?? []);
    }, [getTopFailureProcesses, toExecutiveDashboardRequest]);

    const loadTopFailureCompanyCodes = useCallback(async (request: EimDashboardRequest) => {
        const response = await getTopFailureCompanyCodes({ variables: { request: toExecutiveDashboardRequest(request) } });
        setTopFailureCompanyCodes(response?.data?.eimExecutiveDashboardTopFailureCompanyCodes ?? []);
    }, [getTopFailureCompanyCodes, toExecutiveDashboardRequest]);

    const loadDashboard = useCallback(async (request: EimDashboardRequest = {}) => {
        const viewCacheKey = getExecutiveDashboardViewCacheKey(request);
        let hasWarmSnapshot = false;

        const cachedView = globalThis.sessionStorage.getItem(viewCacheKey);
        if (cachedView) {
            try {
                const parsedEntry = JSON.parse(cachedView) as ExecutiveDashboardViewCacheEntry;
                const isFreshEntry = Number.isFinite(parsedEntry?.updatedAt)
                    && (Date.now() - parsedEntry.updatedAt) <= EXECUTIVE_DASHBOARD_VIEW_CACHE_TTL_MS
                    && parsedEntry?.snapshot !== undefined;

                if (isFreshEntry) {
                    hydrateDashboardFromCache(parsedEntry.snapshot);
                    hasWarmSnapshot = true;
                } else {
                    globalThis.sessionStorage.removeItem(viewCacheKey);
                }
            } catch {
                globalThis.sessionStorage.removeItem(viewCacheKey);
            }
        }

        if (!hasWarmSnapshot) {
            setLoading(true);
        }

        try {
            if (ENABLE_EXECUTIVE_DASHBOARD_V2) {
                // Phase 2: Use the new consolidated executive dashboard query for metrics
                // Keep dashboard card count query for accurate inbound/outbound/pass/fail/transit totals.
                const results = await Promise.allSettled([
                    loadExecutiveDashboardV2(request),
                    loadInboundOutboundTrendSeries(),
                    loadCountryMetrics(request),
                    loadSystemMetrics(request),
                    loadChannelMetrics(request),
                    loadGroupMetrics(request),
                    loadTopFailureReasons(request),
                    loadTopFailureProcesses(request),
                    loadTopFailureCompanyCodes(request),
                    loadDashboardCards(request),
                    loadMonthlyActivePartners(request),
                    loadPartnerCoverage(),
                ]);

                const rejected = results.filter((result) => result.status === 'rejected') as Array<{ status: 'rejected'; reason: unknown }>;
                if (rejected.length > 0) {
                    console.error('One or more dashboard data requests failed:', rejected.map((item) => item.reason));
                }

                // Volume trend baseline uses a 12-month window and can be significantly slower.
                // Keep it non-blocking so the main dashboard loader can stop as soon as primary cards are ready.
                void loadVolumeTrendBaseline(request);
            } else {
                // Phase 1: Use the old separate queries (fallback)
                setExecutiveMetricsV2(undefined);
                setCountryMetrics([]);
                setSystemMetrics([]);
                setChannelMetrics([]);
                setGroupMetrics([]);
                setTopFailureReasons([]);
                setTopFailureProcesses([]);
                setTopFailureCompanyCodes([]);
                setVolumeTrendMetricsV2(undefined);
                setInboundOutboundTrendSeries(undefined);
                const results = await Promise.allSettled([
                    loadDashboardCards(request),
                    loadMonthlyActivePartners(request),
                    loadPartnerCoverage(),
                ]);

                const rejected = results.filter((result) => result.status === 'rejected') as Array<{ status: 'rejected'; reason: unknown }>;
                if (rejected.length > 0) {
                    console.error('One or more fallback dashboard requests failed:', rejected.map((item) => item.reason));
                }
            }
        } finally {
            setLoading(false);
        }
    }, [getExecutiveDashboardViewCacheKey, hydrateDashboardFromCache, loadChannelMetrics, loadCountryMetrics, loadDashboardCards, loadGroupMetrics, loadTopFailureReasons, loadTopFailureProcesses, loadTopFailureCompanyCodes, loadMonthlyActivePartners, loadPartnerCoverage, loadExecutiveDashboardV2, loadSystemMetrics, loadVolumeTrendBaseline, loadInboundOutboundTrendSeries]);

    const handlePeriodOptionChange = useCallback((option: PeriodOption) => {
        setSelectedPeriodOption(option);

        if (option === 'custom') {
            return;
        }

        const nextRange = getPeriodRangeForOption(option);
        setSelectedRangeValue(nextRange);

        const { fromDate, toDate } = getRequestRange(nextRange);
        void loadDashboard({ fromDate, toDate });
    }, [loadDashboard]);

    const handlePeriodRangeChange = useCallback((dates: [Dayjs | null, Dayjs | null] | null) => {
        if (!dates?.[0] || !dates?.[1]) {
            return;
        }

        const nextRange: PeriodRangeValue = [dates[0].startOf('day'), dates[1].endOf('day')];
        setSelectedRangeValue(nextRange);

        const { fromDate, toDate } = getRequestRange(nextRange);
        void loadDashboard({ fromDate, toDate });
    }, [loadDashboard]);

    const disableFutureDates = useCallback((current: Dayjs) => {
        if (!current) {
            return false;
        }
        return current.endOf('day').isAfter(dayjs().endOf('day'));
    }, []);

    useEffect(() => {
        void loadDashboard({ fromDate: currentRequestRange.fromDate, toDate: currentRequestRange.toDate });
    }, [currentRequestRange.fromDate, currentRequestRange.toDate, loadDashboard]);

    useEffect(() => {
        if (transitTrendTimeRange !== 'today') {
            return;
        }

        void loadTodayHourlyTrendSeries();
    }, [loadTodayHourlyTrendSeries, transitTrendTimeRange]);

    useEffect(() => {
        globalThis.sessionStorage.setItem(EXECUTIVE_PERIOD_OPTION_SESSION_KEY, selectedPeriodOption);

        if (selectedPeriodOption === 'custom') {
            globalThis.sessionStorage.setItem(
                EXECUTIVE_PERIOD_RANGE_SESSION_KEY,
                JSON.stringify({
                    from: selectedRangeValue[0].toISOString(),
                    to: selectedRangeValue[1].toISOString(),
                })
            );
            return;
        }

        globalThis.sessionStorage.removeItem(EXECUTIVE_PERIOD_RANGE_SESSION_KEY);
    }, [selectedPeriodOption, selectedRangeValue]);

    useEffect(() => {
        globalThis.sessionStorage.setItem(EXECUTIVE_TRANSIT_TREND_TIME_RANGE_SESSION_KEY, transitTrendTimeRange);
    }, [transitTrendTimeRange]);

    useEffect(() => {
        globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
        if (rawGroupCountData) {
            setActivePartnerDataGraphOption(getActivePartnerGroupBarChartOptions(rawGroupCountData, theme, intl));
        }
        if (rawAllActivePartnerData) {
            setGraphData(getAllActiveInvoiceGraphOptions(rawAllActivePartnerData, theme, intl));
        }
    }, [theme]);

    const formatCount = useCallback((value?: number | null) => intl.formatNumber(value ?? 0), [intl]);

    useEffect(() => {
        if (loading) {
            return;
        }

        const hasSnapshotData = Boolean(executiveMetricsV2 || dashboardCounts || countryMetrics.length || systemMetrics.length || channelMetrics.length);
        if (!hasSnapshotData) {
            return;
        }

        const snapshot: ExecutiveDashboardViewCache = {
            dashboardCounts,
            executiveMetricsV2,
            volumeTrendMetricsV2,
            inboundOutboundTrendSeries,
            todayTransitHourlyTrendData,
            countryMetrics,
            systemMetrics,
            channelMetrics,
            groupMetrics,
            topFailureReasons,
            topFailureProcesses,
            topFailureCompanyCodes,
            rawGroupCountData,
            rawAllActivePartnerData,
            activePartnerTotal,
            activePartnerGroupCount,
        };

        const cacheEntry: ExecutiveDashboardViewCacheEntry = {
            updatedAt: Date.now(),
            snapshot,
        };

        persistExecutiveViewCacheSafely(getExecutiveDashboardViewCacheKey(), cacheEntry);
    }, [
        loading,
        dashboardCounts,
        executiveMetricsV2,
        volumeTrendMetricsV2,
        inboundOutboundTrendSeries,
        todayTransitHourlyTrendData,
        countryMetrics,
        systemMetrics,
        channelMetrics,
        groupMetrics,
        topFailureReasons,
        topFailureProcesses,
        topFailureCompanyCodes,
        rawGroupCountData,
        rawAllActivePartnerData,
        activePartnerTotal,
        activePartnerGroupCount,
        getExecutiveDashboardViewCacheKey,
    ]);

    const formatPercent = useCallback(
        (value: number) => `${intl.formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
        [intl]
    );

    const localizedPeriodOptions = useMemo(
        () => PERIOD_OPTIONS.map((value) => ({
            value,
            label: getI18n(PERIOD_OPTION_LABELS[value].key, PERIOD_OPTION_LABELS[value].defaultLabel, intl),
        })),
        [intl]
    );

    const selectedRange = useMemo(() => {
        const dateFormat = getDateFormatOrDefault(props.currentUser);
        const modeLabel = localizedPeriodOptions.find((option) => option.value === selectedPeriodOption)?.label
            ?? getI18n('eim-period-custom', 'Custom date', intl);
        return `${modeLabel}: ${currentRequestRange.fromDate.format(dateFormat)} – ${currentRequestRange.toDate.format(dateFormat)}`;
    }, [currentRequestRange, intl, localizedPeriodOptions, props.currentUser, selectedPeriodOption]);

    const executiveTileCounts = useMemo<EimDashboardCardsResponse>(() => {
        if (!ENABLE_EXECUTIVE_DASHBOARD_V2 || !executiveMetricsV2) {
            return dashboardCounts;
        }

        const total = executiveMetricsV2?.volume?.totalProcessed ?? dashboardCounts?.total ?? 0;
        const failed = executiveMetricsV2?.risk?.failedCount ?? executiveMetricsV2?.volume?.failedCount ?? dashboardCounts?.fail ?? 0;
        const overdue = executiveMetricsV2?.risk?.overdueCount ?? dashboardCounts?.overdue ?? 0;
        const stpRate = executiveMetricsV2?.flowHealth?.stpRate;
        const passed = typeof stpRate === 'number'
            ? Math.max(0, Math.round((stpRate / 100) * total))
            : dashboardCounts?.pass ?? Math.max(0, total - failed - overdue);

        return {
            total,
            pass: passed,
            fail: failed,
            overdue,
            inbound: executiveMetricsV2?.volume?.inboundCount ?? dashboardCounts?.inbound ?? 0,
            outbound: executiveMetricsV2?.volume?.outboundCount ?? dashboardCounts?.outbound ?? 0,
            transit: executiveMetricsV2?.volume?.inTransitCount ?? dashboardCounts?.transit ?? 0,
            transb: executiveMetricsV2?.volume?.busnTransitCount ?? dashboardCounts?.transb ?? 0,
        } as EimDashboardCardsResponse;
    }, [dashboardCounts, executiveMetricsV2]);

    const totalInvoices = executiveTileCounts?.total ?? 0;
    const passRate = typeof executiveMetricsV2?.flowHealth?.stpRate === 'number'
        ? executiveMetricsV2.flowHealth.stpRate
        : getRatio(executiveTileCounts?.pass, totalInvoices);
    const inboundShare = getRatio(executiveTileCounts?.inbound, totalInvoices);
    const outboundShare = getRatio(executiveTileCounts?.outbound, totalInvoices);
    const riskRate = getRatio((executiveTileCounts?.fail ?? 0) + (executiveTileCounts?.overdue ?? 0), totalInvoices);

    const executiveSections = useMemo(
        () =>
            getExecutiveSections({
                dashboardCounts: executiveTileCounts,
                activePartnerTotal,
                activePartnerGroupCount,
                formatCount,
                formatPercent,
                inboundShare,
                outboundShare,
                passRate,
                riskRate,
                intl,
                executiveMetrics: ENABLE_EXECUTIVE_DASHBOARD_V2 ? executiveMetricsV2 : undefined,
                thresholds: thresholdConfig,
            }),
        [
            activePartnerGroupCount,
            activePartnerTotal,
            executiveTileCounts,
            formatCount,
            formatPercent,
            inboundShare,
            outboundShare,
            passRate,
            riskRate,
            intl,
            thresholdConfig,
        ]
    );

    const activePartnersTitle = useMemo(() => `${getI18n('eim-active-partners-title', 'Active BMW partners by BMW group as at', intl)} ${dayjs().format(getDateFormatOrDefault(props.currentUser))}`, [intl, props.currentUser]);

    const v2TopImpactedGroups = useMemo(() => {
        const groups = executiveMetricsV2?.coverage?.topImpactedGroups ?? [];
        return [...groups].sort((left, right) => {
            const leftOverdueRate = getGroupOverdueRate(left);
            const rightOverdueRate = getGroupOverdueRate(right);
            const primaryDelta = topGroupRankMode === 'failureRate'
                ? (right.failureRate ?? 0) - (left.failureRate ?? 0)
                : rightOverdueRate - leftOverdueRate;

            if (primaryDelta !== 0) {
                return primaryDelta;
            }

            const secondaryDelta = topGroupRankMode === 'failureRate'
                ? rightOverdueRate - leftOverdueRate
                : (right.failureRate ?? 0) - (left.failureRate ?? 0);
            if (secondaryDelta !== 0) {
                return secondaryDelta;
            }

            return (right.volume ?? 0) - (left.volume ?? 0);
        });
    }, [executiveMetricsV2, topGroupRankMode]);

    const rankedTopFailureReasons = useMemo(() => {
        return [...topFailureReasons].sort((left, right) => {
            const leftOverdueRate = getGroupOverdueRate(left);
            const rightOverdueRate = getGroupOverdueRate(right);
            const leftFailureRate = left.failureRate ?? 0;
            const rightFailureRate = right.failureRate ?? 0;

            const primaryDelta = topFailureReasonRankMode === 'failureRate'
                ? rightFailureRate - leftFailureRate
                : rightOverdueRate - leftOverdueRate;
            if (primaryDelta !== 0) {
                return primaryDelta;
            }

            const secondaryDelta = topFailureReasonRankMode === 'failureRate'
                ? rightOverdueRate - leftOverdueRate
                : rightFailureRate - leftFailureRate;
            if (secondaryDelta !== 0) {
                return secondaryDelta;
            }

            return (right.volume ?? 0) - (left.volume ?? 0);
        });
    }, [topFailureReasons, topFailureReasonRankMode]);

    const rankedTopFailureProcesses = useMemo(() => {
        return [...topFailureProcesses].sort((left, right) => {
            const leftOverdueRate = getGroupOverdueRate({ overdueCount: left.overdueCount, volume: left.totalCount });
            const rightOverdueRate = getGroupOverdueRate({ overdueCount: right.overdueCount, volume: right.totalCount });
            const leftFailureRate = left.failureRate ?? 0;
            const rightFailureRate = right.failureRate ?? 0;

            const primaryDelta = topFailureProcessRankMode === 'failureRate'
                ? rightFailureRate - leftFailureRate
                : rightOverdueRate - leftOverdueRate;
            if (primaryDelta !== 0) {
                return primaryDelta;
            }

            const secondaryDelta = topFailureProcessRankMode === 'failureRate'
                ? rightOverdueRate - leftOverdueRate
                : rightFailureRate - leftFailureRate;
            if (secondaryDelta !== 0) {
                return secondaryDelta;
            }

            return (right.totalCount ?? 0) - (left.totalCount ?? 0);
        });
    }, [topFailureProcesses, topFailureProcessRankMode]);

    const rankedTopFailureCompanyCodes = useMemo(() => {
        return [...topFailureCompanyCodes].sort((left, right) => {
            const leftOverdueRate = getGroupOverdueRate(left);
            const rightOverdueRate = getGroupOverdueRate(right);
            const leftFailureRate = left.failureRate ?? 0;
            const rightFailureRate = right.failureRate ?? 0;

            const primaryDelta = topFailureCompanyCodeRankMode === 'failureRate'
                ? rightFailureRate - leftFailureRate
                : rightOverdueRate - leftOverdueRate;
            if (primaryDelta !== 0) {
                return primaryDelta;
            }

            const secondaryDelta = topFailureCompanyCodeRankMode === 'failureRate'
                ? rightOverdueRate - leftOverdueRate
                : rightFailureRate - leftFailureRate;
            if (secondaryDelta !== 0) {
                return secondaryDelta;
            }

            return (right.volume ?? 0) - (left.volume ?? 0);
        });
    }, [topFailureCompanyCodes, topFailureCompanyCodeRankMode]);

    const v2TileHints = useMemo(() => ({
        stpByChannel: getI18n('eim-v2-stp-by-channel-hint', 'Open a channel-level breakdown of STP and on-time rates grouped into the three channels.', intl),
        onTime: getI18n('eim-v2-on-time-rate-hint', 'On-time rate = non-overdue invoices / total processed invoices × 100.', intl),
        failure: getI18n('eim-v2-failure-rate-hint', 'Failure rate = FAIL invoices / total processed invoices × 100.', intl),
        overdue: formatOverdueThresholdText(
            getI18n('eim-v2-overdue-hint', 'Overdue count includes NONE status records older than 24 hours.', intl),
            thresholdConfig.overdueHoursThreshold
        ),
        overdueAge: (
            <div>
                <div>{getI18n('eim-v2-overdue-age-hint-title', 'Estimated from overdue aging buckets:', intl)}</div>
                <div>{getI18n('eim-v2-overdue-age-hint-0-7', '0-7d bucket uses midpoint 3.5d', intl)}</div>
                <div>{getI18n('eim-v2-overdue-age-hint-8-30', '8-30d bucket uses midpoint 19d', intl)}</div>
                <div>{getI18n('eim-v2-overdue-age-hint-30p', '30+d bucket uses representative value 45d', intl)}</div>
                <div>{getI18n('eim-v2-overdue-age-hint-formula', 'Weighted average = sum(bucketDays × bucketCount) / total overdue count.', intl)}</div>
            </div>
        ),
        coverage: getI18n('eim-v2-coverage-rate-hint', 'Coverage rate = active partners / total partners × 100.', intl),
        health: getI18n('eim-v2-health-label-hint', 'Health label comes from the composite health score classification in metadata.', intl),
    }), [intl, thresholdConfig.overdueHoursThreshold]);

    const v2SignalTiles = useMemo<V2SignalTile[]>(
        () => {
            const stpRate = executiveMetricsV2?.flowHealth?.stpRate ?? 0;
            const onTimeRate = executiveMetricsV2?.flowHealth?.onTimeRate ?? 0;
            const avgTransitDays = executiveMetricsV2?.flowHealth?.avgTransitDays ?? 0;
            const previousAvgTransitDays = executiveMetricsV2?.flowHealth?.previousAvgTransitDays ?? 0;
            const failureRate = executiveMetricsV2?.risk?.failureRate ?? 0;
            const overdueCount = executiveMetricsV2?.risk?.overdueCount ?? 0;
            const overdueAvgDays = getEstimatedOverdueAgeDays(executiveMetricsV2?.risk?.agingBuckets);
            const overdueThreshold = thresholdConfig.overdueCriticalThreshold;
            const coverageRate = executiveMetricsV2?.coverage?.coverageRate ?? 0;
            const healthLabel = executiveMetricsV2?.metadata?.healthScoreLabel ?? '-';

            const stpTrendLevels = toTrendLevels((executiveMetricsV2?.flowHealth?.stpRateTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.stpRate ?? 0)));
            const onTimeTrendLevels = toTrendLevels((executiveMetricsV2?.flowHealth?.stpRateTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.onTimeRate ?? 0)));
            const failureTrendLevels = toTrendLevels((executiveMetricsV2?.risk?.overdueTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.overdueCount ?? 0)));
            const overdueTrendLevels = toTrendLevels((executiveMetricsV2?.risk?.overdueTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.overdueCount ?? 0)));
            const overdueAgeTrendLevels = toTrendLevels((executiveMetricsV2?.flowHealth?.stpRateTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.avgTransitDays ?? 0)));
            const coverageTrendLevels = toTrendLevels((executiveMetricsV2?.coverage?.coverageTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.coverageRate ?? 0)));
            const healthTrendLevels = toTrendLevels((executiveMetricsV2?.volume?.volumeTrendByMonth ?? []).map((trendPoint) => Number(trendPoint?.total ?? 0)));

            const buildDelta = (deltaValue: number | null | undefined, lowerIsBetter = false) => {
                if (typeof deltaValue !== 'number' || Number.isNaN(deltaValue)) {
                    return {
                        text: undefined,
                        direction: 'neutral' as const,
                    };
                }

                let direction: 'up' | 'down' | 'neutral' = 'neutral';
                if (deltaValue > 0) {
                    direction = lowerIsBetter ? 'down' : 'up';
                } else if (deltaValue < 0) {
                    direction = lowerIsBetter ? 'up' : 'down';
                }

                const signed = intl.formatNumber(deltaValue, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                    signDisplay: 'always' as any,
                });

                return {
                    text: `${signed}% ${getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}`,
                    direction,
                };
            };

            const stpDelta = buildDelta(executiveMetricsV2?.flowHealth?.stpDelta);
            const onTimeDelta = buildDelta(executiveMetricsV2?.flowHealth?.onTimeDelta);
            const failureDelta = buildDelta(executiveMetricsV2?.risk?.failureRateDelta, true);
            const overdueDelta = buildDelta(executiveMetricsV2?.risk?.overdueDelta, true);
            const coverageDelta = buildDelta(executiveMetricsV2?.coverage?.coverageDelta);

            const transitDeltaValue = avgTransitDays - previousAvgTransitDays;
            let transitDeltaDirection: 'up' | 'down' | 'neutral' = 'neutral';
            if (transitDeltaValue > 0) {
                transitDeltaDirection = 'down';
            } else if (transitDeltaValue < 0) {
                transitDeltaDirection = 'up';
            }
            const transitDelta = typeof avgTransitDays === 'number' && typeof previousAvgTransitDays === 'number'
                ? {
                    text: `${intl.formatNumber(transitDeltaValue, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                        signDisplay: 'always' as any,
                    })}d ${getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}`,
                    direction: transitDeltaDirection,
                }
                : { text: undefined, direction: 'neutral' as const };

            const overdueTone: MetricTone = (() => {
                if (overdueCount >= overdueThreshold && overdueThreshold > 0) {
                    return 'critical';
                }
                if (overdueCount > 0) {
                    return 'warning';
                }
                return 'positive';
            })();

            const healthTone: MetricTone = (() => {
                if (healthLabel === 'Good') {
                    return 'positive';
                }
                if (healthLabel === 'Fair') {
                    return 'warning';
                }
                if (healthLabel === 'At Risk') {
                    return 'critical';
                }
                return 'neutral';
            })();

            let overdueBadgeTone: V2BadgeTone = 'warning';
            if (overdueTone === 'critical') {
                overdueBadgeTone = 'critical';
            }

            let latencyBadgeTone: V2BadgeTone = 'neutral';
            if (overdueCount > 0) {
                latencyBadgeTone = 'warning';
            }

            let healthBadgeTone: V2BadgeTone = 'success';
            if (healthTone === 'critical') {
                healthBadgeTone = 'critical';
            } else if (healthTone === 'warning') {
                healthBadgeTone = 'warning';
            }

            const tiles: V2SignalTile[] = [
                {
                    label: getI18n('eim-v2-stp-rate', 'STP rate', intl),
                    value: formatPercent(stpRate),
                    tone: getCountryProcessingTone(thresholdConfig, stpRate),
                    trendLevels: stpTrendLevels,
                    sparklineMode: 'line' as const,
                    badgeLabel: getI18n('eim-v2-badge-quality', 'Quality', intl),
                    badgeTone: 'success' as const,
                    deltaText: stpDelta.text,
                    deltaDirection: stpDelta.direction,
                },
                {
                    label: getI18n('eim-v2-on-time-rate', 'On-time rate', intl),
                    value: formatPercent(onTimeRate),
                    tone: getCountryProcessingTone(thresholdConfig, onTimeRate),
                    hint: v2TileHints.onTime,
                    trendLevels: onTimeTrendLevels,
                    sparklineMode: 'line' as const,
                    badgeLabel: getI18n('eim-v2-badge-sla', 'SLA', intl),
                    badgeTone: 'info' as const,
                    deltaText: onTimeDelta.text,
                    deltaDirection: onTimeDelta.direction,
                },
                {
                    label: getI18n('eim-v2-failure-rate', 'Failure rate', intl),
                    value: formatPercent(failureRate),
                    tone: getCountryExceptionsTone(thresholdConfig, failureRate),
                    hint: v2TileHints.failure,
                    trendLevels: failureTrendLevels,
                    sparklineMode: 'bars' as const,
                    badgeLabel: getI18n('eim-v2-badge-risk', 'Risk', intl),
                    badgeTone: 'critical' as const,
                    deltaText: failureDelta.text,
                    deltaDirection: failureDelta.direction,
                },
                {
                    label: formatOverdueThresholdText(
                        getI18n('eim-v2-overdue', 'Overdue count', intl),
                        thresholdConfig.overdueHoursThreshold
                    ),
                    value: formatCount(overdueCount),
                    tone: overdueTone,
                    hint: v2TileHints.overdue,
                    trendLevels: overdueTrendLevels,
                    sparklineMode: 'bars' as const,
                    badgeLabel: getI18n('eim-v2-badge-backlog', 'Backlog', intl),
                    badgeTone: overdueBadgeTone,
                    deltaText: overdueDelta.text,
                    deltaDirection: overdueDelta.direction,
                },
                {
                    label: getI18n('eim-v2-overdue-age', 'Overdue age', intl),
                    value: `${intl.formatNumber(overdueAvgDays, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}d`,
                    tone: (overdueCount > 0 ? overdueTone : 'neutral') as MetricTone,
                    hint: v2TileHints.overdueAge,
                    trendLevels: overdueAgeTrendLevels,
                    sparklineMode: 'line' as const,
                    badgeLabel: getI18n('eim-v2-badge-latency', 'Latency', intl),
                    badgeTone: latencyBadgeTone,
                    deltaText: transitDelta.text,
                    deltaDirection: transitDelta.direction,
                },
                {
                    label: getI18n('eim-v2-coverage-rate', 'Coverage rate', intl),
                    value: formatPercent(coverageRate),
                    tone: getCountryCoverageTone(thresholdConfig, coverageRate),
                    hint: v2TileHints.coverage,
                    trendLevels: coverageTrendLevels,
                    sparklineMode: 'line' as const,
                    badgeLabel: getI18n('eim-v2-badge-coverage', 'Coverage', intl),
                    badgeTone: 'info' as const,
                    deltaText: coverageDelta.text,
                    deltaDirection: coverageDelta.direction,
                },
                {
                    label: getI18n('eim-v2-health-label', 'Health label', intl),
                    value: healthLabel,
                    tone: healthTone,
                    hint: v2TileHints.health,
                    trendLevels: healthTrendLevels,
                    sparklineMode: 'bars' as const,
                    badgeLabel: getI18n('eim-v2-badge-health', 'Health', intl),
                    badgeTone: healthBadgeTone,
                    deltaText: undefined,
                    deltaDirection: 'neutral',
                },
            ];

            return tiles;
        },
        [executiveMetricsV2, formatCount, formatPercent, intl, thresholdConfig, v2TileHints]
    );

    const channelMetricRows = useMemo(
        () => [...channelMetrics].sort((left, right) => (right.intake?.totalProcessed ?? 0) - (left.intake?.totalProcessed ?? 0)),
        [channelMetrics]
    );

    const transitTrendRangeOptions = useMemo(() => ([
        {
            value: 'today' as TransitTrendTimeRange,
            label: getI18n('eim-transit-trend-today', 'Today', intl),
        },
        {
            value: 'currentMonth' as TransitTrendTimeRange,
            label: getI18n('eim-transit-trend-currentMonth', 'Current month', intl),
        },
        {
            value: '30d' as TransitTrendTimeRange,
            label: getI18n('eim-transit-trend-30d', 'Last 30 days', intl),
        },
        {
            value: '6m' as TransitTrendTimeRange,
            label: getI18n('eim-transit-trend-6m', 'Last 6 months', intl),
        },
        {
            value: '1y' as TransitTrendTimeRange,
            label: getI18n('eim-transit-trend-1y', 'Last year', intl),
        },
    ]), [intl]);

    const inboundVolumeTrendData = useMemo(
        () => getVolumeTrendByRange(volumeTrendMetricsV2 ?? executiveMetricsV2?.volume, inboundOutboundTrendSeries, transitTrendTimeRange, 'inbound'),
        [volumeTrendMetricsV2, executiveMetricsV2, inboundOutboundTrendSeries, transitTrendTimeRange]
    );

    const outboundVolumeTrendData = useMemo(
        () => getVolumeTrendByRange(volumeTrendMetricsV2 ?? executiveMetricsV2?.volume, inboundOutboundTrendSeries, transitTrendTimeRange, 'outbound'),
        [volumeTrendMetricsV2, executiveMetricsV2, inboundOutboundTrendSeries, transitTrendTimeRange]
    );

    const resolveTrendPointRange = useCallback((label: string): PeriodRangeValue | null => {
        const trimmedLabel = label.trim();
        if (!trimmedLabel) {
            return null;
        }

        const hourMatch = trimmedLabel.match(/^(\d{2}):(\d{2})$/);
        if (hourMatch) {
            const hour = Number(hourMatch[1]);
            const minute = Number(hourMatch[2]);
            if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
                const baseDate = currentRequestRange.toDate.startOf('day');
                const start = baseDate.hour(hour).minute(minute).second(0);
                return [start, start.endOf('hour')];
            }
        }

        if (/^\d{4}-\d{2}$/.test(trimmedLabel)) {
            const monthPoint = dayjs(`${trimmedLabel}-01`);
            if (monthPoint.isValid()) {
                return [monthPoint.startOf('month'), monthPoint.endOf('month')];
            }
        }

        const dayPoint = dayjs(trimmedLabel);
        if (dayPoint.isValid()) {
            return [dayPoint.startOf('day'), dayPoint.endOf('day')];
        }

        return null;
    }, [currentRequestRange.toDate]);

    const openOperationalViewForTrendPoint = useCallback((label: string, direction?: 'IN' | 'OUT') => {
        const selectedRange = resolveTrendPointRange(label);
        if (!selectedRange) {
            return;
        }

        persistScrollPosition();
        globalThis.sessionStorage.setItem(OPERATIONAL_DATA_ORIGIN_KEY, EXECUTIVE_DASHBOARD_V1_ROUTE);

        globalThis.sessionStorage.setItem('fromDate', selectedRange[0].format('YYYY-MM-DD HH:mm:ss'));
        globalThis.sessionStorage.setItem('toDate', selectedRange[1].format('YYYY-MM-DD HH:mm:ss'));
        globalThis.sessionStorage.removeItem('company');
        globalThis.sessionStorage.removeItem('process');
        globalThis.sessionStorage.removeItem('companyGroup');

        if (direction) {
            globalThis.sessionStorage.setItem('type', direction);
        } else {
            globalThis.sessionStorage.removeItem('type');
        }

        navigate('../eim-document-overview-monitoring');
    }, [navigate, resolveTrendPointRange, persistScrollPosition]);

    const handleTransitTrendPointClick = useCallback((params: any) => {
        if (params?.componentType !== 'series' || params?.seriesIndex !== 0) {
            return;
        }
        const label = String(params?.name ?? params?.axisValue ?? '');
        openOperationalViewForTrendPoint(label);
    }, [openOperationalViewForTrendPoint]);

    const handleInboundTrendPointClick = useCallback((params: any) => {
        if (params?.componentType !== 'series' || params?.seriesIndex !== 0) {
            return;
        }
        const label = String(params?.name ?? params?.axisValue ?? '');
        openOperationalViewForTrendPoint(label, 'IN');
    }, [openOperationalViewForTrendPoint]);

    const handleOutboundTrendPointClick = useCallback((params: any) => {
        if (params?.componentType !== 'series' || params?.seriesIndex !== 0) {
            return;
        }
        const label = String(params?.name ?? params?.axisValue ?? '');
        openOperationalViewForTrendPoint(label, 'OUT');
    }, [openOperationalViewForTrendPoint]);

    const transitTrendChartEvents = useMemo(() => ({ click: handleTransitTrendPointClick }), [handleTransitTrendPointClick]);
    const inboundTrendChartEvents = useMemo(() => ({ click: handleInboundTrendPointClick }), [handleInboundTrendPointClick]);
    const outboundTrendChartEvents = useMemo(() => ({ click: handleOutboundTrendPointClick }), [handleOutboundTrendPointClick]);

    const countryMetricRows = useMemo(() => countryMetrics.slice(0, 6), [countryMetrics]);

    const selectedCountryDetailsMetric = useMemo(() => {
        if (!countryDetailsSelection?.country) {
            return null;
        }

        if (countryDetailsSelection.companyCode) {
            const exactMatch = countryMetrics.find((countryMetric) => (
                (countryMetric.country ?? '').trim().toUpperCase() === countryDetailsSelection.country.trim().toUpperCase()
                && (countryMetric.companyCode ?? '').trim().toUpperCase() === countryDetailsSelection.companyCode?.trim().toUpperCase()
            ));

            if (exactMatch) {
                return exactMatch;
            }
        }

        return countryMetrics.find((countryMetric) => (
            (countryMetric.country ?? '').trim().toUpperCase() === countryDetailsSelection.country.trim().toUpperCase()
        )) ?? null;
    }, [countryDetailsSelection, countryMetrics]);

    const countryDetailsCompanyCodes = useMemo(() => {
        if (!countryDetailsSelection?.country) {
            return [] as string[];
        }

        const selectedCompanyCode = countryDetailsSelection.companyCode?.trim();
        if (selectedCompanyCode) {
            return [selectedCompanyCode];
        }

        const countryCode = countryDetailsSelection.country.trim().toUpperCase();
        return Array.from(new Set(countryMetrics
            .filter((countryMetric) => (countryMetric.country ?? '').trim().toUpperCase() === countryCode)
            .map((countryMetric) => (countryMetric.companyCode ?? '').trim())
            .filter((companyCode) => companyCode.length > 0)));
    }, [countryDetailsSelection, countryMetrics]);

    useEffect(() => {
        if (!countryDetailsSelection?.country) {
            setCountryDetailsHourlyTrend([]);
            return;
        }

        if (countryDetailsCompanyCodes.length === 0) {
            setCountryDetailsHourlyTrend([]);
            return;
        }

        const countryKey = countryDetailsSelection.country.trim().toUpperCase();
        const companyKey = countryDetailsCompanyCodes.join('|');
        const cacheKey = `${countryKey}:${companyKey}:${dayjs().format('YYYY-MM-DD-HH')}`;
        const cachedTrend = countryDetailsHourlyTrendCacheRef.current[cacheKey];
        if (cachedTrend) {
            setCountryDetailsHourlyTrend(cachedTrend);
            return;
        }

        let isCancelled = false;
        const now = dayjs();
        const hourlyRanges = new Array(24).fill(null).map((_, index) => {
            const hourStart = now.subtract(23 - index, 'hour').startOf('hour');
            return {
                label: hourStart.format('HH:00'),
                key: hourStart.format('YYYY-MM-DD HH:00'),
            };
        });

        const hourlyTemplateByLabel = new Map<string, CountryHourlyTrendPoint>(
            hourlyRanges.map((range) => ([
                range.label,
                {
                    label: range.label,
                    successful: 0,
                    failed: 0,
                },
            ]))
        );

        const request: EimDashboardRequest = {
            fromDate: now.subtract(24, 'hour'),
            toDate: now,
            company: countryDetailsCompanyCodes,
            partner_country: countryDetailsSelection.country,
        };

        getActivePartnerInvoiceHourlyTrend({ variables: { request }, fetchPolicy: 'network-only' }).then((response) => {
            if (isCancelled) {
                return;
            }

            const rows = response?.data?.allActivePartnerInvoiceHourlyTrend ?? [];
            rows.forEach((row) => {
                const hourLabel = normalizeHourlyBucketLabel(row?.hourLabel);
                if (!hourLabel || !hourlyTemplateByLabel.has(hourLabel)) {
                    return;
                }

                const current = hourlyTemplateByLabel.get(hourLabel) ?? {
                    label: hourLabel,
                    successful: 0,
                    failed: 0,
                };

                hourlyTemplateByLabel.set(hourLabel, {
                    label: hourLabel,
                    successful: current.successful + Math.max(0, Number(row?.successfulCount ?? 0)),
                    failed: current.failed + Math.max(0, Number(row?.failedCount ?? 0)),
                });
            });

            const series = hourlyRanges.map((range) => (
                hourlyTemplateByLabel.get(range.label) ?? {
                    label: range.label,
                    successful: 0,
                    failed: 0,
                }
            ));

            countryDetailsHourlyTrendCacheRef.current[cacheKey] = series;
            setCountryDetailsHourlyTrend(series);
        }).catch(() => {
            if (!isCancelled) {
                setCountryDetailsHourlyTrend([]);
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [countryDetailsCompanyCodes, countryDetailsSelection, getActivePartnerInvoiceHourlyTrend]);

    useEffect(() => {
        if (!countryDetailsSelection?.country) {
            setCountryDetailsTopErrorMessages([]);
            return;
        }

        if (countryDetailsCompanyCodes.length === 0) {
            setCountryDetailsTopErrorMessages([]);
            return;
        }

        let isCancelled = false;
        const now = dayjs();
        const request: EimDashboardRequest = {
            fromDate: now.subtract(24, 'hour'),
            toDate: now,
            company: countryDetailsCompanyCodes,
            partner_country: countryDetailsSelection.country,
        };

        getActivePartnerCountryDetail({ variables: { request }, fetchPolicy: 'network-only' })
            .then((response) => {
                if (isCancelled) {
                    return;
                }

                const rows = response?.data?.allActivePartnerCountryDetail?.topErrorMessages ?? [];
                setCountryDetailsTopErrorMessages(rows.slice(0, 4));
            })
            .catch(() => {
                if (!isCancelled) {
                    setCountryDetailsTopErrorMessages([]);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [countryDetailsCompanyCodes, countryDetailsSelection, getActivePartnerCountryDetail]);

    const countryDetailsSnapshot = useMemo(() => {
        const metric = selectedCountryDetailsMetric;
        if (!metric) {
            return {
                totalInvoices: 0,
                successfulCount: 0,
                inTransitCount: 0,
                failedCount: 0,
                successRate: 0,
                errorRate: 0,
                avgTransitDays: 0,
                stpRate: 0,
            };
        }

        const totalInvoices = metric.intake?.totalProcessed ?? 0;
        const failedCount = (metric.exceptions?.failedCount ?? 0) + (metric.exceptions?.overdueCount ?? 0);
        const inTransitCount = Math.max(0, metric.processing?.inTransitCount ?? 0);
        const successfulCount = Math.max(0, totalInvoices - failedCount - inTransitCount);

        return {
            totalInvoices,
            successfulCount,
            inTransitCount,
            failedCount,
            successRate: totalInvoices > 0 ? (successfulCount * 100) / totalInvoices : 0,
            errorRate: totalInvoices > 0 ? (failedCount * 100) / totalInvoices : 0,
            avgTransitDays: Number(metric.processing?.avgTransitDays ?? 0),
            stpRate: Number(metric.processing?.stpRate ?? 0),
        };
    }, [selectedCountryDetailsMetric]);

    const countryDetailsHealthCategory = useMemo<WorldMapHealthCategory>(() => {
        const total = countryDetailsSnapshot.totalInvoices;
        if (total <= 0) {
            return 'noData';
        }

        const failureRate = countryDetailsSnapshot.errorRate;
        const inTransitRate = total > 0 ? (countryDetailsSnapshot.inTransitCount * 100) / total : 0;
        const successRate = countryDetailsSnapshot.successRate;

        if (failureRate >= inTransitRate && failureRate >= successRate) {
            return 'critical';
        }

        if (inTransitRate >= failureRate && inTransitRate >= successRate) {
            return 'warning';
        }

        return 'healthy';
    }, [countryDetailsSnapshot]);

    const countryDetailsTrendSeries = useMemo(() => {
        const hourly = countryDetailsHourlyTrend.slice(-24);
        return {
            labels: hourly.map((point) => point.label),
            successful: hourly.map((point) => Math.max(0, Number(point.successful ?? 0))),
            failed: hourly.map((point) => Math.max(0, Number(point.failed ?? 0))),
        };
    }, [countryDetailsHourlyTrend]);

    const countryDetailsTrendNowLabel = dayjs().format('HH:00');

    const countryDetailsTrendChartOption = useMemo(() => ({
        backgroundColor: 'transparent',
        animation: true,
        grid: {
            left: 12,
            right: 8,
            top: 14,
            bottom: 24,
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                lineStyle: {
                    color: theme === 'dark' ? 'rgba(203, 213, 225, 0.45)' : 'rgba(71, 85, 105, 0.4)',
                    width: 1,
                },
            },
            formatter: (params: any) => {
                const rows = Array.isArray(params) ? params : [params];
                const hourLabel = rows[0]?.axisValueLabel ?? rows[0]?.axisValue ?? '-';

                const successfulValue = Number(rows.find((entry: any) => entry?.seriesName === 'successful')?.value ?? 0);
                const failedValue = Number(rows.find((entry: any) => entry?.seriesName === 'failed')?.value ?? 0);

                return `
                    <div style="min-width:170px;">
                        <div style="font-weight:700;margin-bottom:4px;">${hourLabel}</div>
                        <div style="display:flex;justify-content:space-between;gap:12px;">
                            <span>${getI18n('eim-successful-label', 'Successful', intl)}</span>
                            <strong>${formatCount(successfulValue)}</strong>
                        </div>
                        <div style="display:flex;justify-content:space-between;gap:12px;">
                            <span>${getI18n('eim-failed-label', 'Failed', intl)}</span>
                            <strong>${formatCount(failedValue)}</strong>
                        </div>
                    </div>
                `;
            },
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: countryDetailsTrendSeries.labels,
            axisLabel: {
                color: '#7f8ba0',
                interval: 5,
            },
            axisLine: {
                lineStyle: {
                    color: 'rgba(149, 165, 192, 0.35)',
                },
            },
            axisTick: {
                show: false,
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            splitNumber: 4,
            axisLabel: {
                color: '#7f8ba0',
                formatter: (value: number) => formatCount(value),
            },
            splitLine: {
                lineStyle: {
                    color: 'rgba(149, 165, 192, 0.24)',
                },
            },
        },
        series: [
            {
                name: 'successful',
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: { color: '#22c55e' },
                lineStyle: { color: '#22c55e', width: 2 },
                areaStyle: { color: 'rgba(34, 197, 94, 0.14)' },
                data: countryDetailsTrendSeries.successful,
                markLine: countryDetailsTrendSeries.labels.includes(countryDetailsTrendNowLabel)
                    ? {
                        symbol: ['none', 'none'],
                        silent: true,
                        lineStyle: {
                            color: theme === 'dark' ? '#f8fafc' : '#334155',
                            width: 1,
                            type: 'dashed',
                        },
                        label: {
                            show: false,
                        },
                        data: [{ xAxis: countryDetailsTrendNowLabel }],
                    }
                    : undefined,
            },
            {
                name: 'failed',
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: { color: '#ef4444' },
                lineStyle: { color: '#ef4444', width: 2 },
                data: countryDetailsTrendSeries.failed,
            },
        ],
    }), [countryDetailsTrendNowLabel, countryDetailsTrendSeries.failed, countryDetailsTrendSeries.labels, countryDetailsTrendSeries.successful, formatCount, intl, theme]);

    const countryDetailsErrorBreakdown = useMemo(() => {
        const tones = ['validation', 'partner', 'timeout', 'system'] as const;
        if (countryDetailsTopErrorMessages.length > 0) {
            const total = countryDetailsTopErrorMessages.reduce((sum, row) => sum + Math.max(0, Number(row.count ?? 0)), 0);

            return countryDetailsTopErrorMessages.map((row, index) => {
                const count = Math.max(0, Number(row.count ?? 0));
                const fallbackPercent = Math.round(Number(row.percentage ?? 0));
                const label = (row.message ?? '').trim().length > 0
                    ? String(row.message).trim()
                    : getI18n('eim-country-details-error-unknown', 'Unknown', intl);

                return {
                    label,
                    tone: tones[index % tones.length],
                    count,
                    percent: total > 0 ? Math.round((count / total) * 100) : fallbackPercent,
                };
            });
        }

        const total = Math.max(0, countryDetailsSnapshot.failedCount);
        const weighted = [0.38, 0.26, 0.18, 0.18];
        const labels = [
            getI18n('eim-country-details-error-validation', 'Validation Error', intl),
            getI18n('eim-country-details-error-partner', 'Partner Error', intl),
            getI18n('eim-country-details-error-timeout', 'Timeout', intl),
            getI18n('eim-country-details-error-system', 'System Error', intl),
        ];

        const counts = weighted.map((factor, index) => {
            if (!total) {
                return index === 0 ? 1 : 0;
            }

            return Math.max(0, Math.round(total * factor));
        });

        const countedTotal = counts.reduce((acc, value) => acc + value, 0);
        if (total && countedTotal !== total) {
            counts[0] += total - countedTotal;
        }

        return labels.map((label, index) => ({
            label,
            tone: tones[index],
            count: counts[index],
            percent: total > 0 ? Math.round((counts[index] / total) * 100) : 0,
        }));
    }, [countryDetailsSnapshot.failedCount, countryDetailsTopErrorMessages, intl]);

    const countryDetailsErrorTotal = useMemo(
        () => countryDetailsErrorBreakdown.reduce((sum, item) => sum + item.count, 0),
        [countryDetailsErrorBreakdown]
    );

    const countryDetailsErrorDonut = useMemo(() => {
        const radius = 34;
        const circumference = 2 * Math.PI * radius;
        let cumulativeOffset = 0;

        return countryDetailsErrorBreakdown.map((item) => {
            const fraction = countryDetailsErrorTotal > 0 ? item.count / countryDetailsErrorTotal : 0;
            const segmentLength = fraction * circumference;
            const visibleLength = Math.max(0, segmentLength - 1.2);
            const dashArray = `${visibleLength} ${circumference - visibleLength}`;
            const dashOffset = -cumulativeOffset;

            cumulativeOffset += segmentLength;

            return {
                ...item,
                dashArray,
                dashOffset,
            };
        });
    }, [countryDetailsErrorBreakdown, countryDetailsErrorTotal]);

    const countryDetailsAllPartners = useMemo<CountryPartnerVolumePoint[]>(() => {
        const companyCountryByCode = new Map<string, string>();
        countryMetrics.forEach((item) => {
            const companyCode = (item.companyCode ?? '').trim();
            const country = (item.country ?? '').trim().toUpperCase();
            if (!companyCode || !country) {
                return;
            }
            companyCountryByCode.set(companyCode, country);
        });

        const companyFailureRateByCode = new Map<string, number>();
        topFailureCompanyCodes.forEach((item) => {
            const companyCode = (item.companyCode ?? '').trim();
            const country = (item.country ?? '').trim().toUpperCase();
            const failureRate = Number(item.failureRate ?? 0);
            if (!companyCode) {
                return;
            }
            if (!companyCountryByCode.has(companyCode) && country) {
                companyCountryByCode.set(companyCode, country);
            }
            companyFailureRateByCode.set(companyCode, Math.max(0, failureRate));
        });

        const sourceRows = rawPartnerInvoiceSummaryData;

        if (Array.isArray(sourceRows) && sourceRows.length > 0) {
            const partnerAggregate = new Map<string, {
                volume: number;
                countryVolumeByCode: Map<string, number>;
                weightedFailureRateSum: number;
                weightedFailureRateVolume: number;
            }>();

            let skippedMissingPartnerNo = 0;
            let acceptedRows = 0;

            sourceRows.forEach((row) => {
                const partnerNumber = normalizePartnerNumber(
                    readFirstDefinedField(row, ['partnerno', 'partnerNo', 'partner_no', 'PARTNERNO', 'PARTNER_NO'])
                    || readFieldByNormalizedKey(row, ['partnerNo', 'partner_no', 'partner_number', 'partnerNum', 'partner_number_id', 'partnerId', 'partnerCode'])
                );
                if (!partnerNumber) {
                    skippedMissingPartnerNo += 1;
                    return;
                }

                const rawPartnerName = String(readFirstDefinedField(row, ['partnerName', 'partner_name', 'PARTNER_NAME']) || '').trim();
                const partnerDisplayName = rawPartnerName || partnerNumber;
                const partnerGroupKey = normalizeFieldKey(partnerDisplayName);
                if (!partnerGroupKey) {
                    skippedMissingPartnerNo += 1;
                    return;
                }

                const companyCode = readFirstDefinedField(row, ['company', 'companyCode', 'COMPANY', 'COMPANY_CODE']);
                const volume = Math.max(0, parseNumericLike(readFirstDefinedField(row, ['total', 'TOTAL', 'volume', 'VOLUME'])));
                acceptedRows += 1;

                const aggregate = partnerAggregate.get(partnerGroupKey) ?? {
                    volume: 0,
                    countryVolumeByCode: new Map<string, number>(),
                    weightedFailureRateSum: 0,
                    weightedFailureRateVolume: 0,
                };

                aggregate.volume += volume;

                const country = companyCountryByCode.get(companyCode) ?? 'N/A';
                const countryVolume = aggregate.countryVolumeByCode.get(country) ?? 0;
                aggregate.countryVolumeByCode.set(country, countryVolume + volume);

                const failureRate = companyFailureRateByCode.get(companyCode);
                if (typeof failureRate === 'number') {
                    aggregate.weightedFailureRateSum += failureRate * volume;
                    aggregate.weightedFailureRateVolume += volume;
                }

                partnerAggregate.set(partnerGroupKey, aggregate);
            });

            if (acceptedRows === 0) {
                const sampleRow = sourceRows[0] ?? {};
                const sampleRowKeys = Object.keys(sampleRow);
                const sampleRowNormalizedKeys = sampleRowKeys.map((key) => normalizeFieldKey(key));
                const warningSignature = JSON.stringify(sampleRowNormalizedKeys);

                if (partnerSummaryInvalidShapeWarningRef.current !== warningSignature) {
                    partnerSummaryInvalidShapeWarningRef.current = warningSignature;
                    console.warn('[EIM Executive][PartnerLookup] Partner invoice summary dataset has no parseable partner numbers', {
                        totalRows: sourceRows.length,
                        skippedMissingPartnerNo,
                        sampleRowKeys,
                        sampleRowNormalizedKeys,
                        sampleRow,
                    });
                }
            }

            const partnerDisplayNameByGroupKey = new Map<string, string>();
            sourceRows.forEach((row) => {
                const partnerNumber = normalizePartnerNumber(
                    readFirstDefinedField(row, ['partnerno', 'partnerNo', 'partner_no', 'PARTNERNO', 'PARTNER_NO'])
                    || readFieldByNormalizedKey(row, ['partnerNo', 'partner_no', 'partner_number', 'partnerNum', 'partner_number_id', 'partnerId', 'partnerCode'])
                );
                const rawPartnerName = String(readFirstDefinedField(row, ['partnerName', 'partner_name', 'PARTNER_NAME']) || '').trim();
                const partnerDisplayName = rawPartnerName || partnerNumber;
                const partnerGroupKey = normalizeFieldKey(partnerDisplayName);

                if (partnerGroupKey && partnerDisplayName && !partnerDisplayNameByGroupKey.has(partnerGroupKey)) {
                    partnerDisplayNameByGroupKey.set(partnerGroupKey, partnerDisplayName);
                }
            });

            const rankedByPartnerName = Array.from(partnerAggregate.entries())
                .map(([partnerGroupKey, aggregate]) => {
                    const dominantCountry = Array.from(aggregate.countryVolumeByCode.entries())
                        .sort((left, right) => right[1] - left[1])[0]?.[0] ?? 'N/A';
                    const effectiveFailureRate = aggregate.weightedFailureRateVolume > 0
                        ? aggregate.weightedFailureRateSum / aggregate.weightedFailureRateVolume
                        : 0;
                    const successRate = Math.max(0, Math.min(100, 100 - effectiveFailureRate));

                    return {
                        partner: partnerDisplayNameByGroupKey.get(partnerGroupKey) ?? partnerGroupKey,
                        country: dominantCountry,
                        volume: aggregate.volume,
                        successRate,
                    };
                })
                .sort((left, right) => right.volume - left.volume);

            if (rankedByPartnerName.length > 0) {
                return rankedByPartnerName;
            }
        }

        return [];
    }, [countryMetrics, rawPartnerInvoiceSummaryData, topFailureCompanyCodes]);

    const partnerNameByPartnerNo = useMemo(() => {
        const lookup: Record<string, string> = {};

        rawPartnerInvoiceSummaryData.forEach((row) => {
            const partnerNo = normalizePartnerNumber(readFirstDefinedField(row, ['partnerno', 'partnerNo', 'partner_no']));
            const partnerName = readFirstDefinedField(row, ['partnerName', 'partner_name', 'PARTNER_NAME']);

            if (partnerNo && partnerName && !lookup[partnerNo]) {
                lookup[partnerNo] = partnerName;
            }
        });

        return lookup;
    }, [rawPartnerInvoiceSummaryData]);

    const countryDetailsTopPartners = useMemo(() => countryDetailsAllPartners.slice(0, 5), [countryDetailsAllPartners]);

    const countryDetailsAllPartnersSorted = useMemo(() => {
        const sorted = [...countryDetailsAllPartners];
        const directionFactor = allPartnersSortDirection === 'desc' ? -1 : 1;

        sorted.sort((left, right) => {
            if (allPartnersSortKey === 'successRate') {
                return (left.successRate - right.successRate) * directionFactor;
            }

            return (left.volume - right.volume) * directionFactor;
        });

        return sorted;
    }, [allPartnersSortDirection, allPartnersSortKey, countryDetailsAllPartners]);

    const getPartnerDisplayLabel = useCallback((partnerNo: string, includePartnerNumber = true) => {
        const normalizedPartnerNo = partnerNo.trim();
        if (!normalizedPartnerNo) {
            return '-';
        }

        const partnerNameFromSummary = partnerNameByPartnerNo[normalizedPartnerNo];
        if (partnerNameFromSummary) {
            if (includePartnerNumber) {
                return `${partnerNameFromSummary} (${normalizedPartnerNo})`;
            }

            return partnerNameFromSummary;
        }
        return normalizedPartnerNo;
    }, [partnerNameByPartnerNo]);

    const getPartnerSystemCode = useCallback((partnerNo: string) => {
        return '';
    }, []);

    const allPartnersVolumeSortIndicator = useMemo(() => {
        if (allPartnersSortKey !== 'volume') {
            return '';
        }

        return allPartnersSortDirection === 'desc' ? ' v' : ' ^';
    }, [allPartnersSortDirection, allPartnersSortKey]);

    const allPartnersSuccessRateSortIndicator = useMemo(() => {
        if (allPartnersSortKey !== 'successRate') {
            return '';
        }

        return allPartnersSortDirection === 'desc' ? ' v' : ' ^';
    }, [allPartnersSortDirection, allPartnersSortKey]);

    const countryDetailsDeltaView = useMemo(() => {
        const formatDeltaText = (value: number | null | undefined, suffix: string) => {
            if (typeof value !== 'number' || Number.isNaN(value)) {
                return `+0${suffix}`;
            }

            return intl.formatNumber(value, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            }) + suffix;
        };

        const volumeDelta = Number(executiveMetricsV2?.volume?.volumeDelta ?? 0);
        const stpDelta = Number(executiveMetricsV2?.flowHealth?.stpDelta ?? 0);
        const transitDelta = Number((executiveMetricsV2?.flowHealth?.avgTransitDays ?? 0) - (executiveMetricsV2?.flowHealth?.previousAvgTransitDays ?? 0));
        const errorDelta = Number(executiveMetricsV2?.risk?.failureRateDelta ?? 0);

        return {
            volumeDeltaText: formatDeltaText(volumeDelta, '%'),
            stpDeltaText: formatDeltaText(stpDelta, '%'),
            transitDeltaText: formatDeltaText(-transitDelta, ' sec'),
            errorDeltaText: formatDeltaText(-errorDelta, '%'),
        };
    }, [executiveMetricsV2, intl]);
    const systemMetricRows = useMemo(() => systemMetrics.slice(0, 6), [systemMetrics]);
    const regionNames = useMemo(() => {
        if (!Intl.DisplayNames) {
            return null;
        }

        return new Intl.DisplayNames(['en'], { type: 'region' });
    }, []);

    const worldMapData = useMemo<WorldMapDataPoint[]>(() => {
        return countryMetrics
            .map((countryMetric) => {
                const countryCode = resolveCountryCode(countryMetric, companyCountryLookup);
                if (countryCode.length !== 2) {
                    return null;
                }

                const localizedCountryName = getCountryDisplayName(countryCode, regionNames);
                const mapCountryName = getBmwMapCountryName(countryCode, localizedCountryName);
                if (!mapCountryName) {
                    return null;
                }

                const totalProcessed = countryMetric.intake?.totalProcessed ?? 0;
                const inTransitCount = countryMetric.processing?.inTransitCount ?? 0;
                const failedCount = countryMetric.exceptions?.failedCount ?? 0;
                const overdueCount = countryMetric.exceptions?.overdueCount ?? 0;
                const stpRate = countryMetric.processing?.stpRate ?? 0;

                return {
                    name: mapCountryName,
                    value: totalProcessed,
                    countryCode,
                    totalProcessed,
                    inTransitCount,
                    failedCount,
                    overdueCount,
                    stpRate,
                };
            })
            .filter((item): item is WorldMapDataPoint => item !== null);
    }, [companyCountryLookup, countryMetrics]);

    const worldMapCountrySeriesData = useMemo<WorldMapSeriesDataPoint[]>(() => worldMapData.map((item) => {
        const totalProcessed = item.totalProcessed;
        const failedInvoiceCount = item.failedCount + item.overdueCount;
        const failureRate = totalProcessed > 0 ? (failedInvoiceCount / totalProcessed) * 100 : 0;
        const inTransitRate = totalProcessed > 0 ? (item.inTransitCount / totalProcessed) * 100 : 0;
        const successRate = Math.max(0, Math.min(100, item.stpRate));

        let healthCategory: WorldMapHealthCategory = 'noData';
        if (totalProcessed > 0) {
            const dominantRate = Math.max(failureRate, inTransitRate, successRate);
            if (dominantRate === failureRate) {
                healthCategory = 'critical';
            } else if (dominantRate === inTransitRate) {
                healthCategory = 'warning';
            } else {
                healthCategory = 'healthy';
            }
        }

        return {
            ...item,
            healthCategory,
            failureRate,
            inTransitRate,
            itemStyle: {
                areaColor: WORLD_MAP_HEALTH_COLORS[healthCategory],
            },
        };
    }), [worldMapData]);

    const worldMapLiveTraffic = useMemo(() => {
        const hourlyPoints = (inboundOutboundTrendSeries?.hourly ?? []).map((point) => ({
            label: point.label,
            value: Math.max(0, Number(point.inbound ?? 0) + Number(point.outbound ?? 0)),
        }));

        const recentPoints = hourlyPoints.slice(-20);
        const fallbackValue = Math.max(0, Number(executiveMetricsV2?.volume?.inTransitCount ?? 0));
        const latestHourlyValue = recentPoints.length > 0
            ? (recentPoints.at(-1)?.value ?? 0)
            : fallbackValue;
        const latestNonZeroHourlyValue = recentPoints
            .slice()
            .reverse()
            .find((point) => point.value > 0)?.value ?? 0;

        let lastFiveMinutesCount = 0;
        if (latestNonZeroHourlyValue > 0) {
            lastFiveMinutesCount = Math.max(0, Math.round(latestNonZeroHourlyValue / 12));
        } else if (latestHourlyValue > 0) {
            lastFiveMinutesCount = Math.max(0, Math.round(latestHourlyValue / 12));
        } else {
            // Integration snapshots can have empty recent hourly buckets; fall back to current in-transit volume.
            lastFiveMinutesCount = fallbackValue;
        }

        const countryPoints = worldMapCountrySeriesData
            .filter((point) => point.totalProcessed > 0)
            .sort((left, right) => right.totalProcessed - left.totalProcessed)
            .slice(0, 20);

        if (countryPoints.length > 0) {
            const maxCountryVolume = Math.max(1, ...countryPoints.map((point) => point.totalProcessed));
            const normalizedBars: LiveTrafficBar[] = countryPoints.map((point, index) => {
                const ratio = point.totalProcessed / maxCountryVolume;

                let level: LiveTrafficBarLevel = 1;
                if (ratio >= 0.85) {
                    level = 6;
                } else if (ratio >= 0.7) {
                    level = 5;
                } else if (ratio >= 0.55) {
                    level = 4;
                } else if (ratio >= 0.4) {
                    level = 3;
                } else if (ratio >= 0.25) {
                    level = 2;
                }

                const tone = getLiveTrafficToneFromHealthCategory(point.healthCategory);

                return {
                    key: `${point.countryCode}-${index}`,
                    level,
                    tone,
                };
            });

            return {
                lastFiveMinutesCount,
                bars: normalizedBars,
            };
        }

        if (recentPoints.length === 0) {
            return {
                lastFiveMinutesCount,
                bars: [] as LiveTrafficBar[],
            };
        }

        const maxValue = Math.max(1, ...recentPoints.map((point) => point.value));
        const averageValue = recentPoints.reduce((accumulator, point) => accumulator + point.value, 0) / recentPoints.length;
        const normalizedBars: LiveTrafficBar[] = recentPoints.map((point, index) => {
            const ratio = point.value / maxValue;

            let level: LiveTrafficBarLevel = 1;
            if (ratio >= 0.85) {
                level = 6;
            } else if (ratio >= 0.7) {
                level = 5;
            } else if (ratio >= 0.55) {
                level = 4;
            } else if (ratio >= 0.4) {
                level = 3;
            } else if (ratio >= 0.25) {
                level = 2;
            }

            return {
                key: `${point.label}-${index}`,
                level,
                tone: point.value > averageValue * 1.25 ? 'critical' : 'positive',
            };
        });

        return {
            lastFiveMinutesCount,
            bars: normalizedBars,
        };
    }, [executiveMetricsV2?.volume?.inTransitCount, inboundOutboundTrendSeries?.hourly, worldMapCountrySeriesData]);

    const hideWorldMapHoverTooltip = Boolean(countryActionSelection)
        || Boolean(countryDetailsSelection)
        || allPartnersModalOpen;

    const worldMapSeriesData = useMemo<WorldMapSeriesDataPoint[]>(() => worldMapData.map((item) => {
        const totalProcessed = item.totalProcessed;
        const failedInvoiceCount = item.failedCount + item.overdueCount;
        const failureRate = totalProcessed > 0 ? (failedInvoiceCount / totalProcessed) * 100 : 0;
        const inTransitRate = totalProcessed > 0 ? (item.inTransitCount / totalProcessed) * 100 : 0;
        const successRate = Math.max(0, Math.min(100, item.stpRate));

        let healthCategory: WorldMapHealthCategory = 'noData';
        if (totalProcessed > 0) {
            const dominantRate = Math.max(failureRate, inTransitRate, successRate);
            if (dominantRate === failureRate) {
                healthCategory = 'critical';
            } else if (dominantRate === inTransitRate) {
                healthCategory = 'warning';
            } else {
                healthCategory = 'healthy';
            }
        }

        return {
            ...item,
            healthCategory,
            failureRate,
            inTransitRate,
            itemStyle: {
                areaColor: WORLD_MAP_HEALTH_COLORS[healthCategory],
            },
        };
    }), [worldMapData]);

    const worldMapOptions = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: {
            show: !hideWorldMapHoverTooltip,
            triggerOn: hideWorldMapHoverTooltip ? 'none' : 'mousemove|click',
            trigger: 'item',
            formatter: (params: any) => {
                const data = params?.data as WorldMapSeriesDataPoint | undefined;
                if (!data) {
                    return `${params?.name ?? getI18n('eim-country-details-error-unknown', 'Unknown', intl)}<br/>${getI18n('eim-total-invoices', 'Total invoices', intl)}: 0`;
                }

                const healthLabel = {
                    healthy: getI18n('eim-world-map-health-healthy', 'Healthy', intl),
                    warning: getI18n('eim-world-map-health-warning', 'Warning', intl),
                    critical: getI18n('eim-world-map-health-critical', 'Critical', intl),
                    noData: getI18n('eim-world-map-health-no-data', 'No data', intl),
                }[data.healthCategory];

                const countryFlagSrc = getCountryFlagUrl(data.countryCode);
                const healthChipStyle = {
                    healthy: 'background:#e8f7ef;color:#1f8b4c;',
                    warning: 'background:#fff4dc;color:#ae6f00;',
                    critical: 'background:#ffe8e8;color:#ba2a2a;',
                    noData: 'background:#eef1f5;color:#64748b;',
                }[data.healthCategory];

                return `
                    <div style="min-width:240px;padding:4px 2px;">
                        <div style="align-items:center;display:flex;justify-content:space-between;gap:12px;margin-bottom:10px;">
                            <div style="align-items:center;display:flex;gap:8px;">
                                <span style="align-items:center;background:#ffffff;border:1px solid #d8dde6;border-radius:999px;display:inline-flex;height:20px;justify-content:center;overflow:hidden;width:20px;">
                                    <img
                                        src="${countryFlagSrc}"
                                        alt="${data.countryCode}"
                                        width="20"
                                        height="20"
                                        style="display:block;height:20px;object-fit:cover;width:20px;"
                                        onerror="this.style.display='none';this.nextElementSibling.style.display='inline-flex';"
                                    />
                                    <span style="display:none;font-size:12px;line-height:1;">🌐</span>
                                </span>
                                <strong style="color:#1f2937;font-size:14px;">${params.name}</strong>
                            </div>
                            <span style="${healthChipStyle}border-radius:999px;font-size:11px;font-weight:600;padding:2px 8px;">${healthLabel}</span>
                        </div>
                        <div style="display:grid;grid-template-columns:1fr auto;row-gap:6px;column-gap:12px;">
                            <span style="color:#6b7280;">${getI18n('eim-total-invoices', 'Total invoices', intl)}</span>
                            <strong style="color:#111827;">${formatCount(data.totalProcessed)}</strong>
                            <span style="color:#6b7280;">${getI18n('eim-stp-rate', 'STP rate', intl)}</span>
                            <strong style="color:#111827;">${formatPercent(data.stpRate)}</strong>
                            <span style="color:#6b7280;">${getI18n('eim-in-transit-label', 'In Transit', intl)}</span>
                            <strong style="color:#111827;">${formatCount(data.inTransitCount)}</strong>
                            <span style="color:#6b7280;">${getI18n('eim-failed-label', 'Failed', intl)}</span>
                            <strong style="color:#c62828;">${formatCount(data.failedCount + data.overdueCount)}</strong>
                            <span style="color:#6b7280;">${getI18n('eim-v2-failure-rate', 'Failure rate', intl)}</span>
                            <strong style="color:#111827;">${formatPercent(data.failureRate)}</strong>
                        </div>
                    </div>
                `;
            },
        },
        series: [
            {
                name: getI18n('eim-world-map-series-name', 'Total invoices by country', intl),
                type: 'map',
                map: 'world',
                roam: true,
                // Force the world geometry to use more of the taller canvas area.
                layoutCenter: ['50%', '44%'],
                layoutSize: '150%',
                left: 12,
                right: 12,
                top: 12,
                bottom: 76,
                emphasis: {
                    label: {
                        show: false,
                    },
                    itemStyle: {
                        borderColor: theme === 'light' ? '#23344d' : '#f0f6ff',
                        borderWidth: 1,
                    },
                },
                itemStyle: {
                    areaColor: WORLD_MAP_HEALTH_COLORS.noData,
                    borderColor: theme === 'light' ? '#9fb3d3' : '#183252',
                    borderWidth: 0.7,
                },
                data: worldMapCountrySeriesData,
            },
        ],
    }), [formatCount, formatPercent, hideWorldMapHoverTooltip, intl, theme, worldMapCountrySeriesData]);

    const worldMapModalOptions = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: worldMapOptions.tooltip,
        series: [
            {
                name: getI18n('eim-world-map-series-name', 'Total invoices by country', intl),
                type: 'map',
                map: 'world',
                roam: true,
                roamTrigger: 'global',
                layoutCenter: ['50%', '50%'],
                layoutSize: '90%',
                preserveAspect: true,
                scaleLimit: {
                    min: 0.85,
                    max: 8,
                },
                selectedMode: false,
                emphasis: {
                    label: {
                        show: false,
                    },
                    itemStyle: {
                        borderColor: theme === 'light' ? '#23344d' : '#f0f6ff',
                        borderWidth: 1,
                    },
                },
                itemStyle: {
                    areaColor: WORLD_MAP_HEALTH_COLORS.noData,
                    borderColor: theme === 'light' ? '#9fb3d3' : '#183252',
                    borderWidth: 0.7,
                },
                data: worldMapCountrySeriesData,
            },
        ],
    }), [intl, theme, worldMapCountrySeriesData, worldMapOptions.tooltip]);

    const handleWorldMapClick = useCallback((params: any) => {
        const countryCode = params?.data?.countryCode;
        if (!countryCode) {
            return;
        }

        openCountryActionModal({
            country: countryCode,
        });
    }, [openCountryActionModal]);

    const worldMapEvents = useMemo(() => ({ click: handleWorldMapClick }), [handleWorldMapClick]);

    const openWorldMapModal = useCallback(() => {
        setWorldMapModalOpen(true);
    }, []);

    useEffect(() => {
        if (!worldMapModalOpen) {
            return;
        }

        const resizeChart = () => {
            const chart = worldMapModalChartRef.current?.getEchartsInstance();
            chart?.resize();
        };
        const timeoutId = globalThis.setTimeout(resizeChart, 0);
        const delayedTimeoutId = globalThis.setTimeout(resizeChart, 180);
        const animationFrameId = globalThis.requestAnimationFrame(resizeChart);

        return () => {
            globalThis.clearTimeout(timeoutId);
            globalThis.clearTimeout(delayedTimeoutId);
            globalThis.cancelAnimationFrame(animationFrameId);
        };
    }, [worldMapModalOpen]);

    const heroMetaItems = useMemo(() => [
        { label: getI18n('eim-period', 'Period', intl), value: selectedRange },
        { label: getI18n('eim-last-updated', 'Last updated', intl), value: dayjs().format(getDateFormatOrDefault(props.currentUser)) },
    ], [selectedRange, intl, props.currentUser]);

    const handleToggleTheme = useCallback(() => {
        setTheme((previousTheme) => (previousTheme === 'dark' ? 'light' : 'dark'));
    }, []);

    const handleIncreaseFontSize = useCallback(() => {
        setFontSizeLevel((prev) => {
            const next = Math.min(prev + 1, 9);
            globalThis.sessionStorage.setItem(EXECUTIVE_FONT_SIZE_LEVEL_SESSION_KEY, next.toString());
            return next;
        });
    }, []);

    const handleDecreaseFontSize = useCallback(() => {
        setFontSizeLevel((prev) => {
            const next = Math.max(prev - 1, 0);
            globalThis.sessionStorage.setItem(EXECUTIVE_FONT_SIZE_LEVEL_SESSION_KEY, next.toString());
            return next;
        });
    }, []);

    const handleResetFontSize = useCallback(() => {
        setFontSizeLevel(DEFAULT_FONT_SIZE_LEVEL);
        globalThis.sessionStorage.setItem(EXECUTIVE_FONT_SIZE_LEVEL_SESSION_KEY, DEFAULT_FONT_SIZE_LEVEL.toString());
    }, []);

    const handleRefresh = useCallback(() => {
        void loadDashboard({ fromDate: currentRequestRange.fromDate, toDate: currentRequestRange.toDate });
    }, [currentRequestRange, loadDashboard]);

    const openChannelBreakdownModal = useCallback(() => {
        setChannelBreakdownZoomLevel(0);
        setChannelBreakdownModalOpen(true);
    }, []);

    const increaseChannelBreakdownZoom = useCallback(() => {
        setChannelBreakdownZoomLevel((previousLevel) => Math.min(previousLevel + 5, 20));
    }, []);

    const decreaseChannelBreakdownZoom = useCallback(() => {
        setChannelBreakdownZoomLevel((previousLevel) => Math.max(previousLevel - 5, -20));
    }, []);

    const getChannelBreakdownZoomLabel = useCallback((level: number): string => {
        if (level > 0) return `+${level}`;
        if (level < 0) return `${level}`;
        return '0';
    }, []);

    const openThresholdModal = useCallback(() => {
        thresholdForm.setFieldsValue(thresholdConfig);
        setThresholdModalOpen(true);
    }, [thresholdConfig, thresholdForm]);

    const handleSaveThresholds = useCallback(async () => {
        const values = await thresholdForm.validateFields();

        if (values.onTimeWarningThreshold > values.onTimePositiveThreshold) {
            message.error(getI18n('eim-thresholds-on-time-order-error', 'On-time warning threshold must be less than or equal to on-time positive threshold.', intl));
            return;
        }
        if (values.coverageWarningThreshold > values.coveragePositiveThreshold) {
            message.error(getI18n('eim-thresholds-coverage-order-error', 'Coverage warning threshold must be less than or equal to coverage positive threshold.', intl));
            return;
        }

        const nextConfig = toThresholdConfig(values);
        setThresholdConfig(nextConfig);
        globalThis.sessionStorage.setItem(EXECUTIVE_THRESHOLD_CONFIG_SESSION_KEY, JSON.stringify(nextConfig));
        setThresholdModalOpen(false);
    }, [intl, thresholdForm]);

    const handleResetThresholds = useCallback(() => {
        setThresholdConfig(DEFAULT_EXECUTIVE_THRESHOLDS);
        thresholdForm.setFieldsValue(DEFAULT_EXECUTIVE_THRESHOLDS);
        globalThis.sessionStorage.setItem(EXECUTIVE_THRESHOLD_CONFIG_SESSION_KEY, JSON.stringify(DEFAULT_EXECUTIVE_THRESHOLDS));
    }, [thresholdForm]);

    const periodHint = useMemo(() => {
        if (selectedPeriodOption === 'custom') {
            return getI18n('eim-custom-period-hint', 'Choose any start and end date for a custom reporting period.', intl);
        }

        return getI18n('eim-period-preset-hint', 'Choose a preset period to refresh the executive metrics instantly.', intl);
    }, [intl, selectedPeriodOption]);

    const currentThresholdSummary = useMemo(
        () => `${getI18n('eim-threshold-summary-short-label', 'Now', intl)}: `
            + `OT ${thresholdConfig.onTimePositiveThreshold}/${thresholdConfig.onTimeWarningThreshold}  |  `
            + `Fail > ${thresholdConfig.failureCriticalThreshold}  |  `
            + `Overdue ${thresholdConfig.overdueWarningThreshold}/${thresholdConfig.overdueCriticalThreshold}  |  `
            + `Overdue age ${thresholdConfig.overdueHoursThreshold}h  |  `
            + `Cov ${thresholdConfig.coveragePositiveThreshold}/${thresholdConfig.coverageWarningThreshold}  |  `
            + `Transit EMAIL ${thresholdConfig.emailTransitSlaHours}h  |  `
            + `PORTAL ${thresholdConfig.portalTransitSlaHours}h  |  `
            + `EDI/QBQ ${thresholdConfig.ediTransitSlaHours}h`,
        [intl, thresholdConfig]
    );

    return (
        <Spin spinning={loading} size={'large'} indicator={<LoadingOutlined style={{ fontSize: 50 }} />}>
            <div
                className={classNames(dashboardStyles.dashboardPage, {
                    [dashboardStyles.dashboardPageLight]: theme === 'light',
                })}
            >
                <div className={dashboardStyles.pageThemeToolbar}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '0 12px', borderRight: '1px solid rgba(149, 165, 192, 0.2)' }}>
                        <Button
                            size="small"
                            onClick={handleDecreaseFontSize}
                            disabled={fontSizeLevel === 0}
                            title={getI18n('eim-font-size-decrease', 'Decrease font size', intl)}
                            style={{ minWidth: '32px' }}
                        >
                            A−
                        </Button>
                        <span style={{ fontSize: '12px', color: '#9db0ce', minWidth: '32px', textAlign: 'center', fontWeight: '500' }}>
                            {fontSizeLevel === DEFAULT_FONT_SIZE_LEVEL ? '0' : fontSizeLevel > DEFAULT_FONT_SIZE_LEVEL ? `+${fontSizeLevel - DEFAULT_FONT_SIZE_LEVEL}` : `${fontSizeLevel - DEFAULT_FONT_SIZE_LEVEL}`}
                        </span>
                        <Button
                            size="small"
                            onClick={handleIncreaseFontSize}
                            disabled={fontSizeLevel === 9}
                            title={getI18n('eim-font-size-increase', 'Increase font size', intl)}
                            style={{ minWidth: '32px' }}
                        >
                            A+
                        </Button>
                        {fontSizeLevel !== DEFAULT_FONT_SIZE_LEVEL && (
                            <Button
                                size="small"
                                onClick={handleResetFontSize}
                                title={getI18n('eim-font-size-reset', 'Reset font size to default', intl)}
                            >
                                {getI18n('eim-font-size-reset-label', 'Reset', intl)}
                            </Button>
                        )}
                    </div>
                    <Button
                        className={dashboardStyles.pageThemeToggle}
                        icon={<BulbOutlined />}
                        onClick={handleToggleTheme}
                    >
                        {theme === 'dark' ? getI18n('eim-switch-to-light-view', 'Switch to light view', intl) : getI18n('eim-switch-to-dark-view', 'Switch to dark view', intl)}
                    </Button>
                </div>
                <div
                    className={classNames('ag-theme-balham', dashboardStyles.dashboardShell, dashboardStyles[`fontSizeLevel${fontSizeLevel}`], {
                        [dashboardStyles.themeLight]: theme === 'light',
                    })}
                >
                    <section className={dashboardStyles.heroCard}>
                        <div>
                            <span className={dashboardStyles.eyebrow}>{getI18n('eim-monitoring', 'EIM monitoring', intl)}</span>
                            <h1 className={dashboardStyles.heroTitle}>{getI18n('eim-executive-overview', 'Executive overview', intl)}</h1>
                            <p className={dashboardStyles.heroLead}>
                                {getI18n('eim-executive-description', 'High-level view of invoice volume, processing health, exception risk, and network coverage. This screen is optimized for executive readout first, with the operational monitoring page one click away.', intl)}
                            </p>
                        </div>

                        <div className={dashboardStyles.heroMeta}>
                            {heroMetaItems.map((item) => (
                                <div className={dashboardStyles.metaPill} key={item.label}>
                                    <span className={dashboardStyles.metaLabel}>{item.label}</span>
                                    <span className={dashboardStyles.metaValue}>{item.value}</span>
                                </div>
                            ))}
                            <div className={dashboardStyles.heroActionsRow}>
                                <div className={dashboardStyles.thresholdHeroGroup}>
                                    <Tooltip title={currentThresholdSummary}>
                                        <Button
                                            className={dashboardStyles.heroAction}
                                            icon={<SettingOutlined />}
                                            onClick={openThresholdModal}
                                        >
                                            {getI18n('eim-thresholds-configure', 'Configure thresholds', intl)}
                                        </Button>
                                    </Tooltip>
                                </div>
                                <Button
                                    className={dashboardStyles.heroAction}
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                >
                                    {getI18n('eim-refresh-overview', 'Refresh overview', intl)}
                                </Button>
                                <Button
                                    className={dashboardStyles.heroAction}
                                    icon={<ArrowRightOutlined />}
                                    type="primary"
                                    onClick={redirectToMain}
                                >
                                    {getI18n('eim-open-operational-view', 'Open operational view', intl)}
                                </Button>
                            </div>
                        </div>
                    </section>

                    <div className={dashboardStyles.periodSelector}>
                        <div className={dashboardStyles.periodControls}>
                            <div className={dashboardStyles.periodControlBlock}>
                                <span className={dashboardStyles.periodModeLabel}>{getI18n('eim-period-selection', 'Period selection', intl)}</span>
                                <Select
                                    className={dashboardStyles.periodModeSelect}
                                    popupClassName={theme === 'light' ? dashboardStyles.periodSelectPopupLight : dashboardStyles.periodSelectPopupDark}
                                    options={localizedPeriodOptions}
                                    value={selectedPeriodOption}
                                    onChange={handlePeriodOptionChange}
                                />
                            </div>
                            {selectedPeriodOption === 'custom' ? (
                                <div className={dashboardStyles.periodControlBlock}>
                                    <span className={dashboardStyles.periodModeLabel}>{getI18n('eim-period-range', 'Selected period', intl)}</span>
                                    <RangePicker
                                        allowClear={false}
                                        className={dashboardStyles.periodRangePicker}
                                        disabledDate={disableFutureDates}
                                        popupClassName={theme === 'light' ? dashboardStyles.periodPickerPopupLight : dashboardStyles.periodPickerPopupDark}
                                        format={getDateFormatOrDefault(props.currentUser)}
                                        locale={getRangePickerLocalization(intl, globalThis.localStorage.getItem('locale') || 'en')}
                                        value={selectedRangeValue}
                                        onChange={handlePeriodRangeChange}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <span className={dashboardStyles.periodHint}>{periodHint}</span>
                    </div>

                    <section className={dashboardStyles.sectionGrid}>
                        {executiveSections.map((section) => (
                            <EimExecutiveSectionCard
                                key={section.key}
                                section={section}
                                onMetricDoubleClick={openMetricDetails}
                            />
                        ))}
                    </section>

                    {ENABLE_EXECUTIVE_DASHBOARD_V2 && executiveMetricsV2 ? (
                        <section className={dashboardStyles.chartGrid}>
                            <div className={dashboardStyles.chartCard}>
                                <Card bordered={false} size={'small'} title={getI18n('eim-v2-metrics-title', 'Executive Summary', intl)}>
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {formatOverdueThresholdText(
                                            getI18n('eim-v2-metrics-subtitle', 'Direct executive metrics for the selected period.', intl),
                                            thresholdConfig.overdueHoursThreshold
                                        )}
                                    </span>
                                    <div className={dashboardStyles.v2TilesGrid}>
                                        {v2SignalTiles.map((tile) => (
                                            <div
                                                key={tile.label}
                                                className={classNames(dashboardStyles.v2Tile, {
                                                    [dashboardStyles.v2ToneNeutral]: tile.tone === 'neutral',
                                                    [dashboardStyles.v2TonePositive]: tile.tone === 'positive',
                                                    [dashboardStyles.v2ToneWarning]: tile.tone === 'warning',
                                                    [dashboardStyles.v2ToneCritical]: tile.tone === 'critical',
                                                })}
                                            >
                                                <div className={dashboardStyles.v2TileTopRow}>
                                                    <div className={dashboardStyles.v2TileLabel}>
                                                        <span>{tile.label}</span>
                                                        {tile.hint ? (
                                                            <Tooltip title={tile.hint}>
                                                                <InfoCircleOutlined className={dashboardStyles.v2TileHintIcon} />
                                                            </Tooltip>
                                                        ) : null}
                                                    </div>
                                                    <span className={classNames(
                                                        dashboardStyles.v2TileBadge,
                                                        {
                                                            [dashboardStyles.v2TileBadgeNeutral]: tile.badgeTone === 'neutral',
                                                            [dashboardStyles.v2TileBadgeInfo]: tile.badgeTone === 'info',
                                                            [dashboardStyles.v2TileBadgeSuccess]: tile.badgeTone === 'success',
                                                            [dashboardStyles.v2TileBadgeWarning]: tile.badgeTone === 'warning',
                                                            [dashboardStyles.v2TileBadgeCritical]: tile.badgeTone === 'critical',
                                                        }
                                                    )}>
                                                        {tile.badgeLabel}
                                                    </span>
                                                </div>
                                                <div className={classNames(
                                                    dashboardStyles.v2MiniTrend,
                                                    {
                                                        [dashboardStyles.v2MiniTrendPositive]: tile.tone === 'positive',
                                                        [dashboardStyles.v2MiniTrendWarning]: tile.tone === 'warning',
                                                        [dashboardStyles.v2MiniTrendCritical]: tile.tone === 'critical',
                                                        [dashboardStyles.v2MiniTrendNeutral]: tile.tone === 'neutral',
                                                        [dashboardStyles.v2MiniTrendLine]: tile.sparklineMode === 'line',
                                                        [dashboardStyles.v2MiniTrendBars]: tile.sparklineMode === 'bars',
                                                    }
                                                )} aria-hidden="true">
                                                    {tile.sparklineMode === 'line' ? (
                                                        <svg viewBox="0 0 100 22" preserveAspectRatio="none" className={dashboardStyles.v2MiniTrendLineSvg}>
                                                            <polyline
                                                                points={toSparklinePoints(tile.trendLevels ?? [])}
                                                                className={dashboardStyles.v2MiniTrendLineStroke}
                                                            />
                                                        </svg>
                                                    ) : (
                                                        (tile.trendLevels ?? []).map((level: number, index: number) => (
                                                            <span
                                                                key={`${tile.label}-trend-${index}`}
                                                                className={classNames(
                                                                    dashboardStyles.v2MiniTrendBar,
                                                                    dashboardStyles[`v2MiniTrendBarLevel${level}`],
                                                                )}
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                                <div className={dashboardStyles.v2TileValueWrap}>
                                                    <div className={dashboardStyles.v2TileValue}>{tile.value}</div>
                                                    {thresholdConfig.enableValueZoom ? (
                                                        <div className={dashboardStyles.v2TileValueZoomPopup} aria-hidden="true">{tile.value}</div>
                                                    ) : null}
                                                </div>
                                                {tile.deltaText ? (
                                                    <div className={classNames(
                                                        dashboardStyles.v2TileDelta,
                                                        {
                                                            [dashboardStyles.v2TileDeltaUp]: tile.deltaDirection === 'up',
                                                            [dashboardStyles.v2TileDeltaDown]: tile.deltaDirection === 'down',
                                                            [dashboardStyles.v2TileDeltaNeutral]: tile.deltaDirection === 'neutral',
                                                        }
                                                    )}>
                                                        {getDeltaIcon(tile.deltaDirection)} {tile.deltaText}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className={classNames(
                                                dashboardStyles.v2Tile,
                                                dashboardStyles.v2TileAction,
                                                {
                                                    [dashboardStyles.v2TonePositive]: getCountryProcessingTone(thresholdConfig, executiveMetricsV2?.flowHealth?.stpRate) === 'positive',
                                                    [dashboardStyles.v2ToneWarning]: getCountryProcessingTone(thresholdConfig, executiveMetricsV2?.flowHealth?.stpRate) === 'warning',
                                                    [dashboardStyles.v2ToneCritical]: getCountryProcessingTone(thresholdConfig, executiveMetricsV2?.flowHealth?.stpRate) === 'critical',
                                                }
                                            )}
                                            onClick={openChannelBreakdownModal}
                                        >
                                            <div className={dashboardStyles.v2TileLabel}>
                                                <span>{getI18n('eim-v2-stp-by-channel', 'STP by channel', intl)}</span>
                                                <Tooltip title={v2TileHints.stpByChannel}>
                                                    <InfoCircleOutlined className={dashboardStyles.v2TileHintIcon} />
                                                </Tooltip>
                                            </div>
                                            <div className={dashboardStyles.v2TileValueWrap}>
                                                <div className={dashboardStyles.v2TileValue}>{formatPercent(executiveMetricsV2?.flowHealth?.stpRate ?? 0)}</div>
                                                {thresholdConfig.enableValueZoom ? (
                                                    <div className={dashboardStyles.v2TileValueZoomPopup} aria-hidden="true">{formatPercent(executiveMetricsV2?.flowHealth?.stpRate ?? 0)}</div>
                                                ) : null}
                                            </div>
                                            <div className={dashboardStyles.v2TileActionMeta}>
                                                {getI18n('eim-v2-click-for-channel-breakdown', 'Click for three-channel breakdown', intl)}
                                                {` • ${formatCount(channelMetricRows.length)} ${getI18n('eim-v2-channels', 'channels', intl)}`}
                                            </div>
                                        </button>
                                    </div>
                                    <div className={dashboardStyles.v2ThresholdSummary}>
                                        <span className={dashboardStyles.v2ThresholdSummaryLabel}>{getI18n('eim-threshold-summary-label', 'Threshold summary', intl)}:</span>
                                        <span>{currentThresholdSummary}</span>
                                    </div>
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-avg-transit-time-trend', 'Average Transit Time Trend', intl)}
                                    extra={(
                                        <Select<TransitTrendTimeRange>
                                            size={'small'}
                                            value={transitTrendTimeRange}
                                            onChange={setTransitTrendTimeRange}
                                            style={{ minWidth: 140 }}
                                            options={transitTrendRangeOptions}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-avg-transit-time-trend-subtitle', 'Average transit time trend with median (dashed) and standard deviation bands (±1σ).', intl)}
                                    </span>
                                    {(executiveMetricsV2.flowHealth.stpRateTrendByDay ?? []).length > 0 || (executiveMetricsV2.flowHealth.stpRateTrendByYear ?? []).length > 0 ? (
                                        <ReactEcharts
                                            option={getAverageTransitDaysTrendChartOptions(
                                                filterTransitTrendDataByRange(
                                                    (executiveMetricsV2.flowHealth.stpRateTrendByDay ?? []).map(trend => ({
                                                        month: trend.month,
                                                        avgTransitDays: trend.avgTransitDays
                                                    })),
                                                    (executiveMetricsV2.flowHealth.stpRateTrendByYear ?? []).map(trend => ({
                                                        month: trend.month,
                                                        avgTransitDays: trend.avgTransitDays
                                                    })),
                                                    transitTrendTimeRange,
                                                    todayTransitHourlyTrendData
                                                ),
                                                executiveMetricsV2.flowHealth.avgTransitDays ?? 0,
                                                theme,
                                                intl
                                            )}
                                            onEvents={transitTrendChartEvents}
                                            style={{ height: 300, width: '100%' }}
                                            notMerge={true}
                                            lazyUpdate={true}
                                        />
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-no-transit-trend-data', 'No transit trend data available.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-inbound-volume-trend', 'Inbound Volume Trend', intl)}
                                    extra={(
                                        <Select<TransitTrendTimeRange>
                                            size={'small'}
                                            value={transitTrendTimeRange}
                                            onChange={setTransitTrendTimeRange}
                                            style={{ minWidth: 140 }}
                                            options={transitTrendRangeOptions}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-inbound-volume-trend-subtitle', 'Inbound volume trend with median (dashed) and standard deviation bands (±1σ).', intl)}
                                    </span>
                                    {inboundVolumeTrendData.length > 0 ? (
                                        <ReactEcharts
                                            option={getVolumeTrendWithMedianAndStdDevChartOptions(
                                                inboundVolumeTrendData,
                                                getI18n('eim-inbound-volume-trend', 'Inbound Volume Trend', intl),
                                                getI18n('eim-inbound-volume-series', 'Inbound Volume', intl),
                                                theme,
                                                intl
                                            )}
                                            onEvents={inboundTrendChartEvents}
                                            style={{ height: 300, width: '100%' }}
                                            notMerge={true}
                                            lazyUpdate={true}
                                        />
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-no-inbound-volume-trend-data', 'No inbound volume trend data available.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-outbound-volume-trend', 'Outbound Volume Trend', intl)}
                                    extra={(
                                        <Select<TransitTrendTimeRange>
                                            size={'small'}
                                            value={transitTrendTimeRange}
                                            onChange={setTransitTrendTimeRange}
                                            style={{ minWidth: 140 }}
                                            options={transitTrendRangeOptions}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-outbound-volume-trend-subtitle', 'Outbound volume trend with median (dashed) and standard deviation bands (±1σ).', intl)}
                                    </span>
                                    {outboundVolumeTrendData.length > 0 ? (
                                        <ReactEcharts
                                            option={getVolumeTrendWithMedianAndStdDevChartOptions(
                                                outboundVolumeTrendData,
                                                getI18n('eim-outbound-volume-trend', 'Outbound Volume Trend', intl),
                                                getI18n('eim-outbound-volume-series', 'Outbound Volume', intl),
                                                theme,
                                                intl
                                            )}
                                            onEvents={outboundTrendChartEvents}
                                            style={{ height: 300, width: '100%' }}
                                            notMerge={true}
                                            lazyUpdate={true}
                                        />
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-no-outbound-volume-trend-data', 'No outbound volume trend data available.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-v2-top-groups-title', 'Top impacted groups (risk)', intl)}
                                    extra={(
                                        <Select<TopGroupRankMode>
                                            size={'small'}
                                            value={topGroupRankMode}
                                            onChange={setTopGroupRankMode}
                                            style={{ minWidth: 210 }}
                                            options={[
                                                {
                                                    value: 'failureRate',
                                                    label: getI18n('eim-v2-top-groups-rank-by-failure', 'Rank by failure rate', intl),
                                                },
                                                {
                                                    value: 'overdueRate',
                                                    label: getI18n('eim-v2-top-groups-rank-by-overdue', 'Rank by overdue rate', intl),
                                                },
                                            ]}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {topGroupRankMode === 'failureRate'
                                            ? getI18n('eim-v2-top-groups-subtitle-failure-rank', 'Ranked by failure rate (highest first). Values shown: Failure % and Overdue %.', intl)
                                            : getI18n('eim-v2-top-groups-subtitle-overdue-rank', 'Ranked by overdue rate (highest first). Values shown: Failure % and Overdue %.', intl)}
                                    </span>
                                    {v2TopImpactedGroups.length ? (
                                        <div className={dashboardStyles.v2GroupRows}>
                                            {v2TopImpactedGroups.slice(0, 5).map((group, index) => (
                                                <button
                                                    key={`${group.groupName}-${index}`}
                                                    type="button"
                                                    className={classNames(dashboardStyles.v2GroupRow, dashboardStyles.v2GroupRowButton)}
                                                    onClick={() => openOperationalViewForRiskEntry({ groupName: group.groupName })}
                                                >
                                                    <span>{group.groupName}</span>
                                                    <span>{formatPercent(group.failureRate ?? 0)}</span>
                                                    <span>{formatPercent(getGroupOverdueRate(group))}</span>
                                                    <ArrowRightOutlined className={dashboardStyles.v2RowActionIcon} aria-hidden="true" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-v2-no-groups', 'No impacted group data available.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-top-failure-reasons-title', 'Top 10 failure reasons', intl)}
                                    extra={(
                                        <Select<TopGroupRankMode>
                                            size={'small'}
                                            value={topFailureReasonRankMode}
                                            onChange={setTopFailureReasonRankMode}
                                            style={{ minWidth: 210 }}
                                            options={[
                                                {
                                                    value: 'failureRate',
                                                    label: getI18n('eim-top-failure-reasons-rank-by-failure', 'Rank by failure rate', intl),
                                                },
                                                {
                                                    value: 'overdueRate',
                                                    label: getI18n('eim-top-failure-reasons-rank-by-overdue', 'Rank by overdue rate', intl),
                                                },
                                            ]}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {topFailureReasonRankMode === 'failureRate'
                                            ? getI18n('eim-top-failure-reasons-subtitle-failure-rank', 'Ranked by failure rate (highest first). Values shown: Failure % and Overdue %.', intl)
                                            : getI18n('eim-top-failure-reasons-subtitle-overdue-rank', 'Ranked by overdue rate (highest first). Values shown: Failure % and Overdue %.', intl)}
                                    </span>
                                    {topFailureReasons.length ? (
                                        <div className={dashboardStyles.v2GroupRows}>
                                            {rankedTopFailureReasons.slice(0, 10).map((item, index) => (
                                                <button
                                                    key={`${item.reason ?? 'unknown'}-${index}`}
                                                    type="button"
                                                    className={classNames(dashboardStyles.v2GroupRow, dashboardStyles.v2GroupRowButton)}
                                                    onClick={() => openOperationalViewForRiskEntry({ statusFilters: ['FAIL', 'OVERDUE'] })}
                                                >
                                                    <span>{item.reason ?? getI18n('eim-country-details-error-unknown', 'Unknown', intl)}</span>
                                                    <span>{formatPercent(item.failureRate ?? 0)}</span>
                                                    <span>{formatPercent(getGroupOverdueRate(item))}</span>
                                                    <ArrowRightOutlined className={dashboardStyles.v2RowActionIcon} aria-hidden="true" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-top-failure-reasons-empty', 'No failure reason data available for the selected period.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-top-failure-processes-title', 'Top failure processes', intl)}
                                    extra={(
                                        <Select<TopGroupRankMode>
                                            size={'small'}
                                            value={topFailureProcessRankMode}
                                            onChange={setTopFailureProcessRankMode}
                                            style={{ minWidth: 210 }}
                                            options={[
                                                {
                                                    value: 'failureRate',
                                                    label: getI18n('eim-top-failure-processes-rank-by-failure', 'Rank by failure rate', intl),
                                                },
                                                {
                                                    value: 'overdueRate',
                                                    label: getI18n('eim-top-failure-processes-rank-by-overdue', 'Rank by overdue rate', intl),
                                                },
                                            ]}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {topFailureProcessRankMode === 'failureRate'
                                            ? getI18n('eim-top-failure-processes-subtitle-failure-rank', 'Ranked by failure rate (highest first). Values shown: Failure % and Overdue %.', intl)
                                            : getI18n('eim-top-failure-processes-subtitle-overdue-rank', 'Ranked by overdue rate (highest first). Values shown: Failure % and Overdue %.', intl)}
                                    </span>
                                    {topFailureProcesses.length ? (
                                        <div className={dashboardStyles.v2GroupRows}>
                                            {rankedTopFailureProcesses.slice(0, 10).map((item, index) => (
                                                <button
                                                    key={`${item.process ?? 'unknown'}-${index}`}
                                                    type="button"
                                                    className={classNames(dashboardStyles.v2GroupRow, dashboardStyles.v2GroupRowButton)}
                                                    onClick={() => openOperationalViewForRiskEntry({ process: item.process })}
                                                >
                                                    <span>{item.process ?? getI18n('eim-country-details-error-unknown', 'Unknown', intl)}</span>
                                                    <span>{formatPercent(item.failureRate ?? 0)}</span>
                                                    <span>{formatPercent(getGroupOverdueRate({ overdueCount: item.overdueCount, volume: item.totalCount }))}</span>
                                                    <ArrowRightOutlined className={dashboardStyles.v2RowActionIcon} aria-hidden="true" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-top-failure-processes-empty', 'No failure process data available for the selected period.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={dashboardStyles.chartCard}>
                                <Card
                                    bordered={false}
                                    size={'small'}
                                    title={getI18n('eim-top-failure-company-codes-title', 'Top 15 failure country and company codes', intl)}
                                    extra={(
                                        <Select<TopGroupRankMode>
                                            size={'small'}
                                            value={topFailureCompanyCodeRankMode}
                                            onChange={setTopFailureCompanyCodeRankMode}
                                            style={{ minWidth: 210 }}
                                            options={[
                                                {
                                                    value: 'failureRate',
                                                    label: getI18n('eim-top-failure-company-codes-rank-by-failure', 'Rank by failure rate', intl),
                                                },
                                                {
                                                    value: 'overdueRate',
                                                    label: getI18n('eim-top-failure-company-codes-rank-by-overdue', 'Rank by overdue rate', intl),
                                                },
                                            ]}
                                        />
                                    )}
                                >
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {topFailureCompanyCodeRankMode === 'failureRate'
                                            ? getI18n('eim-top-failure-company-codes-subtitle-failure-rank', 'Ranked by failure rate (highest first). Values shown: Failure % and Overdue % by country and company code.', intl)
                                            : getI18n('eim-top-failure-company-codes-subtitle-overdue-rank', 'Ranked by overdue rate (highest first). Values shown: Failure % and Overdue % by country and company code.', intl)}
                                    </span>
                                    {topFailureCompanyCodes.length ? (
                                        <div className={dashboardStyles.v2GroupRows}>
                                            {rankedTopFailureCompanyCodes.slice(0, 15).map((item, index) => (
                                                <button
                                                    key={`${item.country ?? 'unknown'}-${item.companyCode ?? 'unknown'}-${index}`}
                                                    type="button"
                                                    className={classNames(dashboardStyles.v2GroupRow, dashboardStyles.v2GroupRowButton)}
                                                    onClick={() => openOperationalViewForRiskEntry({ country: item.country, companyCode: item.companyCode })}
                                                >
                                                    <CountryFlagLabel
                                                        countryCode={resolveCountryCode(item, companyCountryLookup)}
                                                        label={formatCountryCompanyLabelFromUtils(item, companyCountryLookup, regionNames)}
                                                    />
                                                    <span>{formatPercent(item.failureRate ?? 0)}</span>
                                                    <span>{formatPercent(getGroupOverdueRate({ overdueCount: item.overdueCount, volume: item.volume }))}</span>
                                                    <ArrowRightOutlined className={dashboardStyles.v2RowActionIcon} aria-hidden="true" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmpty}>{getI18n('eim-top-failure-company-codes-empty', 'No failure country and company code data available for the selected period.', intl)}</div>
                                    )}
                                </Card>
                            </div>

                            <div className={classNames(dashboardStyles.chartCard, dashboardStyles.chartCardWide)}>
                                <Card bordered={false} size={'small'} title={getI18n('eim-country-metrics-title', 'Country and company code metrics', intl)}>
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-country-metrics-subtitle', 'Snapshot of total invoices, successful, failed, and in transit by country and company code for the selected period.', intl)}
                                    </span>

                                    <div className={dashboardStyles.metricSubsectionTitle}>{getI18n('eim-world-map-title', 'Global country volume map', intl)}</div>
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-world-map-subtitle', 'Visual distribution of invoice volume by country. Click a country to choose operational or country details.', intl)}
                                    </span>
                                    <div className={dashboardStyles.worldMapActionsRow}>
                                        <Button
                                            onClick={openWorldMapModal}
                                            disabled={!worldMapReady || worldMapData.length === 0}
                                            size={'small'}
                                        >
                                            {getI18n('eim-world-map-open-fullscreen', 'Open full map view', intl)}
                                        </Button>
                                    </div>
                                    <div className={dashboardStyles.worldMapLegendRow}>
                                        <span className={dashboardStyles.worldMapLegendItem}>
                                            <span className={classNames(dashboardStyles.worldMapLegendDot, dashboardStyles.worldMapLegendDotHealthy)} aria-hidden="true" />
                                            {getI18n('eim-world-map-health-healthy', 'Healthy', intl)}
                                        </span>
                                        <span className={dashboardStyles.worldMapLegendItem}>
                                            <span className={classNames(dashboardStyles.worldMapLegendDot, dashboardStyles.worldMapLegendDotWarning)} aria-hidden="true" />
                                            {getI18n('eim-world-map-health-warning', 'Warning', intl)}
                                        </span>
                                        <span className={dashboardStyles.worldMapLegendItem}>
                                            <span className={classNames(dashboardStyles.worldMapLegendDot, dashboardStyles.worldMapLegendDotCritical)} aria-hidden="true" />
                                            {getI18n('eim-world-map-health-critical', 'Critical', intl)}
                                        </span>
                                        <span className={dashboardStyles.worldMapLegendItem}>
                                            <span className={classNames(dashboardStyles.worldMapLegendDot, dashboardStyles.worldMapLegendDotNoData)} aria-hidden="true" />
                                            {getI18n('eim-world-map-health-no-data', 'No data', intl)}
                                        </span>
                                    </div>
                                    {worldMapReady && worldMapData.length > 0 ? (
                                        <div className={dashboardStyles.worldMapCanvasShell}>
                                            <ReactEcharts
                                                option={worldMapOptions}
                                                onEvents={worldMapEvents}
                                                className={dashboardStyles.worldMapInlineCanvas}
                                                notMerge={true}
                                                lazyUpdate={true}
                                            />
                                            <aside className={dashboardStyles.worldMapLiveTrafficCard} aria-label={getI18n('eim-world-map-live-traffic-title', 'Live traffic', intl)}>
                                                <div className={dashboardStyles.worldMapLiveTrafficTitle}>{getI18n('eim-world-map-live-traffic-title', 'Live traffic', intl)}</div>
                                                <div className={dashboardStyles.worldMapLiveTrafficWindow}>{getI18n('eim-world-map-live-traffic-window', 'Last 5 minutes', intl)}</div>
                                                <div className={dashboardStyles.worldMapLiveTrafficValue}>{formatCount(worldMapLiveTraffic.lastFiveMinutesCount)}</div>
                                                <div className={dashboardStyles.worldMapLiveTrafficMetric}>{getI18n('eim-world-map-live-traffic-metric', 'Invoices in transit', intl)}</div>
                                                <div className={dashboardStyles.worldMapLiveTrafficBars} aria-hidden="true">
                                                    {worldMapLiveTraffic.bars.length > 0 ? worldMapLiveTraffic.bars.map((bar) => (
                                                        <span
                                                            key={bar.key}
                                                            className={classNames(
                                                                dashboardStyles.worldMapLiveTrafficBar,
                                                                dashboardStyles[`worldMapLiveTrafficBarLevel${bar.level}`],
                                                                getLiveTrafficBarToneClassName(bar.tone),
                                                            )}
                                                        />
                                                    )) : (
                                                        <span className={classNames(
                                                            dashboardStyles.worldMapLiveTrafficBar,
                                                            dashboardStyles.worldMapLiveTrafficBarLevel2,
                                                            dashboardStyles.worldMapLiveTrafficBarPositive,
                                                        )} />
                                                    )}
                                                </div>
                                            </aside>
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmptyCompact}>
                                            {worldMapLoadFailed
                                                ? getI18n('eim-world-map-load-error', 'World map could not be loaded in this environment.', intl)
                                                : getI18n('eim-world-map-loading', 'Loading world map...', intl)}
                                        </div>
                                    )}

                                    {countryMetricRows.length ? (
                                        <div className={dashboardStyles.countryMetricsGrid}>
                                            {countryMetricRows.map((countryMetric) => {
                                                const totalInvoices = countryMetric.intake?.totalProcessed ?? 0;
                                                const failedCount = (countryMetric.exceptions?.failedCount ?? 0) + (countryMetric.exceptions?.overdueCount ?? 0);
                                                const inTransitCount = Math.max(0, countryMetric.processing?.inTransitCount ?? 0);
                                                const successfulCount = Math.max(0, totalInvoices - failedCount - inTransitCount);

                                                const successTone = getGroupSuccessTone(successfulCount, totalInvoices);
                                                const failedTone = getGroupFailedTone(failedCount, totalInvoices);
                                                const inTransitTone = getGroupInTransitTone(inTransitCount, totalInvoices);

                                                return (
                                                    <div key={`${countryMetric.country ?? 'Unknown'}-${countryMetric.companyCode ?? 'Unknown'}`} className={dashboardStyles.countryMetricCard}>
                                                        <div className={dashboardStyles.countryMetricCardHeader}>
                                                            <button
                                                                type="button"
                                                                className={dashboardStyles.countryMetricHeaderAction}
                                                                onClick={() => openCountryActionModal({
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                })}
                                                            >
                                                                <CountryFlagLabel
                                                                    countryCode={resolveCountryCode(countryMetric, companyCountryLookup)}
                                                                    label={formatCountryCompanyLabelFromUtils(countryMetric, companyCountryLookup, regionNames)}
                                                                    className={dashboardStyles.countryName}
                                                                />
                                                            </button>
                                                        </div>
                                                        <div className={classNames(dashboardStyles.countryMetricBlocks, dashboardStyles.countryMetricBlocksCompact)}>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerTopLeft,
                                                                    dashboardStyles.metricTileNeutral,
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'total',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'total',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-total-invoices', 'Total Invoices', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(totalInvoices)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(totalInvoices)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerTopRight,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: successTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: successTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: successTone === 'warning',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'successful',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'successful',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-successful-label', 'Successful', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(successfulCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(successfulCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(successfulCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerBottomLeft,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: inTransitTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: inTransitTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: inTransitTone === 'warning',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'inTransit',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'inTransit',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-in-transit-label', 'In Transit', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(inTransitCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(inTransitCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{getI18n('eim-items-text', 'Items', intl)}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerBottomRight,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: failedTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: failedTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: failedTone === 'warning',
                                                                        [dashboardStyles.metricTileCritical]: failedTone === 'critical',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'failed',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'failed',
                                                                    country: countryMetric.country,
                                                                    companyCode: countryMetric.companyCode,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-failed-label', 'Failed', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(failedCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(failedCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(failedCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmptyCompact}>{getI18n('eim-country-metrics-empty', 'No country and company code metrics available for the selected period.', intl)}</div>
                                    )}

                                    <div className={dashboardStyles.metricSubsectionTitle}>{getI18n('eim-system-metrics-title', 'System metrics', intl)}</div>
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-system-metrics-subtitle', 'Snapshot of total invoices, successful, failed, and in transit by system for the selected period.', intl)}
                                    </span>
                                    {systemMetricRows.length ? (
                                        <div className={dashboardStyles.countryMetricsGrid}>
                                            {systemMetricRows.map((systemMetric, index) => {
                                                const totalInvoices = systemMetric.intake?.totalProcessed ?? 0;
                                                const failedCount = (systemMetric.exceptions?.failedCount ?? 0) + (systemMetric.exceptions?.overdueCount ?? 0);
                                                const inTransitCount = Math.max(0, systemMetric.processing?.inTransitCount ?? 0);
                                                const successfulCount = Math.max(0, totalInvoices - failedCount - inTransitCount);

                                                const successTone = getGroupSuccessTone(successfulCount, totalInvoices);
                                                const failedTone = getGroupFailedTone(failedCount, totalInvoices);
                                                const inTransitTone = getGroupInTransitTone(inTransitCount, totalInvoices);

                                                return (
                                                    <div key={`${systemMetric.systemId ?? 'unknown'}-${index}`} className={dashboardStyles.countryMetricCard}>
                                                        <div className={dashboardStyles.countryMetricCardHeader}>
                                                            <span className={dashboardStyles.countryName}>{systemMetric.systemLabel ?? getI18n('eim-country-details-error-unknown', 'Unknown', intl)}</span>
                                                        </div>
                                                        <div className={classNames(dashboardStyles.countryMetricBlocks, dashboardStyles.countryMetricBlocksCompact)}>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerTopLeft,
                                                                    dashboardStyles.metricTileNeutral,
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'total',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'total',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-total-invoices', 'Total Invoices', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(totalInvoices)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(totalInvoices)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerTopRight,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: successTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: successTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: successTone === 'warning',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'successful',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'successful',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-successful-label', 'Successful', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(successfulCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(successfulCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(successfulCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerBottomLeft,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: inTransitTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: inTransitTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: inTransitTone === 'warning',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'inTransit',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'inTransit',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-in-transit-label', 'In Transit', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(inTransitCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(inTransitCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{getI18n('eim-items-text', 'Items', intl)}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(
                                                                    dashboardStyles.countryMetricBlock,
                                                                    dashboardStyles.countryMetricBlockCornerBottomRight,
                                                                    {
                                                                        [dashboardStyles.metricTileNeutral]: failedTone === 'neutral',
                                                                        [dashboardStyles.metricTilePositive]: failedTone === 'positive',
                                                                        [dashboardStyles.metricTileWarning]: failedTone === 'warning',
                                                                        [dashboardStyles.metricTileCritical]: failedTone === 'critical',
                                                                    }
                                                                )}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({
                                                                    status: 'failed',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({
                                                                    status: 'failed',
                                                                    systemLabel: systemMetric.systemLabel,
                                                                }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-failed-label', 'Failed', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(failedCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? (
                                                                        <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">
                                                                            {formatCount(failedCount)}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(failedCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmptyCompact}>{getI18n('eim-system-metrics-empty', 'No system metrics available for the selected period.', intl)}</div>
                                    )}

                                    <div className={dashboardStyles.metricSubsectionTitle}>{getI18n('eim-group-metrics-title', 'Impacted Group metrics', intl)}</div>
                                    <span className={dashboardStyles.chartSubtitle}>
                                        {getI18n('eim-group-metrics-subtitle', 'Snapshot of invoice counts by impacted groups for the selected period.', intl)}
                                    </span>
                                    {groupMetrics.length ? (
                                        <div className={dashboardStyles.countryMetricsGrid}>
                                            {groupMetrics.map((groupMetric, index) => {
                                                const totalInvoices = groupMetric.totalInvoices ?? 0;
                                                const successfulCount = groupMetric.successfulCount ?? 0;
                                                const inTransitCount = groupMetric.inTransitCount ?? 0;
                                                const failedCount = groupMetric.failedCount ?? 0;
                                                const successTone = getGroupSuccessTone(successfulCount, totalInvoices);
                                                const failedTone = getGroupFailedTone(failedCount, totalInvoices);
                                                const inTransitTone = getGroupInTransitTone(inTransitCount, totalInvoices);
                                                return (
                                                    <div key={`${groupMetric.groupName ?? 'unknown'}-${index}`} className={dashboardStyles.countryMetricCard}>
                                                        <div className={dashboardStyles.countryMetricCardHeader}>
                                                            <span className={dashboardStyles.countryName}>{groupMetric.groupName ?? 'Unknown'}</span>
                                                        </div>
                                                        <div className={classNames(dashboardStyles.countryMetricBlocks, dashboardStyles.countryMetricBlocksCompact)}>
                                                            <div
                                                                className={classNames(dashboardStyles.countryMetricBlock, dashboardStyles.countryMetricBlockCornerTopLeft, dashboardStyles.metricTileNeutral)}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({ status: 'total', groupName: groupMetric.groupName })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({ status: 'total', groupName: groupMetric.groupName }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-total-invoices', 'Total Invoices', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(totalInvoices)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">{formatCount(totalInvoices)}</span> : null}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={classNames(dashboardStyles.countryMetricBlock, dashboardStyles.countryMetricBlockCornerTopRight, {[dashboardStyles.metricTileNeutral]: successTone === 'neutral', [dashboardStyles.metricTilePositive]: successTone === 'positive', [dashboardStyles.metricTileWarning]: successTone === 'warning'})}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({ status: 'successful', groupName: groupMetric.groupName })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({ status: 'successful', groupName: groupMetric.groupName }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-successful-label', 'Successful', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(successfulCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">{formatCount(successfulCount)}</span> : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(successfulCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(dashboardStyles.countryMetricBlock, dashboardStyles.countryMetricBlockCornerBottomLeft, {[dashboardStyles.metricTileNeutral]: inTransitTone === 'neutral', [dashboardStyles.metricTilePositive]: inTransitTone === 'positive', [dashboardStyles.metricTileWarning]: inTransitTone === 'warning'})}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({ status: 'inTransit', groupName: groupMetric.groupName })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({ status: 'inTransit', groupName: groupMetric.groupName }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-in-transit-label', 'In Transit', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(inTransitCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">{formatCount(inTransitCount)}</span> : null}
                                                                </span>
                                                                <span>{getI18n('eim-items-text', 'Items', intl)}</span>
                                                            </div>
                                                            <div
                                                                className={classNames(dashboardStyles.countryMetricBlock, dashboardStyles.countryMetricBlockCornerBottomRight, {[dashboardStyles.metricTileNeutral]: failedTone === 'neutral', [dashboardStyles.metricTilePositive]: failedTone === 'positive', [dashboardStyles.metricTileWarning]: failedTone === 'warning', [dashboardStyles.metricTileCritical]: failedTone === 'critical'})}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => openOperationalViewForMetricTile({ status: 'failed', groupName: groupMetric.groupName })}
                                                                onKeyDown={(event) => handleMetricTileKeyDown(event, () => openOperationalViewForMetricTile({ status: 'failed', groupName: groupMetric.groupName }))}
                                                            >
                                                                <span className={dashboardStyles.countryMetricLabel}>{getI18n('eim-failed-label', 'Failed', intl)}</span>
                                                                <span className={dashboardStyles.countryMetricValueWrap}>
                                                                    <strong>{formatCount(failedCount)}</strong>
                                                                    {thresholdConfig.enableValueZoom ? <span className={dashboardStyles.countryMetricValueZoomPopup} aria-hidden="true">{formatCount(failedCount)}</span> : null}
                                                                </span>
                                                                <span>{totalInvoices > 0 ? `${(failedCount * 100 / totalInvoices).toFixed(1)}%` : '0%'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className={dashboardStyles.chartEmptyCompact}>{getI18n('eim-group-metrics-empty', 'No impacted group metrics available for the selected period.', intl)}</div>
                                    )}
                                </Card>
                            </div>
                        </section>
                    ) : null}

                    <section className={dashboardStyles.chartGrid}>
                        <div className={dashboardStyles.chartCard}>
                            <Card
                                bordered={false}
                                size={'small'}
                                title={activePartnersTitle}
                                extra={getI18n('eim-partner-duplicates-note', 'Total may include duplicates where a partner is active in AP and AR', intl)}
                            >
                                <span className={dashboardStyles.chartSubtitle}>
                                    {getI18n('eim-partner-coverage-description', 'Executive view of partner coverage concentration across BMW groups.', intl)}
                                </span>
                                {activePartnerDataGraphOption ? (
                                    <ReactEcharts className={dashboardStyles.chartCanvas} option={activePartnerDataGraphOption} />
                                ) : (
                                    <div className={dashboardStyles.chartEmpty}>{getI18n('eim-no-partner-data', 'No partner coverage data available.', intl)}</div>
                                )}
                            </Card>
                        </div>

                        <div className={dashboardStyles.chartCard}>
                            <Card bordered={false} size={'small'} title={getI18n('eim-monthly-partners', 'Monthly active partners', intl)}>
                                <span className={dashboardStyles.chartSubtitle}>
                                    {getI18n('eim-monthly-trend-description', 'Twelve-month trend showing network activity momentum over time.', intl)}
                                </span>
                                {graphData ? (
                                    <ReactEcharts className={dashboardStyles.chartCanvas} option={graphData} />
                                ) : (
                                    <div className={dashboardStyles.chartEmpty}>{getI18n('eim-no-monthly-data', 'No monthly partner trend data available.', intl)}</div>
                                )}
                            </Card>
                        </div>
                    </section>
                </div>
            </div>

            <Modal
                title={getI18n('eim-country-action-modal-title', 'Choose country view', intl)}
                open={Boolean(countryActionSelection)}
                footer={null}
                className={dashboardStyles.countryActionModal}
                onCancel={closeCountryActionModal}
                destroyOnClose={true}
            >
                <div className={dashboardStyles.countryActionModalBody}>
                    <p className={dashboardStyles.countryActionModalDescription}>
                        {getI18n('eim-country-action-modal-description', 'Select how you want to open details for this country.', intl)}
                    </p>
                    <div className={dashboardStyles.countryActionModalButtons}>
                        <Button type="primary" onClick={handleOpenCountryOperationalDetails}>
                            {getI18n('eim-country-action-view-operational', 'View Operational Details', intl)}
                        </Button>
                        <Button onClick={handleOpenCountryDetails}>
                            {getI18n('eim-country-action-view-country', 'View Country Details', intl)}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                title={null}
                open={Boolean(countryDetailsSelection)}
                footer={null}
                className={dashboardStyles.countryDetailsModal}
                onCancel={closeCountryDetailsModal}
                afterOpenChange={(open) => setCountryDetailsTrendChartReady(open)}
                destroyOnClose={true}
            >
                <div className={dashboardStyles.countryDetailsModalBody}>
                    <div className={dashboardStyles.countryDetailsTopRow}>
                        <Button type="link" className={dashboardStyles.countryDetailsBackButton} icon={<ArrowLeftOutlined />} onClick={closeCountryDetailsModal}>
                            {getI18n('eim-country-details-back', 'Back', intl)}
                        </Button>
                    </div>

                    <div className={dashboardStyles.countryDetailsHeaderRow}> 
                        <div>
                            <div className={dashboardStyles.countryDetailsCountryRow}>
                                <span className={dashboardStyles.countryDetailsFlagWrap}>
                                    {countryDetailsSelection?.country ? (
                                        <img
                                            src={getCountryFlagUrl(countryDetailsSelection.country)}
                                            alt={countryDetailsSelection.country}
                                            width="28"
                                            height="28"
                                            className={dashboardStyles.countryDetailsFlag}
                                        />
                                    ) : null}
                                </span>
                                <span className={dashboardStyles.countryDetailsTitle}>
                                    {countryDetailsSelection ? (
                                        <CountryFlagLabel
                                            countryCode={resolveCountryCode(countryDetailsSelection, companyCountryLookup)}
                                            label={formatCountryCompanyLabelFromUtils(countryDetailsSelection, companyCountryLookup, regionNames)}
                                        />
                                    ) : '-'}
                                </span>
                                <span className={classNames(
                                    dashboardStyles.countryDetailsHealthPill,
                                    {
                                        [dashboardStyles.countryDetailsHealthHealthy]: countryDetailsHealthCategory === 'healthy',
                                        [dashboardStyles.countryDetailsHealthWarning]: countryDetailsHealthCategory === 'warning',
                                        [dashboardStyles.countryDetailsHealthCritical]: countryDetailsHealthCategory === 'critical',
                                        [dashboardStyles.countryDetailsHealthNoData]: countryDetailsHealthCategory === 'noData',
                                    }
                                )}>
                                    {countryDetailsHealthCategory === 'healthy' && getI18n('eim-world-map-health-healthy', 'Healthy', intl)}
                                    {countryDetailsHealthCategory === 'warning' && getI18n('eim-world-map-health-warning', 'Warning', intl)}
                                    {countryDetailsHealthCategory === 'critical' && getI18n('eim-world-map-health-critical', 'Critical', intl)}
                                    {countryDetailsHealthCategory === 'noData' && getI18n('eim-world-map-health-no-data', 'No data', intl)}
                                </span>
                            </div>
                            <div className={dashboardStyles.countryDetailsSubtitle}>
                                {getI18n('eim-last-updated', 'Last updated', intl)}: {dayjs().format('HH:mm:ss')} UTC
                            </div>
                        </div>
                    </div>

                    <div className={dashboardStyles.countryDetailsStatsGrid}>
                        <div className={dashboardStyles.countryDetailsStatCard}>
                            <span>{getI18n('eim-country-details-invoices-today', 'INVOICES TODAY', intl)}</span>
                            <strong>{formatCount(countryDetailsSnapshot.totalInvoices)}</strong>
                            <small>{countryDetailsDeltaView.volumeDeltaText} {getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}</small>
                        </div>
                        <div className={dashboardStyles.countryDetailsStatCard}>
                            <span>{getI18n('eim-country-details-success-rate', 'SUCCESS RATE', intl)}</span>
                            <strong>{formatPercent(countryDetailsSnapshot.successRate)}</strong>
                            <small>{countryDetailsDeltaView.stpDeltaText} {getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}</small>
                        </div>
                        <div className={dashboardStyles.countryDetailsStatCard}>
                            <span>{getI18n('eim-country-details-avg-processing-time', 'AVG. PROCESSING TIME', intl)}</span>
                            <strong>{intl.formatNumber(countryDetailsSnapshot.avgTransitDays, { maximumFractionDigits: 0 })} sec</strong>
                            <small>{countryDetailsDeltaView.transitDeltaText} {getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}</small>
                        </div>
                        <div className={dashboardStyles.countryDetailsStatCard}>
                            <span>{getI18n('eim-country-details-error-rate', 'ERROR RATE', intl)}</span>
                            <strong>{formatPercent(countryDetailsSnapshot.errorRate)}</strong>
                            <small>{countryDetailsDeltaView.errorDeltaText} {getI18n('eim-v2-vs-yesterday', 'vs yesterday', intl)}</small>
                        </div>
                    </div>

                    <div className={dashboardStyles.countryDetailsTrendCard}>
                        <div className={dashboardStyles.countryDetailsPanelTitle}>{getI18n('eim-country-details-trend-title', 'Processing Trend (Last 24h)', intl)}</div>
                        {countryDetailsTrendChartReady ? (
                            <ReactEcharts className={dashboardStyles.countryDetailsTrendEchart} option={countryDetailsTrendChartOption} />
                        ) : (
                            <div className={dashboardStyles.countryDetailsTrendEchartPlaceholder} />
                        )}
                        <div className={dashboardStyles.countryDetailsTrendLegend}>
                            <span><i className={dashboardStyles.countryDetailsLegendDotSuccess} />{getI18n('eim-successful-label', 'Successful', intl)}</span>
                            <span><i className={dashboardStyles.countryDetailsLegendDotFailed} />{getI18n('eim-failed-label', 'Failed', intl)}</span>
                        </div>
                    </div>

                    <div className={dashboardStyles.countryDetailsBottomGrid}>
                        <div className={dashboardStyles.countryDetailsBreakdownCard}>
                            <div className={dashboardStyles.countryDetailsPanelTitle}>{getI18n('eim-country-details-errors-by-type', 'Errors by Type', intl)}</div>
                            <div className={dashboardStyles.countryDetailsErrorAnalysis}>
                                <div className={dashboardStyles.countryDetailsErrorDonutWrap}>
                                    <svg viewBox="0 0 84 84" className={dashboardStyles.countryDetailsErrorDonutSvg} aria-hidden="true">
                                        <circle className={dashboardStyles.countryDetailsErrorDonutTrack} cx="42" cy="42" r="34" />
                                        {countryDetailsErrorDonut.map((item) => (
                                            <circle
                                                key={item.label}
                                                cx="42"
                                                cy="42"
                                                r="34"
                                                strokeDasharray={item.dashArray}
                                                strokeDashoffset={item.dashOffset}
                                                className={classNames(
                                                    dashboardStyles.countryDetailsErrorDonutSegment,
                                                    {
                                                        [dashboardStyles.countryDetailsErrorDonutSegmentValidation]: item.tone === 'validation',
                                                        [dashboardStyles.countryDetailsErrorDonutSegmentPartner]: item.tone === 'partner',
                                                        [dashboardStyles.countryDetailsErrorDonutSegmentTimeout]: item.tone === 'timeout',
                                                        [dashboardStyles.countryDetailsErrorDonutSegmentSystem]: item.tone === 'system',
                                                    }
                                                )}
                                            />
                                        ))}
                                    </svg>
                                    <div className={dashboardStyles.countryDetailsErrorDonutCenter}>
                                        <strong>{formatCount(countryDetailsErrorTotal)}</strong>
                                        <span>{getI18n('eim-country-details-total', 'Total', intl)}</span>
                                    </div>
                                </div>
                                <div className={dashboardStyles.countryDetailsErrorLegend}>
                                    {countryDetailsErrorBreakdown.map((item) => (
                                        <div key={item.label} className={dashboardStyles.countryDetailsErrorRow}>
                                            <span className={dashboardStyles.countryDetailsErrorLabel}>
                                                <span className={dashboardStyles.countryDetailsErrorDot} aria-hidden="true" />
                                                {item.label}
                                            </span>
                                            <span className={dashboardStyles.countryDetailsErrorMeta}>{formatCount(item.count)} ({item.percent}%)</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={dashboardStyles.countryDetailsBreakdownCard}>
                            <div className={dashboardStyles.countryDetailsPanelTitle}>{getI18n('eim-country-details-top-partners', 'Top Partners (by Volume)', intl)}</div>
                            <div className={dashboardStyles.countryDetailsPartnerList}>
                                {countryDetailsTopPartners.map((partner, index) => (
                                    <div key={`${partner.partner}-${index}`} className={dashboardStyles.countryDetailsPartnerRow}>
                                        <span>
                                            {index + 1}. {getPartnerDisplayLabel(partner.partner, false)}
                                            {getPartnerSystemCode(partner.partner) ? ` | ${getPartnerSystemCode(partner.partner)}` : ''}
                                        </span>
                                        <span>{formatCount(partner.volume)}</span>
                                        <span className={dashboardStyles.countryDetailsPartnerRate}>{formatPercent(partner.successRate)}</span>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className={dashboardStyles.countryDetailsPartnersLink} onClick={openAllPartnersModal}>
                                {getI18n('eim-country-details-view-all-partners', 'View all partners', intl)}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                title={getI18n('eim-country-details-all-partners-modal-title', 'All Partners by Volume (All Countries)', intl)}
                open={allPartnersModalOpen}
                footer={null}
                width={780}
                className={dashboardStyles.countryDetailsAllPartnersModal}
                onCancel={closeAllPartnersModal}
                destroyOnClose={true}
            >
                <div className={dashboardStyles.countryDetailsAllPartnersBody}>
                    <div className={dashboardStyles.countryDetailsAllPartnersHeader}>
                        <span className={dashboardStyles.countryDetailsAllPartnersRankCell}>#</span>
                        <span className={dashboardStyles.countryDetailsAllPartnersPartnerCell}>{getI18n('eim-country-details-all-partners-partner', 'Partner', intl)}</span>
                        <button
                            type="button"
                            className={classNames(
                                dashboardStyles.countryDetailsAllPartnersSortButton,
                                dashboardStyles.countryDetailsAllPartnersNumericHeader,
                                {
                                    [dashboardStyles.countryDetailsAllPartnersSortButtonActive]: allPartnersSortKey === 'volume',
                                }
                            )}
                            onClick={() => handleAllPartnersSort('volume')}
                        >
                            {getI18n('eim-country-details-all-partners-volume', 'Volume', intl)}
                            {allPartnersVolumeSortIndicator}
                        </button>
                        <button
                            type="button"
                            className={classNames(
                                dashboardStyles.countryDetailsAllPartnersSortButton,
                                dashboardStyles.countryDetailsAllPartnersNumericHeader,
                                {
                                    [dashboardStyles.countryDetailsAllPartnersSortButtonActive]: allPartnersSortKey === 'successRate',
                                }
                            )}
                            onClick={() => handleAllPartnersSort('successRate')}
                        >
                            {getI18n('eim-country-details-all-partners-success-rate', 'Success rate', intl)}
                            {allPartnersSuccessRateSortIndicator}
                        </button>
                    </div>
                    <div className={dashboardStyles.countryDetailsAllPartnersList}>
                        {countryDetailsAllPartnersSorted.length > 0 ? countryDetailsAllPartnersSorted.map((partner, index) => (
                            <div key={`${partner.country}-${partner.partner}-${index}`} className={dashboardStyles.countryDetailsAllPartnersRow}>
                                <span className={dashboardStyles.countryDetailsAllPartnersRankCell}>{index + 1}</span>
                                <span className={dashboardStyles.countryDetailsAllPartnersPartnerCell}>{partner.partner}</span>
                                <span className={dashboardStyles.countryDetailsAllPartnersNumericCell}>{formatCount(partner.volume)}</span>
                                <span className={classNames(dashboardStyles.countryDetailsAllPartnersNumericCell, dashboardStyles.countryDetailsPartnerRate)}>{formatPercent(partner.successRate)}</span>
                            </div>
                        )) : (
                            <div className={dashboardStyles.countryDetailsAllPartnersEmpty}>
                                {getI18n('eim-country-details-all-partners-empty', 'No partner volume data available.', intl)}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                title={getI18n('eim-world-map-modal-title', 'Global country volume map (full view)', intl)}
                open={worldMapModalOpen}
                footer={null}
                width={'100vw'}
                className={dashboardStyles.worldMapModal}
                destroyOnClose={true}
                style={{ maxWidth: '100vw', top: 0, paddingBottom: 0 }}
                onCancel={() => setWorldMapModalOpen(false)}
            >
                {worldMapReady && worldMapData.length > 0 ? (
                    <ReactEcharts
                        key={`modal-chart-${worldMapModalOpen}`}
                        ref={worldMapModalChartRef}
                        option={worldMapModalOptions}
                        onEvents={worldMapEvents}
                        className={dashboardStyles.worldMapModalCanvas}
                        notMerge={true}
                        lazyUpdate={true}
                    />
                ) : (
                    <div className={dashboardStyles.chartEmptyCompact}>
                        {worldMapLoadFailed
                            ? getI18n('eim-world-map-load-error', 'World map could not be loaded in this environment.', intl)
                            : getI18n('eim-world-map-loading', 'Loading world map...', intl)}
                    </div>
                )}
            </Modal>

            <Modal
                title={(
                    <div className={dashboardStyles.channelBreakdownTitleBar}>
                        <span>{getI18n('eim-v2-channel-breakdown-title', 'Processing rates by channel (3 channels)', intl)}</span>
                        <div className={dashboardStyles.channelBreakdownZoomControls}>
                            <Button size="small" className={dashboardStyles.channelBreakdownZoomButton} onClick={decreaseChannelBreakdownZoom} disabled={channelBreakdownZoomLevel <= -20}>
                                −5
                            </Button>
                            <span className={dashboardStyles.channelBreakdownZoomLevel}>
                                {getChannelBreakdownZoomLabel(channelBreakdownZoomLevel)}
                            </span>
                            <Button size="small" className={dashboardStyles.channelBreakdownZoomButton} onClick={increaseChannelBreakdownZoom} disabled={channelBreakdownZoomLevel >= 20}>
                                +5
                            </Button>
                        </div>
                    </div>
                )}
                open={channelBreakdownModalOpen}
                footer={null}
                width={`calc(920px + ${channelBreakdownZoomLevel * 8}px)`}
                className={classNames(dashboardStyles.channelBreakdownModal, {
                    [dashboardStyles.channelBreakdownModalLight]: theme === 'light',
                    [dashboardStyles.channelBreakdownModalZoomed]: channelBreakdownZoomLevel !== 0,
                })}
                onCancel={() => {
                    setChannelBreakdownZoomLevel(0);
                    setChannelBreakdownModalOpen(false);
                }}
            >
                {channelMetricRows.length ? (
                    <div>
                        <div className={dashboardStyles.channelBreakdownSlaNote}>
                            {getI18n('eim-v2-channel-breakdown-sla-note', 'Transit SLA: EMAIL 2h, PORTAL 2h, EDI/QBQ 8h by default. Edit these values in Threshold configuration.', intl)
                                .replace('EMAIL 2h', `EMAIL ${thresholdConfig.emailTransitSlaHours}h`)
                                .replace('PORTAL 2h', `PORTAL ${thresholdConfig.portalTransitSlaHours}h`)
                                .replace('EDI/QBQ 8h', `EDI/QBQ ${thresholdConfig.ediTransitSlaHours}h`)}
                        </div>
                        <div className={dashboardStyles.channelBreakdownGrid}>
                        <div className={classNames(dashboardStyles.channelBreakdownRow, dashboardStyles.channelBreakdownHeader)}>
                            <span>{getI18n('eim-v2-channel-col', 'Channel', intl)}</span>
                            <span>{getI18n('eim-v2-total-col', 'Total', intl)}</span>
                            <span>{getI18n('eim-v2-stp-col', 'STP', intl)}</span>
                            <span>{getI18n('eim-v2-on-time-col', 'On-time', intl)}</span>
                            <span>{getI18n('eim-v2-transit-col', 'Transit', intl)}</span>
                            <span>{getI18n('eim-v2-failure-col', 'Failure', intl)}</span>
                            <span>{getI18n('eim-v2-overdue-col', 'Overdue', intl)}</span>
                        </div>
                        {channelMetricRows.map((channelMetric, index) => (
                            <div className={dashboardStyles.channelBreakdownRow} key={`${channelMetric.channel ?? 'Unknown'}-${index}`}>
                                <span className={dashboardStyles.channelBreakdownChannel}>{channelMetric.channel ?? 'Unknown'}</span>
                                <span className={dashboardStyles.channelBreakdownMetric}>{formatCount(channelMetric.intake?.totalProcessed ?? 0)}</span>
                                <span className={classNames(dashboardStyles.channelBreakdownMetric, {
                                    [dashboardStyles.channelBreakdownTonePositive]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.stpRate) === 'positive',
                                    [dashboardStyles.channelBreakdownToneWarning]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.stpRate) === 'warning',
                                    [dashboardStyles.channelBreakdownToneCritical]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.stpRate) === 'critical',
                                })}>{formatPercent(channelMetric.processing?.stpRate ?? 0)}</span>
                                <span className={classNames(dashboardStyles.channelBreakdownMetric, {
                                    [dashboardStyles.channelBreakdownTonePositive]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.onTimeRate) === 'positive',
                                    [dashboardStyles.channelBreakdownToneWarning]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.onTimeRate) === 'warning',
                                    [dashboardStyles.channelBreakdownToneCritical]: getCountryProcessingTone(thresholdConfig, channelMetric.processing?.onTimeRate) === 'critical',
                                })}>{formatPercent(channelMetric.processing?.onTimeRate ?? 0)}</span>
                                <span className={classNames(dashboardStyles.channelBreakdownMetric, {
                                    [dashboardStyles.channelBreakdownToneNeutral]: getChannelTransitTone(thresholdConfig, channelMetric.channel, channelMetric.processing?.avgTransitDays) === 'neutral',
                                    [dashboardStyles.channelBreakdownTonePositive]: getChannelTransitTone(thresholdConfig, channelMetric.channel, channelMetric.processing?.avgTransitDays) === 'positive',
                                    [dashboardStyles.channelBreakdownToneWarning]: getChannelTransitTone(thresholdConfig, channelMetric.channel, channelMetric.processing?.avgTransitDays) === 'warning',
                                    [dashboardStyles.channelBreakdownToneCritical]: getChannelTransitTone(thresholdConfig, channelMetric.channel, channelMetric.processing?.avgTransitDays) === 'critical',
                                })}>{`${intl.formatNumber(channelMetric.processing?.avgTransitDays ?? 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}d`}</span>
                                <span className={classNames(dashboardStyles.channelBreakdownMetric, {
                                    [dashboardStyles.channelBreakdownToneWarning]: getCountryExceptionsTone(thresholdConfig, channelMetric.exceptions?.failureRate) === 'warning',
                                    [dashboardStyles.channelBreakdownToneCritical]: getCountryExceptionsTone(thresholdConfig, channelMetric.exceptions?.failureRate) === 'critical',
                                })}>{formatPercent(channelMetric.exceptions?.failureRate ?? 0)}</span>
                                <span className={classNames(dashboardStyles.channelBreakdownMetric, {
                                    [dashboardStyles.channelBreakdownToneNeutral]: getCountryOverdueTone(thresholdConfig, channelMetric.exceptions?.overdueCount) === 'neutral',
                                    [dashboardStyles.channelBreakdownTonePositive]: getCountryOverdueTone(thresholdConfig, channelMetric.exceptions?.overdueCount) === 'positive',
                                    [dashboardStyles.channelBreakdownToneWarning]: getCountryOverdueTone(thresholdConfig, channelMetric.exceptions?.overdueCount) === 'warning',
                                    [dashboardStyles.channelBreakdownToneCritical]: getCountryOverdueTone(thresholdConfig, channelMetric.exceptions?.overdueCount) === 'critical',
                                })}>{formatCount(channelMetric.exceptions?.overdueCount ?? 0)}</span>
                            </div>
                        ))}
                        </div>
                    </div>
                ) : (
                    <div className={dashboardStyles.chartEmptyCompact}>{getI18n('eim-v2-channel-breakdown-empty', 'No channel metrics available for the selected period.', intl)}</div>
                )}
            </Modal>

            <Modal
                title={getI18n('eim-thresholds-modal-title', 'Threshold configuration', intl)}
                open={thresholdModalOpen}
                okText={getI18n('eim-thresholds-save', 'Save', intl)}
                cancelText={getI18n('eim-thresholds-cancel', 'Cancel', intl)}
                onCancel={() => setThresholdModalOpen(false)}
                onOk={() => void handleSaveThresholds()}
            >
                <Form form={thresholdForm} layout="vertical" initialValues={thresholdConfig}>
                    <Form.Item label={getI18n('eim-threshold-on-time-positive', 'On-time positive threshold (%)', intl)} name="onTimePositiveThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-on-time-warning', 'On-time warning threshold (%)', intl)} name="onTimeWarningThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-failure-critical', 'Failure critical threshold (%)', intl)} name="failureCriticalThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-overdue-warning', 'Overdue warning threshold (count)', intl)} name="overdueWarningThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-overdue-critical', 'Overdue critical threshold (count)', intl)} name="overdueCriticalThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-overdue-hours', 'Overdue age threshold (hours)', intl)} name="overdueHoursThreshold" rules={[{ required: true }]}>
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-coverage-positive', 'Coverage positive threshold (%)', intl)} name="coveragePositiveThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-coverage-warning', 'Coverage warning threshold (%)', intl)} name="coverageWarningThreshold" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-email-transit-sla', 'EMAIL transit SLA (hours)', intl)} name="emailTransitSlaHours" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-portal-transit-sla', 'PORTAL transit SLA (hours)', intl)} name="portalTransitSlaHours" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-edi-transit-sla', 'EDI / QBQ transit SLA (hours)', intl)} name="ediTransitSlaHours" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={getI18n('eim-threshold-enable-value-zoom', 'Enable value zoom popup', intl)} name="enableValueZoom" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Button onClick={handleResetThresholds}>
                        {getI18n('eim-threshold-reset-defaults', 'Reset to defaults', intl)}
                    </Button>
                </Form>
            </Modal>
        </Spin>
    );
};
export default EIMExecutiveDashboard;
